// DeckAssembler -- meeting-prep deck builder shell.
//
// Lane 22 Phase 1 (2026-06-29) laid the builder out to match the approved mockup's
// STRUCTURE: a full-width TOP CONFIG STRIP (subject supplier, title, meeting type,
// load-saved, New deck, Download HTML) over a THREE-zone workspace:
//   LEFT  "Your deck"      = the deck's slide outline rail (select active slide,
//                            reorder via up/down + drag, remove) + the add-slides
//                            palette (the P3 library drawer).
//   CENTER "Preview"       = the sticky live preview (Active-slide / Whole-deck
//                            toggle + Refresh) -- behavior unchanged.
//   RIGHT  "Slide details" = the active slide's TEXT editor (editable_fields only)
//                            OR the honest generated-from-data note, plus the
//                            "Meeting plan" tab (deck v3 amendment 4: the overall
//                            write-up + per-slide briefing + one download, folded
//                            into one; the old separate "Briefing" tab is gone).
//
// Lane 299 (QA-8, 2026-07-22): PRESENTATION-ONLY re-skin to the locked Linear
// direction (docs/design/mockups/meeting-prep-deck-linear.html). The inline
// brand-token styling is replaced by the `.dk` namespaced token layer in
// DeckAssembler.linear.css (light-only; liftable to :root for a later dark lane).
// FUNCTIONAL PARITY: no control, tab, toggle, drag, field, or state removed --
// only styling classes changed (plus the narrow-collapse moved from a JS resize
// listener to a CSS @media query, and the rail "+ Generate a page" label swapped
// for an icon + "Generate a page"). The EXPORTED deck slides are server-built and
// UNTOUCHED -- they render inside the preview iframe as a light artifact.
//
// Lane 789 (2026-08-03): THE REDESIGN, BUILT. Ryan, on the Lane 782 render:
// "the new deck builder thing is much better. Looks better." Measured before /
// after, from this file: 59 buttons on screen at rest -> 9; 4 places an action
// can live -> 1; 3 independently scrolling panels -> 1; 3 tabs -> 0; and the prep
// for page 7 went from 13.2 screens of scrolling to one click on page 7.
//
// FEWER BUTTONS AT REST, NOT FEWER CAPABILITIES. Nothing was removed. Where each
// control went:
//   subject / title / meeting type / load-saved / New deck / Plan a meeting /
//     Save deck ...................... "Deck setup" in the one toolbar
//   Download HTML + Download the pack . the ONE "Download" menu, which names both
//                                       artifacts and says which one the supplier
//                                       may see (Ryan: "make sure that they can
//                                       see that there's the prep and the document")
//   Add slides from library .......... "Add a page" at the foot of the page list
//   Generate a page .................. inside that library (it is the catalogue of
//                                       pages; generating one is what you do when
//                                       the catalogue has not got it)
//   up / down / Remove, per page ..... on the row you are pointing at
//   "What this argues" tab ........... the always-visible argument line (its
//                                       controls are Lane 766's, VERBATIM)
//   "Meeting plan" tab ............... the selected page's prep, in the right
//                                       panel; "Open the full plan" still opens
//                                       the whole pack in the wide sheet
//   Slide details tab ................ the right panel is the slide details
//   Refresh preview / mode / Edit panel  the preview's own header, as before
//
// Lane 804 (2026-08-04, pre-launch queue items 1 + 2): TWO THINGS STOP BEING SHOWN.
// This is a DISPLAY change -- it moves no number and removes no measurement.
//   1. "Generation cost" (the per-page AI spend, "$0.125" / "Not recorded") is gone
//      from the right panel. cost_usd still arrives on the payload and is still
//      recorded server-side for Ryan's tracking; users just do not see it.
//   2. The "Meeting type" input is gone from Deck setup (4 fields -> 3), and the
//      meeting type is dropped from the toolbar subline. meetingType REMAINS state:
//      loaded from a saved deck, sent on both save calls, and still the headline's
//      fallback name. It is simply no longer user-editable, resting at 'QBR'.
//      This is inert for deck CONTENT: {{meeting_type}} has rendered on zero slides
//      since QA-6 (2026-07-22) pulled it from the cover chrome + content eyebrow,
//      and the "Plan a meeting" intake sets meeting_type server-side from the
//      objective's label anyway (routes/deck_builder.py:1775).
//
// UNCHANGED, load-bearing technical contracts (do not regress):
//   * Per-instance edits (Part 5): each slide in `selected` is
//     {template_key, overrides:{token:value}}, so two copies of one template carry
//     INDEPENDENT text -- reorder/remove move edits with the instance (no re-key).
//   * Editor is registry-driven with NO hardcoded template_key; analytical/data
//     fields are NEVER editable -- only the registry's editable_fields (Rule 116).
//   * Single-slide preview reuses the whole-deck export and HIDES the other slides
//     inside the already-sandboxed (allow-scripts) preview iframe. V1 injects
//     isolateSlideInDoc; workspace-shell V2 keeps one marked document and lets
//     the existing hydrate select the visible page. Rendering/export are unchanged.
//   * Staleness signature over {subject, title, meeting type, slide instances incl.
//     per-instance overrides} flips the preview to "out of date" on ANY change.
//   * No emojis (design-system rule); SVG/text + design tokens only.

import React, { useState, useEffect, useCallback, useMemo, useRef, useContext } from 'react';
import AsyncSelect from 'react-select/async';
import { flushSync } from 'react-dom';
import { useAuth } from '../AuthContext';
import { API_BASE } from '../config';
import {
  selectStyles, debounce, searchResultToOption,
  renderedIndexForActive, isolateSlideInDoc, hideCanvasScrollbar,
} from './deck/deckStyles';
import LibraryDrawer from './deck/LibraryDrawer';
import DeckPreviewPane from './deck/DeckPreviewPane';
import PositionPad from './deck/PositionPad';
import BuildProgress, { expectedBuildTime, recordBuildTime } from './deck/BuildProgress';
import { useToast } from './ToastHost';
import DeckWorkspaceShell from './deck/DeckWorkspaceShell';
import DeckChatDock from './deck/DeckChatDock';
import useHideFeedback from './workspace-chat/useHideFeedback';
import WorkspaceSidebarContext from './WorkspaceSidebarContext';
import MeetingPrepPack from './deck/MeetingPrepPack';
// (SlideBriefing, the legacy per-slide briefing pack, was deleted with the
//  deck v2 briefing feature on 2026-10-17, Lane 1410 W8b; template decks use
//  MeetingPrepPack.)  // Lane 317: the "Meeting plan" pack (per-slide briefing + one download)
import MetricCaveatPanel from './deck/MetricCaveatPanel';  // Lane 736: a buyer's note ABOUT a number, rendered beside that number
import ArgumentLine from './deck/ArgumentLine';  // Lane 789: what this deck argues, out of the tab strip and onto the page
import { downloadPackHtml, triggerHtmlDownload } from './deck/packExport';
import savedDeckLabel from './deck/savedDeckLabel';  // Lane 1372: title, supplier and built date of a saved deck
import './DeckAssembler.linear.css';

const SUPPLIER_SEARCH_LIMIT = 50;

// The deck's mutation signature. ONE formula, two callers: the memo that drives
// the unsaved/dirty state, and `loadDeck`, which has to know the signature of
// the deck it just loaded before React has re-rendered with it (Lane 789).
function signatureOf({ supplierId, title, meetingType, order, briefRevision }) {
  return JSON.stringify({
    s: supplierId == null ? null : supplierId,
    t: title,
    m: meetingType,
    order,
    briefRevision,
  });
}

// ⭐ PLAN B PHASE 2 (Lane 1129): the STRUCTURE signature — everything in the
// full signature EXCEPT per-instance override VALUES. Plan §7.3 splits preview
// staleness from text hydration: an inline/panel text edit is hydrated into
// the existing preview (visually current, no server rebuild), so it must NOT
// flip the preview to "out of date"; reorder/add/remove/generated-swap and the
// top-bar fields still do. `dirty` (unsaved work) keeps the FULL signature.
function structureSignatureOf({ supplierId, title, meetingType, orderKeys, briefRevision }) {
  return JSON.stringify({
    s: supplierId == null ? null : supplierId,
    t: title,
    m: meetingType,
    keys: orderKeys,
    briefRevision,
  });
}

// Compare structure by stable identity, ignoring only order. Duplicate template
// instances must remain distinct; add/remove/topology/title changes stay stale.
function unorderedStructure(signature, instances) {
  if (!signature) return null;
  const value = JSON.parse(signature);
  value.keys = instances.map((inst, i) => [inst._editor_instance_id, value.keys[i]])
    .sort(([a], [b]) => a.localeCompare(b));
  return JSON.stringify(value);
}

// Bounded undo history (plan §7.2): one entry per committed inline edit, one
// per coalesced side-panel editing session, cleared on save/load/new/reset.
const UNDO_LIMIT = 50;
// The longest a deck chat message waits for a running save or build before it is sent
// with the deck as it is (and refused if it is still not clean).
const CHAT_SETTLE_MS = 60000;

// Lane 1229 / S3 (§9): the filmstrip placeholder wording per canonical
// snapshot state. Tiles NEVER infer freshness or fitness from the image —
// those remain the existing deck truths (§3).
// Lane 1231 / F3: ONE derivation of a v2 tile's status chips from the
// EXISTING per-instance truths — every persisted edit container (text
// overrides, materialized rows, element patches, added boxes), the
// generated-page audience, and the per-index fit maps. Never thumbnail
// inference. Exported for direct unit coverage of every state (the
// fit-FAILED arm is only reachable through the frame protocol, which
// jsdom cannot drive).
export function v2TileStatus({ instance, internalOnly, fitError, fitPending }) {
  const chips = [];
  const inst = instance || {};
  const edited = Object.keys(inst.overrides || {}).length > 0
    || Object.keys(inst.row_blocks || {}).length > 0
    || Object.keys(inst.element_patches || {}).length > 0
    || ((inst.added_elements || []).length > 0);
  if (internalOnly) chips.push({ kind: 'int', label: 'Internal only' });
  if (edited) chips.push({ kind: 'edit', label: 'Edited' });
  if (fitError) chips.push({ kind: 'fit', label: 'Text does not fit' });
  else if (fitPending) chips.push({ kind: 'fitp', label: 'Checking fit' });
  return chips;
}

const FILMSTRIP_PLACEHOLDER = {
  none: 'Thumbnails appear when the deck is ready',
  loading: 'Loading previews…',
  generating: 'Preparing previews…',
  failed: 'Previews unavailable',
  unavailable: 'Previews unavailable',
  expired: 'Previews expired. Refresh the preview',
};

// Stable identity for "no overrides": a fresh {} per render would re-fire the
// hydrate effect on every parent re-render, and a hydrate cancels an
// in-progress inline edit (Consult #4 separate observation 1).
const EMPTY_OVERRIDES = {};
// Lane 1233 / W5: identity-stable empties — a fresh [] per render defeats
// the hydrate freeze (every pane dep compare sees a new array).
const EMPTY_ADDED = [];
let editorInstanceSequence = 0;

const STYLE_KEYS = new Set([
  'fontFamily', 'fontSize', 'color', 'fontWeight', 'fontStyle',
  'textDecoration', 'textAlign', 'lineHeight', 'letterSpacing',
  'textTransform', 'background', 'opacity',
]);
const STYLE_ENUMS = {
  fontFamily: ['display', 'body', 'mono'],
  fontWeight: [400, 500, 600, 700, 800],
  fontStyle: ['normal', 'italic'],
  textDecoration: ['none', 'underline', 'line-through', 'underline line-through'],
  textAlign: ['left', 'center', 'right', 'justify'],
  textTransform: ['none', 'uppercase', 'lowercase', 'capitalize'],
};

function validElementStyle(style) {
  if (!style || typeof style !== 'object' || Array.isArray(style)
    || Object.keys(style).some((key) => !STYLE_KEYS.has(key))) return false;
  return Object.entries(style).every(([key, value]) => {
    if (STYLE_ENUMS[key]) return STYLE_ENUMS[key].includes(value);
    if (key === 'fontSize' || key === 'lineHeight') {
      const [min, max] = key === 'fontSize' ? [8, 120] : [8, 160];
      return Number.isInteger(value) && value >= min && value <= max;
    }
    if (key === 'letterSpacing') return Number.isFinite(value) && value >= -0.1 && value <= 0.5;
    if (key === 'opacity') return Number.isFinite(value) && value >= 0 && value <= 1;
    if (key === 'background' && value === 'none') return true;
    return (key === 'color' || key === 'background')
      && typeof value === 'string' && /^#[0-9a-f]{6}$/.test(value);
  });
}

function StyleInspector({ style, onChange, onReset }) {
  const set = (key, value) => onChange(key, value);
  const [numberDrafts, setNumberDrafts] = useState(() => ({
    fontSize: style.fontSize === undefined ? '' : String(style.fontSize),
    lineHeight: style.lineHeight === undefined ? '' : String(style.lineHeight),
    letterSpacing: style.letterSpacing === undefined ? '' : String(style.letterSpacing),
    opacity: style.opacity === undefined ? '' : String(style.opacity),
  }));
  useEffect(() => setNumberDrafts({
    fontSize: style.fontSize === undefined ? '' : String(style.fontSize),
    lineHeight: style.lineHeight === undefined ? '' : String(style.lineHeight),
    letterSpacing: style.letterSpacing === undefined ? '' : String(style.letterSpacing),
    opacity: style.opacity === undefined ? '' : String(style.opacity),
  }), [style]);
  const decoration = style.textDecoration || '';
  const hasUnderline = decoration.includes('underline');
  const hasStrike = decoration.includes('line-through');
  const toggleDecoration = (kind) => {
    const underline = kind === 'underline' ? !hasUnderline : hasUnderline;
    const strike = kind === 'line-through' ? !hasStrike : hasStrike;
    set('textDecoration', underline && strike ? 'underline line-through'
      : (underline ? 'underline' : (strike ? 'line-through' : 'none')));
  };
  const numberChange = (key, raw) => {
    setNumberDrafts((current) => ({ ...current, [key]: raw }));
    if (raw === '') set(key, null);
    else if (/^-?(?:\d+|\d*\.\d+)$/.test(raw)) {
      const value = Number(raw);
      const candidate = { ...style, [key]: value };
      if (validElementStyle(candidate)) set(key, value);
    }
  };
  const resetNumberDraft = (key) => setNumberDrafts((current) => ({
    ...current, [key]: style[key] === undefined ? '' : String(style[key]),
  }));
  return (
    <div className="dk-style" data-testid="element-style-inspector">
      <div className="dk-style-grid">
        <label className="dk-style-field">
          <span>Font</span>
          <select aria-label="Font" value={style.fontFamily || ''}
            onChange={(e) => set('fontFamily', e.target.value || null)}>
            <option value="">Base</option><option value="display">Display</option>
            <option value="body">Body</option><option value="mono">Mono</option>
          </select>
        </label>
        <label className="dk-style-field">
          <span>Size</span>
          <input aria-label="Font size" type="number" min="8" max="120" step="1"
            value={numberDrafts.fontSize} onBlur={() => resetNumberDraft('fontSize')}
            onChange={(e) => numberChange('fontSize', e.target.value)} placeholder="Base" />
        </label>
        <label className="dk-style-field">
          <span>Weight</span>
          <select aria-label="Font weight" value={style.fontWeight || ''}
            onChange={(e) => set('fontWeight', e.target.value ? Number(e.target.value) : null)}>
            <option value="">Base</option>{[400, 500, 600, 700, 800].map((weight) => (
              <option value={weight} key={weight}>{weight}</option>
            ))}
          </select>
        </label>
        <label className="dk-style-field">
          <span>Case</span>
          <select aria-label="Text case" value={style.textTransform || ''}
            onChange={(e) => set('textTransform', e.target.value || null)}>
            <option value="">Base</option><option value="none">None</option>
            <option value="uppercase">Uppercase</option><option value="lowercase">Lowercase</option>
            <option value="capitalize">Capitalize</option>
          </select>
        </label>
        <label className="dk-style-field">
          <span>Leading</span>
          <input aria-label="Line height" type="number" min="8" max="160" step="1"
            value={numberDrafts.lineHeight} onBlur={() => resetNumberDraft('lineHeight')}
            onChange={(e) => numberChange('lineHeight', e.target.value)} placeholder="Base" />
        </label>
        <label className="dk-style-field">
          <span>Tracking</span>
          <input aria-label="Letter spacing" type="number" min="-0.1" max="0.5" step="0.01"
            value={numberDrafts.letterSpacing} onBlur={() => resetNumberDraft('letterSpacing')}
            onChange={(e) => numberChange('letterSpacing', e.target.value)} placeholder="Base" />
        </label>
        <label className="dk-style-field">
          <span>Opacity</span>
          <input aria-label="Opacity" type="number" min="0" max="1" step="0.05"
            value={numberDrafts.opacity} onBlur={() => resetNumberDraft('opacity')}
            onChange={(e) => numberChange('opacity', e.target.value)} placeholder="Base" />
        </label>
      </div>
      <div className="dk-style-row" role="group" aria-label="Text emphasis">
        <button type="button" className={style.fontStyle === 'italic' ? 'on' : ''}
          aria-pressed={style.fontStyle === 'italic'}
          onClick={() => set('fontStyle', style.fontStyle === 'italic' ? 'normal' : 'italic')}>Italic</button>
        <button type="button" className={hasUnderline ? 'on' : ''} aria-pressed={hasUnderline}
          onClick={() => toggleDecoration('underline')}>Underline</button>
        <button type="button" className={hasStrike ? 'on' : ''} aria-pressed={hasStrike}
          onClick={() => toggleDecoration('line-through')}>Strike</button>
      </div>
      <div className="dk-style-row" role="group" aria-label="Text alignment">
        {['left', 'center', 'right', 'justify'].map((alignment) => (
          <button type="button" key={alignment} className={style.textAlign === alignment ? 'on' : ''}
            aria-pressed={style.textAlign === alignment}
            onClick={() => set('textAlign', alignment)}>{alignment}</button>
        ))}
        <button type="button" className={!style.textAlign ? 'on' : ''}
          aria-pressed={!style.textAlign} onClick={() => set('textAlign', null)}>Base</button>
      </div>
      <div className="dk-style-colors">
        <label className="dk-style-color"><span>Color</span>
          <input aria-label="Text color" type="color" value={style.color || '#253068'}
            onChange={(e) => set('color', e.target.value.toLowerCase())} />
        </label>
        <button type="button" className="dk-style-base" onClick={() => set('color', null)}>Base color</button>
        <label className="dk-style-color"><span>Background</span>
          <input aria-label="Background color" type="color"
            value={style.background && style.background !== 'none' ? style.background : '#ffffff'}
            onChange={(e) => set('background', e.target.value.toLowerCase())} />
        </label>
        <button type="button" className="dk-style-base"
          onClick={() => set('background', 'none')}>No fill</button>
      </div>
      <button type="button" className="dk-btn sm" disabled={!Object.keys(style).length}
        onClick={onReset}>Reset formatting</button>
    </div>
  );
}

// S6c: notices use the app's existing host, outside the workspace's slots.
function WorkspaceNotice({ status, error, dismissError }) {
  const { showToast } = useToast();
  useEffect(() => {
    if (error) showToast({ message: error, variant: 'error', durationMs: 0,
      actionLabel: 'Dismiss', onAction: dismissError });
    else if (status) showToast({ message: status === 'Saved.' ? 'Saved' : status,
      durationMs: 3000 });
  }, [status, error, dismissError, showToast]);
  return null;
}

function withEditorInstanceIdentity(instance) {
  editorInstanceSequence += 1;
  return { ...instance, _editor_instance_id: `deck-el-${editorInstanceSequence}` };
}

function stableStringify(value) {
  if (Array.isArray(value)) return `[${value.map(stableStringify).join(',')}]`;
  if (value && typeof value === 'object') {
    return `{${Object.keys(value).sort().map((key) =>
      `${JSON.stringify(key)}:${stableStringify(value[key])}`).join(',')}}`;
  }
  return JSON.stringify(value);
}

// Fit truth is positional because the frame speaks instance indexes. Structural
// mutations must therefore move the flags with the same slide instance instead
// of dropping them and failing open.
function remapIndexFlags(flags, mapIndex) {
  const next = {};
  Object.keys(flags).forEach((raw) => {
    const mapped = mapIndex(Number(raw));
    if (Number.isInteger(mapped) && mapped >= 0) next[mapped] = true;
  });
  return next;
}

// Per-page CONTENT identity for fit measurements. Structure revision cannot
// distinguish two successive text hydrations by design, so accepting a fit
// result requires both identities. Keep this shape in lockstep with the
// hydrate payload and the iframe's echoed `fitRevision`.
function fitRevisionOf(inst, version = 1) {
  const legacy = {
    // the template is part of the content identity: two unedited instances of
    // DIFFERENT templates must never share a revision string (Consult #6)
    template_key: inst ? inst.template_key : null,
    overrides: inst && inst.overrides ? inst.overrides : {},
    row_blocks: inst && inst.row_blocks ? inst.row_blocks : {},
  };
  if (version !== 2) return JSON.stringify(legacy);
  return stableStringify({
    ...legacy,
    element_patches: inst && inst.element_patches ? inst.element_patches : {},
    added_elements: inst && inst.added_elements ? inst.added_elements : [],
  });
}

function elementStateOf(inst) {
  return {
    overrides: { ...((inst && inst.overrides) || {}) },
    row_blocks: JSON.parse(JSON.stringify((inst && inst.row_blocks) || {})),
    element_patches: JSON.parse(JSON.stringify((inst && inst.element_patches) || {})),
    added_elements: JSON.parse(JSON.stringify((inst && inst.added_elements) || [])),
  };
}

// Lane 789: `GripIcon` is gone with the card layout it sat on -- the rail row IS
// the drag handle now (the whole row is `draggable`), so a separate six-dot grip
// was a permanent affordance for something the row already does. `SparkleIcon`
// moved to LibraryDrawer with the "Generate a page" control it labels.

function PlusIcon() {
  return (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden="true" focusable="false">
      <path d="M12 5v14M5 12h14" />
    </svg>
  );
}

function DownloadIcon() {
  return (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden="true" focusable="false">
      <path d="M12 3v12M7 10l5 5 5-5M5 21h14" />
    </svg>
  );
}

function ChevronDownIcon() {
  return (
    <svg className="chv" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.4"
      aria-hidden="true" focusable="false">
      <path d="M6 9l6 6 6-6" />
    </svg>
  );
}

// Lane 941: the SIDEWAYS disclosure chevron, matching LibraryDrawer's own and
// styled by the `.dk-chv` / `.dk-chv.open` pair that already ships in
// DeckAssembler.linear.css (:362-363). Distinct from ChevronDownIcon above,
// which is the always-down caret on the Download menu and never rotates.
function Chevron({ open }) {
  return (
    <span className={`dk-chv${open ? ' open' : ''}`}>
      <svg width="10" height="10" viewBox="0 0 10 10" aria-hidden="true" focusable="false">
        <path d="M3 1.5 L7 5 L3 8.5" fill="none" stroke="currentColor" strokeWidth="1.5"
          strokeLinecap="round" strokeLinejoin="round" />
      </svg>
    </span>
  );
}

function GearIcon() {
  return (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden="true" focusable="false">
      <circle cx="12" cy="12" r="3" />
      <path d="M19.4 15a1.65 1.65 0 00.33 1.82l.06.06a2 2 0 11-2.83 2.83l-.06-.06a1.65 1.65 0 00-1.82-.33 1.65 1.65 0 00-1 1.51V21a2 2 0 11-4 0v-.09A1.65 1.65 0 008 19.4a1.65 1.65 0 00-1.82.33l-.06.06a2 2 0 11-2.83-2.83l.06-.06A1.65 1.65 0 003.68 15a1.65 1.65 0 00-1.51-1H2a2 2 0 110-4h.09A1.65 1.65 0 003.6 9a1.65 1.65 0 00-.33-1.82l-.06-.06a2 2 0 112.83-2.83l.06.06A1.65 1.65 0 009 3.68 1.65 1.65 0 0010 2.17V2a2 2 0 114 0v.09a1.65 1.65 0 001 1.51 1.65 1.65 0 001.82-.33l.06-.06a2 2 0 112.83 2.83l-.06.06A1.65 1.65 0 0020.32 9c.2.61.76 1 1.42 1H22a2 2 0 110 4h-.09a1.65 1.65 0 00-1.51 1z" />
    </svg>
  );
}

/**
 * New presentations start in chat; this component edits saved decks.
 */
function DeckAssembler({ initialCompositionId = null, onOpenDeck = null, onCloseDeck = null, onStartChat = null }) {
  const { token, user } = useAuth();
  const authHeaders = useCallback(
    () => ({ Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' }),
    [token]
  );

  const [libraryTemplates, setTemplates] = useState([]);
  const [presentationTemplates, setPresentationTemplates] = useState(null);
  const templates = presentationTemplates || libraryTemplates;
  const nativeRevision = useRef(null);
  const [subject, setSubject] = useState(null);     // {supplier_id, supplier_name}
  const [title, setTitle] = useState('');
  const [meetingType, setMeetingType] = useState('QBR');
  // Per-instance slide list (Part 5): each item is {template_key, overrides:{token:value}}.
  const [selected, setSelected] = useState([]);
  const [activeSlideIdx, setActiveSlideIdx] = useState(0);  // which slide the editor shows
  const [savedDecks, setSavedDecks] = useState([]);
  const [editingId, setEditingId] = useState(null);  // composition_id when editing
  const [previewOrder, setPreviewOrder] = useState([]);
  const [previewGeneration, setPreviewGeneration] = useState(0);
  const [reorderPending, setReorderPending] = useState(false);
  const [reorderRequest, setReorderRequest] = useState(0);
  const [visibleBuild, setVisibleBuild] = useState(null);
  const [workspaceLoading, setWorkspaceLoading] = useState(false);
  // Opened for one deck (?composition=): nothing else is drawn until that deck is
  // loaded, so the empty setup screen never flashes before it (Ryan, staging 2026-10-12).
  const [openingDeck, setOpeningDeck] = useState(
    () => Boolean(initialCompositionId && /^\d+$/.test(String(initialCompositionId))));
  const automaticAttempt = useRef(null);
  const automaticTimer = useRef(null);
  const reorderEpoch = useRef(0);
  const workspaceEpoch = useRef(0);
  const reorderLatest = useRef({});
  const writerSerial = useRef(Promise.resolve());
  const writerWork = useRef({});
  const refreshWork = useRef(null);
  const workspaceAlive = useRef(true);
  const [settlement, setSettlement] = useState(null);
  const settlementRef = useRef(null);
  const settlementSequence = useRef(0);
  useEffect(() => {
    workspaceAlive.current = true;
    return () => {
      workspaceAlive.current = false; reorderEpoch.current += 1;
      settlementRef.current?.finish(false);
    };
  }, []);
  const [exportDoc, setExportDoc] = useState('');    // whole-deck export HTML (preview source)
  // Plan B Phase 2 (Lane 1129) — inline-editing state:
  // the preview's structure revision (from the export's response header; the
  // frame carries its meta twin and echoes it on every message — plan §4.1/4.2)
  const [previewRevision, setPreviewRevision] = useState(null);
  const previewStaleRef = useRef(false);
  // the STRUCTURE signature the shown preview was built from (staleness is
  // structural only now — text edits hydrate instead of staling, plan §7.3)
  const [exportedStructureSig, setExportedStructureSig] = useState(null);
  // fit gate (plan §7.4, per-page after Consult #4): the frame reports fit
  // truth PER instance; Save and both downloads stay disabled while ANY page
  // is over — switching to a clean page can never unlock an overflow left on
  // another page, and a panel-side shorten clears its own page's entry.
  const [fitErrors, setFitErrors] = useState({});
  // A text mutation is unsafe until the CURRENT preview reports fit for that
  // page. This closes the no-preview and hydrate-race gaps without treating
  // missing measurement as success.
  const [fitChecksPending, setFitChecksPending] = useState({});
  // inline editing capability: wide screen + fine pointer only (plan §7.5);
  // narrow/coarse keeps the side panel as the complete editor
  const [inlineCapable, setInlineCapable] = useState(true);
  // "Your words on this page" collapse — null = automatic (collapsed when
  // inline editing is available); a click stores the buyer's explicit choice,
  // preserved across visits (plan §7.5).
  const [wordsOpen, setWordsOpen] = useState(() => {
    try {
      const v = localStorage.getItem('dk.wordsOpen');
      return v == null ? null : v === '1';
    } catch (e) { return null; }
  });
  // bounded undo history + the side-panel coalescing anchor (plan §7.2)
  const undoStack = useRef([]);
  // Protocol v2 owns per-instance history. The client-only identity survives
  // reorder/duplicate-template operations but is stripped by cleanSlideOrder.
  const elementHistory = useRef({});
  const [editorProtocolVersion, setEditorProtocolVersion] = useState(0);
  const editorProtocolRef = useRef(0);
  const [selectedElement, setSelectedElement] = useState(null);
  const positionPadRef = useRef(null);
  const positionHistoryRef = useRef(null);
  const panelEditStart = useRef(null);   // v1 token anchor or v2 whole-instance anchor
  const selectedRef = useRef(selected);
  selectedRef.current = selected;
  // Lane 1235: the sandboxed frame can focus its internal contenteditable
  // without becoming the TOP-LEVEL active element. DeckAssembler owns the
  // cross-frame handoff and invokes it only after a current v2 `select`
  // message passes the pane boundary and the authority checks below.
  const previewFrameRef = useRef(null);
  const [omitted, setOmitted] = useState([]);
  // Lane 905. Pages the server left OUT of a supplier-facing deck because they
  // are internal-only, plus the one sentence explaining it. Both come from
  // response headers and never from the exported document -- naming what was
  // withheld inside the file would hand the supplier that fact. `keys` is NOT
  // cosmetic: a page missing from the export shifts every page after it, so it
  // has to join `omitted` when deriving either V1's positional isolation index
  // or V2's active-instance fallback.
  const [audienceFiltered, setAudienceFiltered] = useState({ keys: [], notice: '' });
  // Lane 72 (Section-18 export guardrail): when a DOWNLOAD would include
  // internal-only slides, hold {id, slides:[{template_key,name}]} here to drive
  // the warn-and-confirm modal; null = no pending confirmation.
  const [pendingInternalConfirm, setPendingInternalConfirm] = useState(null);
  const [previewMode, setPreviewMode] = useState('slide');  // 'slide' | 'deck'
  const [exportedAt, setExportedAt] = useState(null);  // wall-clock label of the last successful export
  const [busy, setBusy] = useState(false);
  const busyRef = useRef(false);   // the deck chat reads it while a message waits
  busyRef.current = busy;
  const [error, setError] = useState(null);
  const [status, setStatus] = useState(null);
  const dismissError = useCallback(() => setError(null), []);
  // Phase 2: right inspector open/close. Default OPEN so the editor never
  // vanishes unexpectedly on first load. Plan B §7.5: the buyer's choice is
  // preserved across visits (guarded reads -- storage can be unavailable).
  const [inspectorOpen, setInspectorOpen] = useState(() => {
    try { return localStorage.getItem('dk.pagePanelOpen') !== '0'; }
    catch (e) { return true; }
  });
  useEffect(() => {
    try { localStorage.setItem('dk.pagePanelOpen', inspectorOpen ? '1' : '0'); }
    catch (e) { /* storage unavailable -- the choice is session-only */ }
  }, [inspectorOpen]);
  useEffect(() => {
    try {
      if (wordsOpen == null) localStorage.removeItem('dk.wordsOpen');
      else localStorage.setItem('dk.wordsOpen', wordsOpen ? '1' : '0');
    } catch (e) { /* storage unavailable -- the choice is session-only */ }
  }, [wordsOpen]);
  // Lane 168 Stage D: the persisted meeting-intent story (composition.intake).
  // Lane 789 also reads `intake.intent.excluded` from it -- the build's own
  // record of the pages a HARD gate left out, which is the "ruled out by the
  // data" half of the argument line.
  const [intake, setIntake] = useState(null);
  // Lane 1412 (Ryan 2026-10-16): every deck is shared with the company. A
  // chat-built deck another person built opens view-only (view, present,
  // download); only its builder changes or deletes it. null = the caller may.
  const [viewOnly, setViewOnly] = useState(null);
  const [canDelete, setCanDelete] = useState(true);
  // Lane 1355: the deck chat's unsent message, for the open deck only. Held here,
  // above the desktop and narrow layouts, so switching between them keeps it.
  const [deckChatDraft, setDeckChatDraft] = useState({ id: null, text: '' });
  // Lane 1355 slice 2: a chat change (Add slide, a typed yes, Undo) is running in the
  // save queue; editing is paused until the deck has reloaded.
  const [chatWriting, setChatWriting] = useState(false);
  const chatWritingRef = useRef(false);
  chatWritingRef.current = chatWriting;
  // The deck changed from the chat but the editor could not follow it (unsaved
  // edits, or a failed reload): saving is off until the buyer reloads the deck.
  const [chatReload, setChatReload] = useState(null);
  // While a chat change is written the whole deck page is inert: no pointer, no
  // keyboard, no focus, every layout (Codex s2 r5 #2, r6 #1).
  useEffect(() => {
    const nodes = document.querySelectorAll('.dk');
    nodes.forEach((node) => {
      if (chatWriting) node.setAttribute('inert', '');
      else node.removeAttribute('inert');
    });
    return () => nodes.forEach((node) => node.removeAttribute('inert'));
  }, [chatWriting]);
  const writerPending = useRef(0);   // writes queued or running in writerSerial
  const dirtyRef = useRef(false);
  // Lane 789: the ONE toolbar's two disclosures. `setupOpen` holds the four
  // configuration fields that used to be a permanent wrapping strip; `outOpen`
  // is the download menu that names the two artifacts.
  const [setupOpen, setSetupOpen] = useState(false);
  const [outOpen, setOutOpen] = useState(false);
  // Lane 1387 (Ryan 2026-10-05): no Feedback button on an open deck for now (not in the
  // toolbar, not floating, not in the chat header); the chat opens from its bubble.
  useHideFeedback();
  // Lane 789: the deck signature as last PERSISTED, so the toolbar can say "All
  // changes saved" instead of standing a Save button there forever.
  const [savedSignature, setSavedSignature] = useState(null);
  // Lane 789: what the prep pack is doing, reported up by MeetingPrepPack (and, until
  // 2026-10-17, the deleted legacy SlideBriefing). The
  // toolbar holds the pack download while a pass is still running -- Lane 768's
  // guarantee, which used to hold only because the button lived inside the pack.
  const [packStatus, setPackStatus] = useState({ loading: false, enriching: false, ready: false });
  const [packDownloading, setPackDownloading] = useState(false);
  const notesToggleRef = useRef(null);
  const [prepHeaderTarget, setPrepHeaderTarget] = useState(null);
  // Lane 268: whether the deck v2 BRIEFING floor is enabled server-side
  // (DECK_BRIEFING_ENABLED). Echoed additively on GET /api/decks/templates.
  // Default false -> no "Briefing" tab, no briefing fetch (byte-identical to
  // before). The briefing endpoints enforce the flag independently (404 when OFF).
  // The meeting prep pack exists only for template decks (Lane 1352); the legacy
  // per-slide briefing it replaced was deleted on 2026-10-17 (Lane 1410 W8b).
  const briefingEnabled = Boolean(presentationTemplates);
  // Lane 273's sibling AI-enrichment mirror (`briefingAiEnabled`) is GONE as of
  // Lane 752. It gated exactly one thing -- the "Sharpen with AI" button Lane 746
  // deleted from SlideBriefing -- and that lane's own note named this file as
  // where the now-dead pass-through had to be cleaned up.
  // Lane 285 / G3: only NEW generation/refinement follows this server echo.
  // Metadata for already-saved `gen:` pages is loaded with the composition and
  // remains available when the creation flag is OFF.
  const [libraryElementEditorEnabled, setElementEditorEnabled] = useState(false);
  // Lane 1412: a colleague's chat-built deck is view-only, so no editor at all.
  const elementEditorEnabled = (Boolean(presentationTemplates) || libraryElementEditorEnabled) && !viewOnly;
  // Lane 1229 / S1 (shell brief §6): the server-echoed workspace-shell v2
  // capability, read ONCE with the template response. OFF (the default
  // everywhere) renders the pre-lane shell exactly; the v2 workspace
  // (toolbar/dock/filmstrip/notes/Present, brief §2-§5) mounts only when
  // the server says ON. No REACT_APP_* build-time flag.
  const [libraryWorkspaceShellV2Enabled, setWorkspaceShellV2Enabled] = useState(false);
  const workspaceShellV2Enabled = Boolean(presentationTemplates) || libraryWorkspaceShellV2Enabled;
  // Lane 1229 / S2 (§2): the v2 left dock's two mutually exclusive modes —
  // 'pages' (the page list; the S3 filmstrip lands here) or 'edit' (the ONE
  // existing selected-target inspector, moved not copied). V2-only UI state;
  // V1 keeps `inspectorOpen` untouched. Selection and unsaved edits live in
  // this component, so they survive the mode switch by construction (§7.6) —
  // pinned in tests rather than assumed.
  const [dockMode, setDockMode] = useState('pages');
  // Lane 1229 / S3 (§3/§11): the filmstrip's snapshot consumption. A grant
  // is captured from the preview response's snapshot headers; the effect
  // below resolves it into per-page thumbnail object URLs (authenticated
  // fetch -> blob; a plain <img src> cannot carry the Bearer header). ONE
  // pass per grant — no polling, no retry storm (§9). V1 never fetches:
  // the effect is flag-gated.
  const [snapshotGrant, setSnapshotGrant] = useState(null);
  const [filmstrip, setFilmstrip] = useState({ state: 'none', urls: null });
  const filmstripUrlsRef = useRef(null);
  const railRef = useRef(null);
  const stripRef = useRef(null);
  // Lane 1229 / S4 → Lane 1233 / W2 (brief §2 as amended 2026-09-11):
  // null = the capability default (desktop OPEN, narrow/coarse collapsed),
  // chosen at first mount; an explicit user choice is then preserved across
  // page and dock changes — never force-reopened on re-render. Local UI
  // state only. V2-only.
  const [notesOpen, setNotesOpen] = useState(null);
  // Lane 1233 / W5 (plan v1.1 §8.2): the panel-typing hydrate debounce.
  // TRUTH is never debounced — the state transaction and fit-pending land
  // on every keystroke; what waits is the outbound hydrate/measure WORK.
  // Mechanism: during a v2 panel typing burst the hydrate-bound view of
  // the active instance is FROZEN (hydrateFreezeRef holds the pre-burst
  // instance object, so every pane hydrate input keeps its identity and
  // the frame receives nothing); one trailing timer thaws it and the live
  // state flows in a single hydrate. Urgent operations flush; deck
  // transitions and unmount cancel.
  const hydrateFreezeRef = useRef(null);
  const hydrateTimerRef = useRef(null);
  const [, setHydrateEpoch] = useState(0);
  const cancelHydrateDebounce = () => {
    if (hydrateTimerRef.current) {
      clearTimeout(hydrateTimerRef.current);
      hydrateTimerRef.current = null;
    }
    hydrateFreezeRef.current = null;
  };
  const flushHydrate = () => {
    if (hydrateTimerRef.current) {
      clearTimeout(hydrateTimerRef.current);
      hydrateTimerRef.current = null;
    }
    if (hydrateFreezeRef.current !== null) {
      hydrateFreezeRef.current = null;
      setHydrateEpoch((n) => n + 1);
    }
  };
  const flushHydrateRef = useRef(() => {});
  flushHydrateRef.current = flushHydrate;
  // Unmount: a timer must never post into a later authority.
  useEffect(() => () => {
    if (hydrateTimerRef.current) clearTimeout(hydrateTimerRef.current);
    hydrateFreezeRef.current = null;
  }, []);
  // Lane 1231 / F4 (§4): the narrow/coarse Pages drawer. The horizontal
  // strip is selection-only; withheld/add/reorder details live in this
  // focus-managed drawer over the SAME state and handlers (no duplicate
  // mutation authority — the drawer hosts the one rail).
  // Lane 1233 / W4: the v2 Deck-setup dialog's focus contract.
  const setupHeadingRef = useRef(null);
  const [argumentOpen, setArgumentOpen] = useState(false);
  const closeSetupDialog = () => {
    setArgumentOpen(false);
    setSetupOpen(false);
    if (setupToggleRef.current) setupToggleRef.current.focus();
  };
  useEffect(() => {
    if (workspaceShellV2Enabled && setupOpen && setupHeadingRef.current) {
      setupHeadingRef.current.focus();
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [setupOpen, argumentOpen]);
  const [pagesDrawerOpen, setPagesDrawerOpen] = useState(false);
  const pagesDrawerBtnRef = useRef(null);
  const pagesDrawerHeadingRef = useRef(null);
  const narrowWorkspace = workspaceShellV2Enabled && !inlineCapable;
  const notesOpenEffective = notesOpen == null ? !narrowWorkspace : notesOpen;
  const desktopWorkspace = workspaceShellV2Enabled && inlineCapable;
  const enterWorkspaceSidebar = useContext(WorkspaceSidebarContext);
  useEffect(() => {
    if (desktopWorkspace) return enterWorkspaceSidebar();
    return undefined;
  }, [desktopWorkspace, enterWorkspaceSidebar]);
  // Narrow/touch retains its existing stacked viewport budget. The new desktop
  // shell owns a separate fixed region and never participates in this path.
  const stackedRootRef = useRef(null);
  useEffect(() => {
    if (!narrowWorkspace) return undefined;
    const root = stackedRootRef.current;
    const measure = () => {
      root.style.height = `${Math.max(0, window.innerHeight - Math.max(0,
        Math.round(root.getBoundingClientRect().top)))}px`;
    };
    window.scrollTo(0, 0);
    measure();
    document.body.classList.add('dk-stacked-open');
    window.addEventListener('resize', measure);
    return () => {
      root.style.removeProperty('height');
      document.body.classList.remove('dk-stacked-open');
      window.removeEventListener('resize', measure);
    };
  }, [narrowWorkspace]);
  useEffect(() => {
    if (pagesDrawerOpen && pagesDrawerHeadingRef.current) {
      pagesDrawerHeadingRef.current.focus();
    }
  }, [pagesDrawerOpen]);
  useEffect(() => {
    if ((!narrowWorkspace || dockMode !== 'pages') && pagesDrawerOpen) {
      setPagesDrawerOpen(false);
    }
  }, [dockMode, narrowWorkspace, pagesDrawerOpen]);
  // Lane 1229 / S5 (§5): Present overlay state. presentUrls maps rendered
  // page index -> object URL (string) | false (fetch failed) | null
  // (in flight); everything revoked on close. V2-only.
  const [presentOpen, setPresentOpen] = useState(false);
  const [presentIdx, setPresentIdx] = useState(0);
  const [presentUrls, setPresentUrls] = useState({});
  const presentRef = useRef(null);
  const presentBtnRef = useRef(null);
  const presentFetchedRef = useRef(new Set());
  // Consult #5 fixes: an epoch fences fetches that resolve after close
  // (their URL is revoked immediately instead of leaking into fresh
  // state), and a live mirror lets the unmount cleanup revoke whatever
  // Present still holds.
  const presentEpochRef = useRef(0);
  const presentUrlsLiveRef = useRef({});
  // Lane 1231 / F6: the fullscreenchange listener needs the CURRENT
  // closePresent (it is re-created per render) — same ref pattern as
  // refreshPreviewRef.
  const closePresentRef = useRef(() => {});
  const presentEnteredFsRef = useRef(false);
  // Lane 1231 / F1: the filmstrip's own fencing epoch. Any composition
  // transition (load / new / delete) bumps it, so in-flight manifest and
  // thumbnail work for the OLD deck can never publish under the new one.
  const filmstripEpochRef = useRef(0);

  const releaseFilmstripUrls = () => {
    (filmstripUrlsRef.current || [])
      .forEach((u) => { if (u) URL.revokeObjectURL(u); });
    filmstripUrlsRef.current = null;
  };

  // Lane 1231 / F1: ONE composition-scoped snapshot-presentation reset,
  // used by every deck-identity transition. Fences in-flight filmstrip AND
  // Present work, closes Present, revokes every held object URL, and
  // clears the grant/filmstrip state before the new page list renders —
  // one deck's images must never appear under another deck's page names.
  const resetSnapshotPresentation = () => {
    // All state below is v2-only. Keeping the OFF path out of even these
    // otherwise invisible setters preserves its pre-shell render cadence as
    // well as its DOM and network behavior.
    if (!workspaceShellV2Enabled) return;
    cancelHydrateDebounce();  // W5: a deck transition cancels, never posts late
    filmstripEpochRef.current += 1;
    presentEpochRef.current += 1;
    releaseFilmstripUrls();
    Object.values(presentUrlsLiveRef.current || {})
      .forEach((u) => { if (typeof u === 'string') URL.revokeObjectURL(u); });
    presentUrlsLiveRef.current = {};
    setPresentUrls({});
    presentFetchedRef.current = new Set();
    presentEnteredFsRef.current = false;
    setPresentOpen(false);
    if (document.fullscreenElement && document.exitFullscreen) {
      try {
        const exit = document.exitFullscreen();
        if (exit && exit.catch) exit.catch(() => {});
      } catch (e) { /* identity reset still completes */ }
    }
    setPagesDrawerOpen(false);
    setSnapshotGrant(null);
    setFilmstrip({ state: 'none', urls: null });
  };
  // Lane 1229 / S4 (§4 + Consult #2 carry): dock-switch focus management.
  // The clicked tab's DOM node unmounts with its zone, so the switch names
  // where focus lands next: the panel heading when Edit opens (§4), the
  // matching tab on a tab-driven switch, the toolbar toggle on Escape.
  const dockFocusPending = useRef(null);
  const inspHeadingRef = useRef(null);
  // Lane 1231 / F2: keyboard reorder must not lose focus. The move buttons
  // arm this with the row's stable identity + which control was used; the
  // effect below restores focus after the order re-renders (the same-
  // direction control when still enabled, else the row's selection button).
  const railFocusPending = useRef(null);
  useEffect(() => {
    if (!workspaceShellV2Enabled || !railFocusPending.current) return;
    const { identity, control } = railFocusPending.current;
    railFocusPending.current = null;
    const root = railRef.current || document;
    const rowEl = root.querySelector(`[data-row-identity="${identity}"]`);
    if (!rowEl) return;
    const target = rowEl.querySelector(`[data-act="${control}"]:not(:disabled)`)
      || rowEl.querySelector('.dk-rowbtn');
    if (target) target.focus();
  }, [workspaceShellV2Enabled, selected]);
  useEffect(() => {
    if (!workspaceShellV2Enabled || !dockFocusPending.current) return;
    const target = dockFocusPending.current;
    dockFocusPending.current = null;
    let el = null;
    if (target === 'heading') el = inspHeadingRef.current;
    else if (target === 'toggle') {
      el = document.querySelector('[data-testid="dock-edit-toggle"]');
    } else el = document.querySelector(`[data-testid="dock-${target}"]`);
    if (el) el.focus();
  }, [workspaceShellV2Enabled, dockMode]);
  // Opening Edit starts at Content, without moving keyboard focus from the
  // selected slide. The existing rail remains the only desktop scroll owner.
  useEffect(() => {
    if (!workspaceShellV2Enabled || dockMode !== 'edit') return;
    const rail = inspHeadingRef.current?.closest('.dk-normal-rail');
    if (rail) rail.scrollTop = 0;
  }, [workspaceShellV2Enabled, dockMode]);
  // Lane 766 / Phase 4. THE ANSWER TO "they see the deck and they want some
  // things changed -- how does that work?" A brief edit (ruling an argument out,
  // promoting one, setting how hard we can push) changes what the SERVER will
  // build, but touches nothing in this component's deck state -- so without
  // this counter the preview would keep reading "up to date" while showing a
  // deck built from a brief that no longer exists. Bumping it feeds the
  // existing `deckSignature`, which flips the preview to "out of date", and the
  // existing Refresh preview rebuilds. No second regeneration mechanism.
  const [briefRevision, setBriefRevision] = useState(0);
  // Always-mounted setup control for returning keyboard focus.
  const setupToggleRef = useRef(null);
  // Phase 3: the "Slide library" gallery open/close. Default CLOSED.
  const [libraryOpen, setLibraryOpen] = useState(false);
  // Lane 941: the "left out of this deck" disclosure under the page rail. Default
  // CLOSED -- the COUNT is always visible (that is the part Brad never had), and
  // the reasons are one click away rather than a wall of text over the page list.
  const [withheldOpen, setWithheldOpen] = useState(false);
  // Phase 4: drag-to-reorder. dragIdx = the card being dragged; dragOverIdx = the
  // insertion slot [0..length] under the cursor. The up/down arrows stay as the
  // universal fallback (native HTML5 drag is mouse-only).
  const [dragIdx, setDragIdx] = useState(null);
  const [dragOverIdx, setDragOverIdx] = useState(null);

  // --- load menu + saved decks -------------------------------------------
  const loadTemplates = useCallback(async () => {
    try {
      const r = await fetch(`${API_BASE}/api/decks/templates`, { headers: authHeaders() });
      if (!r.ok) throw new Error(`templates (${r.status})`);
      const data = await r.json();
      setTemplates(data.templates || []);
      setElementEditorEnabled(data.element_editor_enabled === true);
      // Strict === true (matching the editor flag): a missing/older server
      // echo means OFF, so a rolled-back backend instantly restores V1.
      setWorkspaceShellV2Enabled(data.workspace_shell_v2_enabled === true);
    } catch (e) { setError(e.message); }
  }, [authHeaders]);

  const loadSaved = useCallback(async (valid = () => true) => {
    try {
      const r = await fetch(`${API_BASE}/api/decks/compositions`, { headers: authHeaders() });
      if (!r.ok) throw new Error(`saved decks (${r.status})`);
      const decks = (await r.json()).compositions || [];
      if (valid()) setSavedDecks(decks);
    } catch (e) { if (valid()) setError(e.message); }
  }, [authHeaders]);

  useEffect(() => { loadTemplates(); loadSaved(); }, [loadTemplates, loadSaved]);

  // --- supplier typeahead (TODO-B endpoint reuse) ------------------------
  const loadOptions = useCallback(
    (input, callback) => {
      fetch(
        `${API_BASE}/api/suppliers/search?q=${encodeURIComponent(input || '')}&limit=${SUPPLIER_SEARCH_LIMIT}`,
        { headers: authHeaders() }
      )
        .then((r) => (r.ok ? r.json() : { results: [] }))
        .then((d) => callback((d.results || []).map(searchResultToOption)))
        .catch(() => callback([]));
    },
    [authHeaders]
  );
  const debouncedLoad = useMemo(() => debounce(loadOptions, 250), [loadOptions]);

  // --- slide list ops ----------------------------------------------------
  // Slide-order/override model unchanged; the only addition is activeSlideIdx
  // bookkeeping so the editor + preview highlight track add/remove/reorder.
  const addSlide = (key) => {
    if (viewOnly) return;   // Lane 1412: view-only deck
    setActiveSlideIdx(selected.length);      // focus the slide we're appending
    setSelected((s) => [...s, withEditorInstanceIdentity({ template_key: key, overrides: {} })]);
    structuralReset();
  };

  const removeSlide = (idx) => {
    if (viewOnly) return;   // Lane 1412: view-only deck
    const newLen = selected.length - 1;
    setSelected((s) => s.filter((_, i) => i !== idx));
    structuralReset((i) => (i === idx ? null : (i > idx ? i - 1 : i)));
    // Keep the highlight stable and ALWAYS in range (consult condition): shift
    // down if we removed at/above the active slide, then clamp to the new end.
    setActiveSlideIdx((cur) => {
      if (newLen <= 0) return 0;
      const shifted = idx <= cur ? cur - 1 : cur;
      return Math.max(0, Math.min(shifted, newLen - 1));
    });
  };

  const moveSlide = (idx, dir) => {
    if (viewOnly) return;   // Lane 1412: view-only deck
    const j = idx + dir;
    if (j < 0 || j >= selected.length) return;
    reorderReset((i) => (i === idx ? j : (i === j ? idx : i)));
    setSelected((s) => {
      const next = [...s];
      [next[idx], next[j]] = [next[j], next[idx]];
      return next;
    });
    setActiveSlideIdx((cur) => (cur === idx ? j : cur === j ? idx : cur));
  };

  // Phase 4: move a slide from one index to another (drag-to-reorder). Reorders
  // the SAME `selected` list the arrows use, and keeps the active highlight on
  // the moved card as the indices shift.
  const moveSlideTo = (from, to) => {
    if (viewOnly) return;   // Lane 1412: view-only deck
    if (from == null || to == null || from === to) return;
    reorderReset((i) => {
      if (i === from) return to;
      if (from < to && i > from && i <= to) return i - 1;
      if (from > to && i >= to && i < from) return i + 1;
      return i;
    });
    setSelected((s) => {
      if (from < 0 || from >= s.length || to < 0 || to >= s.length) return s;
      const next = [...s];
      const [moved] = next.splice(from, 1);
      next.splice(to, 0, moved);
      return next;
    });
    setActiveSlideIdx((cur) => {
      if (cur === from) return to;
      if (from < cur && to >= cur) return cur - 1;
      if (from > cur && to <= cur) return cur + 1;
      return cur;
    });
  };

  // Drop handler: dragOverIdx is an INSERTION slot in [0..length]; convert it to
  // a final index and reuse moveSlideTo.
  const handleSlideDrop = (insertAt) => {
    const from = dragIdx;
    setDragIdx(null);
    setDragOverIdx(null);
    if (from == null || insertAt == null) return;
    let to = insertAt > from ? insertAt - 1 : insertAt;
    to = Math.max(0, Math.min(to, selected.length - 1));
    moveSlideTo(from, to);
  };

  const templateName = (key) => {
    const t = templates.find((x) => x.template_key === key);
    return t ? t.name : key;
  };

  // --- inline TEXT editing (TODO-F) --------------------------------------
  // Editable fields come from the registry (template.editable_fields); analytical
  // data tokens are absent there, so they are never offered as editable (Rule 116).
  const editableFieldsFor = (key) => {
    if (viewOnly) return [];   // Lane 1412: view-only deck, no side-panel fields
    const t = templates.find((x) => x.template_key === key);
    return (t && Array.isArray(t.editable_fields)) ? t.editable_fields : [];
  };

  // (Part 5's direct `setOverride` mutation is GONE as of Plan B Phase 3:
  // every mutation path — panel keystrokes included — goes through the one
  // transactional writer below, which routes managed positional tokens into
  // stable row objects once a block is materialized.)

  // ⭐ PLAN B PHASE 3 — the authorized-rows layer. The contract arrives from
  // the registry listing (row_contract on GET /api/decks/templates): block and
  // count identity, bounds, field names + managed token patterns, defaults.
  // DENY BY DEFAULT: no contract, no row controls, and the server re-validates
  // every save (helpers/deck_row_edit).
  const rowContractFor = useCallback((key) => {
    const t = templates.find((x) => x.template_key === key);
    return (t && t.row_contract) || null;
  }, [templates]);
  const tokenFromPattern = (pattern, n) => pattern.replace('{n}', String(n));
  const parseManagedToken = (contract, token) => {
    const entries = Object.entries((contract && contract.field_tokens) || {});
    for (let i = 0; i < entries.length; i += 1) {
      const [field, pattern] = entries[i];
      const m = new RegExp(`^${pattern.replace('{n}', '([1-9][0-9]?)')}$`).exec(token);
      if (m) return { field, rowNum: parseInt(m[1], 10) };
    }
    return null;
  };
  const registryDefaultFor = (key, token) => {
    const f = editableFieldsFor(key).find((x) => x.token === token);
    return (f && f.default) || '';
  };

  // The EFFECTIVE rows of an instance's authorized block. Materialized blocks
  // return their stable row objects; un-materialized blocks mirror the server
  // render exactly: the count is the contiguous run of the KEY field in
  // overrides (else the template's default row count — the same formula as
  // deck_intent.block_row_counts), and each value is override ?? registry
  // default ?? '' — never the rendered "N/A" (plan §8.2: defaults fill only
  // genuinely absent values).
  const effectiveRowsFor = useCallback((inst) => {
    if (!inst) return null;
    const contract = rowContractFor(inst.template_key);
    if (!contract) return null;
    const existing = inst.row_blocks && inst.row_blocks[contract.block_key];
    if (Array.isArray(existing)) {
      return { contract, rows: existing, materialized: true };
    }
    const ov = inst.overrides || {};
    const keyPattern = contract.field_tokens[contract.key_field];
    let count = 0;
    for (let n = 1; n <= contract.max_rows; n += 1) {
      const v = ov[tokenFromPattern(keyPattern, n)];
      if (typeof v === 'string' && v.trim() !== '') count = n;
      else break;
    }
    if (count === 0) count = contract.default_rows;
    const rows = [];
    for (let n = 1; n <= count; n += 1) {
      const values = {};
      contract.fields.forEach((field) => {
        const tok = tokenFromPattern(contract.field_tokens[field], n);
        const o = ov[tok];
        values[field] = (typeof o === 'string' && o.trim() !== '')
          ? o : registryDefaultFor(inst.template_key, tok);
      });
      rows.push({ row_id: `r${n}`, values });
    }
    return { contract, rows, materialized: false };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [rowContractFor, templates]);

  // ⭐ PLAN B PHASE 2 — ONE transactional override writer (plan §7.2). Every
  // mutation path (inline commit, side-panel session, undo, hydrate-driven
  // rejection recovery) goes through here; direct setOverride survives only as
  // the no-history keystroke layer the panel coalesces over.
  const pushUndo = (entry) => {
    undoStack.current.push(entry);
    if (undoStack.current.length > UNDO_LIMIT) undoStack.current.shift();
  };
  const currentOverride = (idx, token) => {
    const inst = selected[idx];
    if (!inst) return null;
    // Phase 3: a materialized block's managed tokens read from the row object
    const contract = rowContractFor(inst.template_key);
    const rb = contract && inst.row_blocks && inst.row_blocks[contract.block_key];
    if (Array.isArray(rb)) {
      const parsed = parseManagedToken(contract, token);
      if (parsed && parsed.rowNum >= 1 && parsed.rowNum <= rb.length) {
        return (rb[parsed.rowNum - 1].values || {})[parsed.field] || null;
      }
    }
    const v = inst.overrides ? inst.overrides[token] : undefined;
    return v === undefined ? null : v;
  };
  const applyOverrideTxn = useCallback((idx, token, value, { history = true } = {}) => {
    setSelected((s) => {
      const inst = s[idx];
      if (!inst) return s;
      // Phase 3 routing (plan §8.1): once a block is materialized, a text
      // edit to one of its managed positional tokens updates the ROW OBJECT
      // — it must never recreate a positional override. Undo entries keep
      // the token shape, so undo routes back through here transparently.
      const contract = rowContractFor(inst.template_key);
      const rb = contract && inst.row_blocks
        && inst.row_blocks[contract.block_key];
      if (Array.isArray(rb)) {
        const parsed = parseManagedToken(contract, token);
        // Consult #5 fix: once a block is materialized, a managed token BEYOND
        // its current rows must never fall through to a positional override —
        // that payload is a guaranteed dual-source 422 at save. Drop it.
        if (parsed && (parsed.rowNum < 1 || parsed.rowNum > rb.length)) return s;
        if (parsed && parsed.rowNum >= 1 && parsed.rowNum <= rb.length) {
          const prevVal = (rb[parsed.rowNum - 1].values || {})[parsed.field] || '';
          const nextVal = value == null ? '' : String(value);
          if (prevVal === nextVal) return s;
          if (history) pushUndo({ idx, token, prev: prevVal, next: nextVal });
          const rows = rb.map((r, i) => (i === parsed.rowNum - 1
            ? { ...r, values: { ...(r.values || {}), [parsed.field]: nextVal } }
            : r));
          const out = [...s];
          out[idx] = { ...inst,
            row_blocks: { ...inst.row_blocks, [contract.block_key]: rows } };
          return out;
        }
      }
      const prevRaw = (inst.overrides || {})[token];
      const prev = prevRaw === undefined ? null : prevRaw;
      const next = (value == null || String(value).trim() === '') ? null : String(value);
      if (prev === next) return s;
      if (history) pushUndo({ idx, token, prev, next });
      const ov = { ...(inst.overrides || {}) };
      if (next === null) delete ov[token]; else ov[token] = next;
      const out = [...s];
      out[idx] = { ...inst, overrides: ov };
      return out;
    });
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [rowContractFor]);
  // Row add/delete rebuild the preview automatically (plan §8.5): the deck is
  // saved and re-exported so the server renders the new row set — never
  // browser-synthesized markup. Guarded by a sequence id; row controls stay
  // disabled while a rebuild is pending; a failure keeps the valid dirty
  // state with the old preview marked stale and Refresh as the retry.
  const rebuildSeq = useRef(0);
  const [rowOpPending, setRowOpPending] = useState(false);
  // latest refreshPreview through a ref: scheduleRowRebuild is a stable
  // callback captured by undo/applyRowOp closures, and a stale captured
  // refreshPreview would save a stale deck state.
  const refreshPreviewRef = useRef(null);
  // The rebuild must run AFTER React commits the row-op state — a direct call
  // from the mutator would save the PRE-op deck through the stale closure the
  // ref still holds. The request counter defers it to the next effect pass.
  const [rebuildRequest, setRebuildRequest] = useState(0);
  const scheduleRowRebuild = useCallback(async () => {
    rebuildSeq.current += 1;
    const seq = rebuildSeq.current;
    setRowOpPending(true);
    try {
      if (refreshPreviewRef.current) await refreshPreviewRef.current();
    } finally {
      if (rebuildSeq.current === seq) setRowOpPending(false);
    }
  }, []);

  const undo = useCallback(() => {
    const e = undoStack.current.pop();
    if (!e) return;
    if (e.kind === 'rows') {
      setFitChecksPending((m) => ({ ...m, [e.idx]: true }));
      setSelected((s) => {
        const inst = s[e.idx];
        if (!inst) return s;
        const out = [...s];
        out[e.idx] = { ...inst, overrides: e.prevOverrides,
          row_blocks: e.prevRowBlocks || {} };
        return out;
      });
      setRebuildRequest((n) => n + 1);
      return;
    }
    setFitChecksPending((m) => ({ ...m, [e.idx]: true }));
    applyOverrideTxn(e.idx, e.token, e.prev, { history: false });
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [applyOverrideTxn]);

  const validServerRowBase = useCallback((inst, rowBase, blockOrdinal) => {
    const contract = rowContractFor(inst && inst.template_key);
    if (!contract || !rowBase || rowBase.blockKey !== contract.block_key
      || rowBase.blockOrdinal !== blockOrdinal || !Array.isArray(rowBase.rows)
      || rowBase.rows.length < contract.min_rows
      || rowBase.rows.length > contract.max_rows) return null;
    const seen = new Set();
    const rows = [];
    for (let i = 0; i < rowBase.rows.length; i += 1) {
      const row = rowBase.rows[i];
      if (!row || typeof row.row_id !== 'string'
        || !/^[A-Za-z0-9_-]{1,32}$/.test(row.row_id) || seen.has(row.row_id)
        || !row.values || typeof row.values !== 'object') return null;
      seen.add(row.row_id);
      const values = {};
      for (let j = 0; j < contract.fields.length; j += 1) {
        const field = contract.fields[j];
        if (typeof row.values[field] !== 'string' || row.values[field].length > 2000) return null;
        values[field] = row.values[field];
      }
      if (Object.keys(row.values).some((field) => !contract.fields.includes(field))) return null;
      rows.push({ row_id: row.row_id, values });
    }
    return { contract, rows };
  }, [rowContractFor]);

  const pushElementHistory = (identity, entry, group) => {
    const history = elementHistory.current[identity] || { undo: [], redo: [] };
    // A held D-pad gesture extends its own existing entry; other edits never
    // join it. Coalescing here avoids a release-time snapshot/undo race.
    if (group && group.identity === identity && group.entry
      && history.undo[history.undo.length - 1] === group.entry) {
      group.entry.after = entry.after;
      group.entry.rebuild = group.entry.rebuild || entry.rebuild;
    } else {
      history.undo.push(entry);
      if (group) group.entry = entry;
    }
    if (history.undo.length > UNDO_LIMIT) history.undo.shift();
    history.redo = [];
    elementHistory.current[identity] = history;
  };

  const handleElementSelect = useCallback(({
    instanceIndex, elementId, digest, base, dualContext, nudgeable, rowBase, removable, editing,
    structureRevision,
  }) => {
    if (editorProtocolRef.current !== 2 || !previewRevision
      || structureRevision !== previewRevision || previewStaleRef.current) return;
    if (!workspaceShellV2Enabled
      && instanceIndex !== Math.min(activeSlideIdx, selected.length - 1)) return;
    const inst = selected[instanceIndex];
    if (!inst) return;
    if (workspaceShellV2Enabled && previewFrameRef.current) {
      previewFrameRef.current.focus({ preventScroll: true });
    }
    if (workspaceShellV2Enabled) {
      flushHydrateRef.current();
      setActiveSlideIdx(instanceIndex);
      setDockMode('edit');
    }
    setSelectedElement({ kind: 'source', identity: inst._editor_instance_id,
      instanceIndex, elementId, digest, base, dualContext: dualContext === true,
      nudgeable: nudgeable === true, rowBase, removable: removable === true, editing: editing === true });
  }, [previewRevision, selected, activeSlideIdx, workspaceShellV2Enabled]);

  // The iframe and selected-target inspector use this one transaction for all
  // supported v2 patch fields. It is the sole place that may materialize a
  // server-owned row base, mark fit pending, and write d/t/s.
  const applyElementPatchTxn = useCallback(({
    identity, instanceIndex, elementId, digest, change, rowBase,
  }, { history = true, historyGroup } = {}) => {
    if (editorProtocolRef.current !== 2 || !previewRevision || previewStaleRef.current
      || !change || typeof change !== 'object') return;
    if (Object.prototype.hasOwnProperty.call(change, 't')
      && (typeof change.t !== 'string' || change.t.length > 2000 || /[\r\n]/.test(change.t))) return;
    if (Object.prototype.hasOwnProperty.call(change, 's')
      && !validElementStyle(change.s)) return;
    if (Object.prototype.hasOwnProperty.call(change, 'g')
      && (!change.g || typeof change.g !== 'object'
        || Object.keys(change.g).length !== 2
        || typeof change.g.dx !== 'number' || !Number.isFinite(change.g.dx)
        || typeof change.g.dy !== 'number' || !Number.isFinite(change.g.dy))) return;
    if (Object.prototype.hasOwnProperty.call(change, 'r')
      && (!workspaceShellV2Enabled || change.r !== true || !selectedElement?.removable
        || selectedElement.editing || selectedElement.elementId !== elementId
        || selectedElement.digest !== digest || selectedElement.identity !== identity
        || /(?:^|\.)r[1-9][0-9]*\./.test(elementId))) return;
    const current = selected[instanceIndex];
    if (!current || current._editor_instance_id !== identity) return;
    const rowMatch = /(?:^|\.)r([1-9][0-9]{0,2})\.([A-Za-z0-9_-]{1,32})(?:\.|$)/.exec(elementId);
    let materialized = null;
    if (rowMatch) {
      const ordinal = parseInt(rowMatch[1], 10);
      const existingContract = rowContractFor(current.template_key);
      const existingRows = existingContract && current.row_blocks
        && current.row_blocks[existingContract.block_key];
      if (Array.isArray(existingRows)) {
        if (!existingRows.some((row) => row && row.row_id === rowMatch[2])) return;
      } else {
        materialized = validServerRowBase(current, rowBase, ordinal);
        if (!materialized
          || !materialized.rows.some((row) => row.row_id === rowMatch[2])) return;
      }
    } else if (rowBase !== null) return;
    setFitChecksPending((m) => ({ ...m, [instanceIndex]: true }));
    setSelected((state) => {
      const inst = state.find((item) => item._editor_instance_id === current._editor_instance_id);
      if (!inst) return state;
      const before = elementStateOf(inst);
      let next = { ...inst };
      if (materialized) {
        const ov = { ...(inst.overrides || {}) };
        Object.values(materialized.contract.field_tokens).forEach((pattern) => {
          for (let n = 1; n <= materialized.contract.max_rows; n += 1) {
            delete ov[tokenFromPattern(pattern, n)];
          }
        });
        next.overrides = ov;
        next.row_blocks = { ...(inst.row_blocks || {}),
          [materialized.contract.block_key]: materialized.rows };
      }
      const patches = { ...(inst.element_patches || {}) };
      const changedPatch = { ...(patches[elementId] || {}), d: digest };
      if (change.r === true) changedPatch.r = true;
      if (Object.prototype.hasOwnProperty.call(change, 't')) changedPatch.t = change.t;
      if (Object.prototype.hasOwnProperty.call(change, 's')) {
        if (Object.keys(change.s).length) changedPatch.s = change.s;
        else delete changedPatch.s;
      }
      if (Object.prototype.hasOwnProperty.call(change, 'g')) {
        if (change.g.dx !== 0 || change.g.dy !== 0) changedPatch.g = change.g;
        else delete changedPatch.g;
      }
      if (Object.keys(changedPatch).some((key) => key !== 'd')) patches[elementId] = changedPatch;
      else delete patches[elementId];
      next.element_patches = patches;
      const after = elementStateOf(next);
      if (history) pushElementHistory(inst._editor_instance_id, {
        before, after, rebuild: Boolean(materialized),
      }, historyGroup);
      return state.map((item) => (item._editor_instance_id === inst._editor_instance_id
        ? next : item));
    });
    if (materialized) setRebuildRequest((n) => n + 1);
  }, [previewRevision, selected, rowContractFor, validServerRowBase, workspaceShellV2Enabled, selectedElement]);

  const applyElementTextTxn = useCallback((payload, options) => {
    applyElementPatchTxn({ ...payload, change: { t: payload.value } }, options);
  }, [applyElementPatchTxn]);

  const removalBlockedRef = useRef(false);
  const handleElementEdit = useCallback(({
    instanceIndex, elementId, digest, value, fitRevision, rowBase, remove,
    structureRevision,
  }) => {
    if (editorProtocolRef.current !== 2 || !previewRevision
      || structureRevision !== previewRevision || previewStaleRef.current) {
      setStatus('The preview is out of date. Refresh it to keep editing.');
      return;
    }
    const current = selected[instanceIndex];
    if (!current || fitRevision !== fitRevisionOf(current, 2)) return;
    if (remove === true) {
      if (removalBlockedRef.current) return;
      applyElementPatchTxn({ identity: current._editor_instance_id, instanceIndex,
        elementId, digest, rowBase, change: { r: true } });
    } else applyElementTextTxn({ identity: current._editor_instance_id, instanceIndex,
      elementId, digest, value, rowBase });
  }, [previewRevision, selected, applyElementTextTxn, applyElementPatchTxn]);

  const handleElementNudge = useCallback(({
    instanceIndex, elementId, digest, dx, dy, fitRevision, rowBase,
    structureRevision,
  }) => {
    if (editorProtocolRef.current !== 2 || !previewRevision
      || structureRevision !== previewRevision || previewStaleRef.current) return;
    const current = selected[instanceIndex];
    const selection = selectedElement;
    if (!current || fitRevision !== fitRevisionOf(current, 2)
      || !selection || selection.kind !== 'source' || !selection.nudgeable
      || selection.identity !== current._editor_instance_id
      || selection.elementId !== elementId || selection.digest !== digest) return;
    applyElementPatchTxn({ identity: current._editor_instance_id, instanceIndex,
      elementId, digest, change: { g: { dx, dy } }, rowBase });
  }, [previewRevision, selected, selectedElement, applyElementPatchTxn]);

  const applyAddedElementTxn = useCallback((instanceIndex, addedId, mutate,
    { history = true, historyGroup } = {}) => {
    const current = selected[instanceIndex];
    if (!current || typeof mutate !== 'function') return;
    if (addedId && !(current.added_elements || []).some((box) => box.id === addedId)) return;
    const before = elementStateOf(current);
    const elements = JSON.parse(JSON.stringify(current.added_elements || []));
    const nextElements = mutate(elements);
    if (!Array.isArray(nextElements) || nextElements.length > 100) return;
    if (!nextElements.every((box) => box && /^a[1-9][0-9]{0,2}$/.test(box.id)
      && typeof box.x === 'number' && Number.isFinite(box.x) && box.x >= 0
      && typeof box.y === 'number' && Number.isFinite(box.y) && box.y >= 0
      && typeof box.w === 'number' && Number.isFinite(box.w) && box.w >= 40
      && box.x + box.w <= 1920 && typeof box.t === 'string' && box.t.length <= 2000
      && (!box.s || validElementStyle(box.s)))) return;
    const next = { ...current, added_elements: nextElements };
    const after = elementStateOf(next);
    setFitChecksPending((flags) => ({ ...flags, [instanceIndex]: true }));
    if (history) pushElementHistory(current._editor_instance_id, {
      before, after, rebuild: false,
    }, historyGroup);
    setSelected((state) => state.map((item) =>
      (item._editor_instance_id === current._editor_instance_id ? next : item)));
    if (addedId && !nextElements.some((box) => box.id === addedId)) {
      setSelectedElement(null);
    }
  }, [selected]);

  const validElementFrameTxn = useCallback((instanceIndex, fitRevision,
    structureRevision) => {
    if (editorProtocolRef.current !== 2 || !previewRevision
      || structureRevision !== previewRevision || previewStaleRef.current) return null;
    const current = selected[instanceIndex];
    return current && fitRevision === fitRevisionOf(current, 2) ? current : null;
  }, [previewRevision, selected]);

  const handleAddedAdd = useCallback(({ instanceIndex, fitRevision, structureRevision }) => {
    const current = validElementFrameTxn(instanceIndex, fitRevision, structureRevision);
    if (!current) return;
    const used = new Set((current.added_elements || []).map((box) => box.id));
    let number = 1;
    while (used.has(`a${number}`) && number <= 999) number += 1;
    if (number > 999) return;
    const id = `a${number}`;
    applyAddedElementTxn(instanceIndex, null, (elements) => [
      ...elements, { id, x: 120, y: 120, w: 420, t: 'New text', s: {} },
    ]);
    setSelectedElement({ kind: 'added', identity: current._editor_instance_id,
      instanceIndex, addedId: id });
  }, [validElementFrameTxn, applyAddedElementTxn]);

  const handleAddedSelect = useCallback(({ instanceIndex, addedId, structureRevision }) => {
    if (!previewRevision || structureRevision !== previewRevision || previewStaleRef.current) return;
    const current = selected[instanceIndex];
    if (!current || !(current.added_elements || []).some((box) => box.id === addedId)) return;
    if (workspaceShellV2Enabled) {
      // Added-box mousedown prevents native focus to support dragging. The
      // selected-not-editing Delete key needs the same iframe handoff as text.
      previewFrameRef.current?.focus({ preventScroll: true });
      flushHydrateRef.current();
      setActiveSlideIdx(instanceIndex);
      setDockMode('edit');
    }
    setSelectedElement({ kind: 'added', identity: current._editor_instance_id,
      instanceIndex, addedId });
  }, [previewRevision, selected, workspaceShellV2Enabled]);

  const handleAddedMove = useCallback(({ instanceIndex, addedId, x, y,
    fitRevision, structureRevision }) => {
    if (!validElementFrameTxn(instanceIndex, fitRevision, structureRevision)) return;
    applyAddedElementTxn(instanceIndex, addedId, (elements) => elements.map((box) =>
      (box.id === addedId ? { ...box, x, y } : box)));
  }, [validElementFrameTxn, applyAddedElementTxn]);

  const handleAddedResize = useCallback(({ instanceIndex, addedId, w,
    fitRevision, structureRevision }) => {
    if (!validElementFrameTxn(instanceIndex, fitRevision, structureRevision)) return;
    applyAddedElementTxn(instanceIndex, addedId, (elements) => elements.map((box) =>
      (box.id === addedId ? { ...box, w } : box)));
  }, [validElementFrameTxn, applyAddedElementTxn]);

  const handleElementReset = useCallback(() => {
    if (!selectedElement || editorProtocolRef.current !== 2
      || !previewRevision || previewStaleRef.current) return;
    const current = selected[selectedElement.instanceIndex];
    if (!current || current._editor_instance_id !== selectedElement.identity) return;
    const patch = current.element_patches && current.element_patches[selectedElement.elementId];
    if (!patch || patch.d !== selectedElement.digest || typeof patch.t !== 'string') return;
    setFitChecksPending((flags) => ({ ...flags, [selectedElement.instanceIndex]: true }));
    setSelected((state) => {
      const inst = state.find((item) => item._editor_instance_id === selectedElement.identity);
      if (!inst) return state;
      const before = elementStateOf(inst);
      const patches = { ...(inst.element_patches || {}) };
      const retained = { ...patches[selectedElement.elementId] };
      delete retained.t;
      if (Object.keys(retained).some((key) => key !== 'd')) {
        patches[selectedElement.elementId] = retained;
      } else {
        delete patches[selectedElement.elementId];
      }
      const next = { ...inst, element_patches: patches };
      const after = elementStateOf(next);
      pushElementHistory(inst._editor_instance_id, { before, after, rebuild: false });
      return state.map((item) => (item._editor_instance_id === inst._editor_instance_id
        ? next : item));
    });
  }, [previewRevision, selected, selectedElement]);

  const restoreElementHistory = useCallback((instanceIndex, direction) => {
    const current = selected[instanceIndex];
    if (!current) return;
    const history = elementHistory.current[current._editor_instance_id];
    const from = direction === 'undo' ? history && history.undo : history && history.redo;
    const to = direction === 'undo' ? history && history.redo : history && history.undo;
    if (!from || !from.length) return;
    const entry = from.pop();
    to.push(entry);
    const stateToRestore = direction === 'undo' ? entry.before : entry.after;
    setFitChecksPending((m) => ({ ...m, [instanceIndex]: true }));
    setSelected((state) => state.map((inst) => (inst._editor_instance_id === current._editor_instance_id
      ? { ...inst, ...elementStateOf(stateToRestore) } : inst)));
    if (entry.rebuild) setRebuildRequest((n) => n + 1);
  }, [selected]);

  const handleElementHistory = useCallback((direction) => ({
    instanceIndex, structureRevision,
  }) => {
    if (editorProtocolRef.current !== 2 || !previewRevision
      || structureRevision !== previewRevision || previewStaleRef.current) return;
    restoreElementHistory(instanceIndex, direction);
  }, [previewRevision, restoreElementHistory]);

  // Undo entries are unsafe after any structural mutation. Fit state is also
  // positional, but clearing it would turn a known overflow into permission to
  // download. Each mutator supplies the exact old-index -> new-index mapping.
  const structuralReset = (mapIndex = (i) => i) => {
    undoStack.current = [];
    setFitErrors((m) => remapIndexFlags(m, mapIndex));
    setFitChecksPending((m) => remapIndexFlags(m, mapIndex));
  };

  const reorderReset = (mapIndex) => {
    structuralReset(mapIndex);
    // Keep W6's existing fail-closed fallback when the independent editor
    // capability or exact shell marker seam is missing: that frame cannot
    // provide the shell's commit-before-hydrate settlement guarantee.
    if (!workspaceShellV2Enabled || !elementEditorEnabled
      || !exportDoc.includes('</head><body>') || previewStaleRef.current) return;
    flushHydrateRef.current();
    setSelectedElement((value) => value
      ? { ...value, instanceIndex: mapIndex(value.instanceIndex) } : value);
    reorderEpoch.current += 1;
    setReorderRequest((n) => n + 1);
    setReorderPending(true);
  };

  const cancelReorder = () => {
    clearTimeout(automaticTimer.current);
    setVisibleBuild(null);
    workspaceEpoch.current += 1;
    reorderEpoch.current += 1;
    settlementRef.current?.finish(false);
    setBusy(false);
    setPackDownloading(false);
    setReorderPending(false);
    setPreviewOrder([]);
  };

  // Cmd/Ctrl-Z at the app level (plan §7.7): honored when focus is in the
  // parent and NOT inside a text field (native text undo stays native); the
  // idle iframe posts undo-request instead, handled by the same undo().
  useEffect(() => {
    const onKey = (e) => {
      if (chatWritingRef.current) return;   // the deck is being changed from the chat
      if (!(e.metaKey || e.ctrlKey) || e.shiftKey) return;
      if (String(e.key).toLowerCase() !== 'z') return;
      const t = e.target;
      if (workspaceShellV2Enabled && t?.closest?.('[data-deck-full-plan]')) return;
      const tag = t && t.tagName;
      if (tag === 'INPUT' || tag === 'TEXTAREA' || (t && t.isContentEditable)) return;
      e.preventDefault();
      if (editorProtocolRef.current === 2) restoreElementHistory(activeSlideIdx, 'undo');
      else undo();
    };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [undo, restoreElementHistory, activeSlideIdx, workspaceShellV2Enabled]);

  // Inline-editing capability (plan §7.5): below 1160px or on a coarse
  // pointer the frame editor is disabled and the panel is the editor.
  useEffect(() => {
    if (typeof window.matchMedia !== 'function') return undefined;
    const mq = window.matchMedia('(max-width: 1160px), (pointer: coarse)');
    const apply = () => setInlineCapable(!mq.matches);
    apply();
    if (mq.addEventListener) mq.addEventListener('change', apply);
    else if (mq.addListener) mq.addListener(apply);
    return () => {
      if (mq.removeEventListener) mq.removeEventListener('change', apply);
      else if (mq.removeListener) mq.removeListener(apply);
    };
  }, []);

  // Build the per-instance slide_order payload, dropping empty values (the editor
  // already clears them, but a saved deck loaded with stale empties is normalized).
  // Phase 3: stable authored rows travel with the instance — the clean/save
  // payload is one of the §8.1 seams; a legacy instance emits no row_blocks key.
  const cleanSlideOrder = (sel) => sel.map((inst) => {
    const kept = {};
    for (const [tok, val] of Object.entries(inst.overrides || {})) {
      if (typeof val === 'string' && val.trim() !== '') kept[tok] = val;
    }
    const out = { template_key: inst.template_key, overrides: kept };
    if (inst.row_blocks && Object.keys(inst.row_blocks).length) {
      out.row_blocks = inst.row_blocks;
    }
    // Preserve the complete v2 envelope even when the server flag is OFF.
    // Empty containers are intentional reset state and must round-trip too.
    if (Object.prototype.hasOwnProperty.call(inst, 'element_patches')) {
      out.element_patches = inst.element_patches;
    }
    if (Object.prototype.hasOwnProperty.call(inst, 'added_elements')) {
      out.added_elements = inst.added_elements;
    }
    return out;
  });

  const resetEditor = () => {
    setPresentationTemplates(null); nativeRevision.current = null;
    resetSnapshotPresentation();  // Lane 1231 / F1: deck identity changes
    setSubject(null); setTitle(''); setMeetingType('QBR'); setSelected([]);
    setActiveSlideIdx(0);
    if (workspaceShellV2Enabled) cancelReorder();
    setEditingId(null); setExportDoc(''); setOmitted([]); setPreviewMode('slide');
    setAudienceFiltered({ keys: [], notice: '' });
    setExportedStructureSig(null); setPreviewRevision(null); setFitErrors({});
    setFitChecksPending({});
    undoStack.current = [];
    elementHistory.current = {};
    editorProtocolRef.current = 0;
    setEditorProtocolVersion(0);
    setSelectedElement(null);
    setExportedAt(null); setStatus(null); setError(null);
    setIntake(null); setSavedSignature(null); setSetupOpen(false); setOutOpen(false);
    setViewOnly(null); setCanDelete(true);
    setBriefRevision(0);
    if (onCloseDeck) onCloseDeck();
  };

  // --- staleness signature (catch ALL mutation sources) ------------------
  // subject is null until picked -> guard with subject?.supplier_id so the
  // signature never throws before the first selection (consult condition).
  // `selected` now carries each instance's overrides, so it captures BOTH the
  // slide order AND every per-instance edit -- one field covers all mutation
  // sources except the top-bar fields.
  //
  // Lane 789 EXTRACTED the formula so `loadDeck` can compute the signature of
  // the deck it just loaded WITHOUT waiting for a re-render. A second inline
  // copy of the field list is exactly how the two would drift, and a drifted
  // "All changes saved" is a lie about the buyer's own work.
  const deckSignature = useMemo(() => signatureOf({
    supplierId: subject ? subject.supplier_id : null,
    title,
    meetingType,
    order: selected,
    briefRevision,
  }), [subject, title, meetingType, selected, briefRevision]);
  // Phase 2 (plan §7.3): staleness is STRUCTURAL. Text overrides hydrate into
  // the existing preview, so they no longer flip it to "out of date"; the
  // ordinary dirty/save state below still records the unsaved work.
  const structureSignature = useMemo(() => structureSignatureOf({
    supplierId: subject ? subject.supplier_id : null,
    title,
    meetingType,
    // Phase 3: row TOPOLOGY is structure (ids per block); row values are not
    // — mirrors the server-side compute_structure_revision extension.
    orderKeys: selected.map((x) => (x.row_blocks
      && Object.keys(x.row_blocks).length
      ? {
        k: x.template_key,
        rows: Object.fromEntries(Object.entries(x.row_blocks).map(
          ([b, rows]) => [b, (rows || []).map((r) => r && r.row_id)])),
      }
      : x.template_key)),
    briefRevision,
  }), [subject, title, meetingType, selected, briefRevision]);
  const reorderCompatible = workspaceShellV2Enabled && Boolean(exportDoc)
    && unorderedStructure(structureSignature, selected)
      === unorderedStructure(exportedStructureSig, previewOrder);
  const instantReorder = reorderPending && reorderCompatible;
  const previewStale = Boolean(exportDoc) && structureSignature !== exportedStructureSig
    && !instantReorder;
  reorderLatest.current = { signature: deckSignature, compatible: reorderCompatible,
    identity: selected[Math.min(activeSlideIdx, selected.length - 1)]?._editor_instance_id,
    mode: workspaceShellV2Enabled ? 'deck' : previewMode, revision: previewRevision };
  // All frame messages remain unchanged. Translate positional authority once,
  // at the parent boundary, against the document actually being displayed.
  const frameIndex = (index) => workspaceShellV2Enabled && previewOrder.length
    ? previewOrder.findIndex((inst) => inst._editor_instance_id
      === selected[index]?._editor_instance_id) : index;
  const fromFrame = (handler) => !workspaceShellV2Enabled ? handler : (message) => {
    const original = previewOrder[message.instanceIndex];
    const index = original ? selectedRef.current.findIndex((inst) =>
      inst._editor_instance_id === original._editor_instance_id) : message.instanceIndex;
    if (index >= 0) handler({ ...message, instanceIndex: index });
  };
  previewStaleRef.current = previewStale;
  // Undo entries are keyed by instance INDEX, so any structural change not
  // routed through the explicit mutators still clears history. Fit flags are
  // remapped by those mutators and must never be erased here.
  const prevKeysRef = useRef(null);
  useEffect(() => {
    const keys = selected.map((x) => x.template_key).join('\u0000');
    if (prevKeysRef.current != null && prevKeysRef.current !== keys) {
      undoStack.current = [];
    }
    prevKeysRef.current = keys;
  }, [selected]);

  // Is there anything to save, and is it unsaved? Both are needed: an empty
  // editor has no saved signature either, and offering to save nothing is noise.
  const canSave = !!editingId && selected.length > 0 && !!subject;
  const dirty = canSave && deckSignature !== savedSignature;
  dirtyRef.current = dirty;

  // --- save (create or update) -------------------------------------------
  const performSave = async ({ clearHistory = true, forPreview = false,
    silent = false, valid = () => true, workspaceValid = valid } = {}) => {
    if (!silent) { setError(null); setStatus(null); }
    if (chatReload) { setError(chatReload); return null; }
    if (!editingId) { setError('Start a new deck in chat.'); return null; }
    // Lane 1412: a view-only deck is never written; preview and downloads use
    // the saved deck as it is.
    if (viewOnly) return editingId;
    if (!subject) { setError('Pick a subject supplier first.'); return null; }
    if (selected.length === 0) { setError('Add at least one slide.'); return null; }
    if (!forPreview && fitError) {
      // Plan §7.4: overflow blocks save and (through save-first) both
      // downloads -- a supplier can never receive a clipped page.
      setError('Shorten the highlighted text to fit the page before saving.');
      return null;
    }
    if (!forPreview && fitPending) {
      setError('Build or revisit the edited page in the preview so its fit can be checked before saving.');
      return null;
    }
    if (!silent) setBusy(true);
    try {
      // slide_order carries each instance's own overrides now (Part 5), so a single
      // create OR update persists both order AND edits -- no follow-up PUT.
      const slideOrder = cleanSlideOrder(selected);
      let compositionId = editingId;
      if (editingId) {
        const nativeState = nativeRevision.current;
        const payload = {title:title || 'Untitled deck',meeting_type:meetingType,slide_order:slideOrder};
        if (nativeState?.compositionId === editingId) {
          // Recover an uncertain prior response before replacing its request identity.
          if (nativeState.pending) {
            const recovery = await fetch(`${API_BASE}/api/decks/compositions/${editingId}`,{headers:authHeaders()});
            if (!recovery.ok) throw new Error('The previous save could not be checked. Try saving again.');
            const current = await recovery.json();
            if (!workspaceValid() || nativeRevision.current !== nativeState) return null;
            if (current.intake.last_edit_id === nativeState.pending.body.edit_id) {
              nativeState.revision = current.intake.edit_revision;
              nativeState.pending = null;
            } else if (current.intake.edit_revision !== nativeState.revision) {
              throw new Error('A newer version exists. Reopen the deck before saving.');
            }
          }
          const fingerprint = JSON.stringify(payload);
          if (!nativeState.pending || nativeState.pending.fingerprint !== fingerprint) {
            nativeState.pending = {fingerprint,body:{...payload,base_revision:nativeState.revision,edit_id:crypto.randomUUID()}};
          }
        }
        const r = await fetch(`${API_BASE}/api/decks/compositions/${editingId}`, {
          method: 'PUT', headers: authHeaders(),
          body: JSON.stringify(nativeState?.compositionId === editingId ? nativeState.pending.body : payload),
        });
        if (!r.ok) {
          const failure = (await r.json().catch(() => ({}))).detail;
          throw new Error(failure?.message || (typeof failure === 'string' ? failure : `Save failed (${r.status})`));
        }
        if (nativeState?.compositionId === editingId) {
          const saved = await r.json();
          if (!workspaceValid() || nativeRevision.current !== nativeState) return null;
          nativeState.revision = saved.revision;
          nativeState.pending = null;
        }
      }
      if (!valid()) return null;
      if (!silent) setStatus('Saved.');
      // Lane 789: the signature AS SUBMITTED. If the buyer typed while the PUT
      // was in flight the deck has already moved on, and the toolbar correctly
      // keeps saying there are unsaved changes.
      setSavedSignature(deckSignature);
      // Plan §7.2: undo across an EXPLICIT save is out of scope -- the Save
      // button and the save-first downloads clear the history. The automatic
      // row-op rebuild also saves (the export renders the saved deck), but
      // clearing there would erase the row operation's own undo entry the
      // instant it was created (§8.5 makes row ops undoable), so it opts out.
      if (clearHistory) {
        undoStack.current = [];
        elementHistory.current = {};
      }
      await loadSaved(valid);
      return compositionId;
    } catch (e) {
      if (valid()) setError(e.message);
      return null;
    } finally {
      if (!silent && valid()) setBusy(false);
    }
  };

  // Every v2 persistence/export entry shares the writer, including explicit
  // Save and downloads. Resolve the work after prior writes finish so a queued
  // reorder saves the latest state. A workspace transition abandons UI effects.
  const queueWriter = (kind, options) => {
    const epoch = workspaceEpoch.current;
    const valid = () => workspaceAlive.current && workspaceEpoch.current === epoch;
    const run = () => valid() ? writerWork.current[kind](options, valid) : undefined;
    const next = writerSerial.current.then(run, run);
    writerSerial.current = next;
    writerPending.current += 1;
    next.then(() => { writerPending.current -= 1; }, () => { writerPending.current -= 1; });
    return next;
  };
  writerWork.current.save = (_, valid) => performSave({ valid });
  const save = (options) => {
    if (!workspaceShellV2Enabled) return performSave(options);
    setError(null); setStatus(null);
    return queueWriter('save');
  };

  // --- delete the loaded deck --------------------------------------------
  // Lane 828. Acts ONLY on the deck currently open in the editor, never on a row
  // in the picker, and that is the whole misclick defence: saved decks are shared
  // across the company and (for every deck that existed before this lane) record
  // no author at all, so the person deleting is quite often not the person who
  // built it. Requiring the deck to be LOADED means you have already seen its
  // title, its subject and its slides before the confirm appears.
  //
  // ⚠️ resetEditor() ON SUCCESS IS LOAD-BEARING, not tidiness. `save` PUTs when
  // editingId is set and POSTs otherwise; leaving a deleted id in editingId would
  // make the next Save PUT to a deck the server has removed, 404, and silently
  // strand the work still sitting in editor state.
  const deleteDeck = async () => {
    if (!editingId) return;
    const label = title || 'Untitled deck';
    // eslint-disable-next-line no-alert
    if (!window.confirm(
      `Delete "${label}"? Saved decks are shared, so this removes it for everyone at your company.`
    )) return;
    setError(null); setStatus(null); setBusy(true);
    try {
      const r = await fetch(`${API_BASE}/api/decks/compositions/${editingId}`, {
        method: 'DELETE', headers: authHeaders(),
      });
      if (!r.ok) {
        const d = await r.json().catch(() => ({}));
        const detail = typeof d.detail === 'object' ? d.detail.message : d.detail;
        throw new Error(detail || `Delete failed (${r.status})`);
      }
      resetEditor();
      setStatus('Deck deleted.');
      await loadSaved();
    } catch (e) {
      setError(e.message);
    } finally {
      setBusy(false);
    }
  };

  // --- preview + download (one export call feeds both) -------------------
  // One export call feeds preview AND download. `intent` distinguishes them for
  // the Lane-72 Section-18 guardrail: intent='preview' is NEVER gated (the buyer
  // viewing their own deck); intent='download' (default) is refused with HTTP 409
  // when the deck has internal-only slides that have not been acknowledged. On
  // that 409 we return {needsConfirmation, internalSlides} instead of throwing,
  // so download() can warn-and-confirm rather than surface a generic error.
  const exportHtml = async (compositionId, { intent = 'download', acknowledgeInternal = false } = {}) => {
    const qs = new URLSearchParams({
      intent,
      acknowledge_internal: acknowledgeInternal ? 'true' : 'false',
    });
    const r = await fetch(
      `${API_BASE}/api/decks/compositions/${compositionId}/export?${qs.toString()}`,
      { method: 'POST', headers: authHeaders() }
    );
    if (r.status === 409) {
      const body = await r.json().catch(() => ({}));
      const detail = body.detail || {};
      if (detail.code === 'INTERNAL_SLIDES_REQUIRE_CONFIRMATION') {
        return { needsConfirmation: true, internalSlides: detail.internal_slides || [] };
      }
    }
    if (!r.ok) throw new Error(`Export failed (${r.status})`);
    const html = await r.text();
    const omittedHeader = r.headers.get('X-Deck-Omitted-Slides') || '';
    // Lane 905. Read on BOTH intents, so a buyer who goes straight to Download
    // without refreshing the preview is still told why a page is missing.
    const filteredHeader = r.headers.get('X-Deck-Audience-Filtered') || '';
    return {
      html,
      omitted: omittedHeader ? omittedHeader.split(',').filter(Boolean) : [],
      audienceFiltered: {
        keys: filteredHeader ? filteredHeader.split(',').filter(Boolean) : [],
        notice: r.headers.get('X-Deck-Audience-Notice') || '',
      },
      // Phase 2: present on preview responses only; the frame echoes its meta
      // twin on every message and the parent compares (plan §4.1/§4.2).
      structureRevision: r.headers.get('X-Deck-Structure-Revision') || null,
      // Lane 1229 / S3 (§11.4): the preview hook's snapshot echo. Status is
      // always present on flag-ON preview responses; the descriptor only when
      // a readable bundle minted a grant. Consumed by the v2 filmstrip only.
      snapshot: {
        status: r.headers.get('X-Deck-Snapshot-Status') || null,
        descriptor: r.headers.get('X-Deck-Snapshot-Descriptor') || null,
      },
    };
  };

  // On-demand refresh = the existing save-then-export flow (save semantics
  // unchanged). Caches the whole-deck export; V1 derives an isolated srcDoc,
  // while workspace-shell V2 hydrates one persistent marked document. Records
  // the deck signature so the preview reads current.
  // Ryan-approved S6d exception: a temporary correlation value travels in the
  // existing fitRevision field. It is never a content revision or fit truth.
  // The unchanged SHELL2 frame commits before applying a disabling hydrate,
  // then echoes fit status after that commit. Old/unrelated reports cannot ack.
  const settleBeforeSwap = async (valid) => {
    while (valid()) {
      if (document.activeElement === previewFrameRef.current
        || editorProtocolRef.current !== 2) {
        await new Promise((resolve) => setTimeout(resolve, 50));
        continue;
      }
      const current = reorderLatest.current;
      const rendered = previewOrder.map((inst, i) => ({ inst, i }))
        .filter(({ inst }) => !omitted.includes(inst.template_key)
          && !(audienceFiltered.keys || []).includes(inst.template_key));
      // An export with no rendered pages has no editing buffer to settle.
      if (!rendered.length) return true;
      const target = rendered.find(({ inst }) => inst._editor_instance_id === current.identity) || rendered[0];
      const record = {
        nonce: `dk-settle:${previewGeneration}:${++settlementSequence.current}`,
        frame: previewFrameRef.current?.contentWindow,
        revision: current.revision, identity: current.identity, mode: current.mode,
        instanceIndex: target.i,
      };
      const stillCurrent = () => valid() && record.frame === previewFrameRef.current?.contentWindow
        && record.identity === reorderLatest.current.identity
        && record.mode === reorderLatest.current.mode
        && document.activeElement !== previewFrameRef.current;
      const acknowledged = await new Promise((resolve) => {
        const timer = setInterval(() => { if (!stillCurrent()) record.finish(false); }, 50);
        record.valid = stillCurrent;
        record.finish = (ok) => {
          clearInterval(timer);
          if (settlementRef.current === record) {
            settlementRef.current = null;
            if (workspaceAlive.current) setSettlement(null);
          }
          resolve(ok);
        };
        settlementRef.current = record;
        setSettlement(record);
      });
      if (acknowledged && stillCurrent()) return true;
    }
    return false;
  };
  const acknowledgeSettlement = (message) => {
    const record = settlementRef.current;
    if (!record || message.fitRevision !== record.nonce) return false;
    if (record.valid() && message.structureRevision === record.revision
      && message.instanceIndex === record.instanceIndex) {
      // Commit messages precede this ack on the same frame's message channel.
      // Flush their queued React state before comparing the submitted deck.
      flushSync(() => flushHydrateRef.current());
      record.finish(true);
    }
    return true; // A settlement response can never clear a real fit flag.
  };

  const performRefresh = async ({ silent = false, token = null } = {}) => {
    const epoch = workspaceEpoch.current;
    const workspaceValid = () => workspaceAlive.current && workspaceEpoch.current === epoch;
    const valid = () => !workspaceShellV2Enabled || (workspaceAlive.current
      && workspaceEpoch.current === epoch
      && reorderEpoch.current === token
      && (!silent || reorderLatest.current.compatible));
    if (!valid()) return;
    const submittedSignature = deckSignature;
    // Preview generation is the one safe persistence bypass: it creates no
    // supplier artifact and exists specifically so the frame can measure the
    // draft. Explicit Save and both downloads remain fail-closed above.
    const started = Date.now();
    if (workspaceShellV2Enabled && !silent) setVisibleBuild({ token, started, expected: expectedBuildTime(editingId) });
    try {
      const id = await performSave({ clearHistory: false, forPreview: true, silent, valid, workspaceValid });
      if (!id || !valid()) return;
      if (!silent) setBusy(true);
      // intent='preview' -> NEVER gated by the §18 guardrail, so previewing an
      // internal-only deck stays smooth (the buyer is viewing their own screen;
      // the DOWNLOAD is the action that gates).
      // (history survives a refresh/rebuild save -- see save()'s clearHistory)
      const { html, omitted: om, audienceFiltered: af, structureRevision,
        snapshot } = await exportHtml(id, { intent: 'preview' });
      const duration = Date.now() - started;
      if (!valid()) return;
      if (silent) {
        if (!await settleBeforeSwap(valid)) return;
        if (reorderLatest.current.signature !== submittedSignature) {
          setReorderRequest((n) => n + 1);
          return;
        }
      }
      if (workspaceShellV2Enabled && !silent && reorderLatest.current.signature !== submittedSignature) {
        // Text/geometry changed while the visible build ran. Save the current
        // draft through the same writer before publishing its new document.
        refreshPreviewRef.current();
        return;
      }
      if (workspaceShellV2Enabled) {
        recordBuildTime(id, duration);
        setPreviewOrder(selected);
        setPreviewGeneration((n) => n + 1);
        if (silent) setSelectedElement(null);
        setReorderPending(false);
        // Measurements from the old document cannot certify the new layout.
        if (silent) setFitChecksPending(Object.fromEntries(selected.map((_, i) => [i, true])));
      }
      setExportDoc(html);
      setOmitted(om);
      setAudienceFiltered(af);
      setExportedStructureSig(structureSignature);
      setPreviewRevision(structureRevision);
      // Lane 1229 / S3: one grant per preview build. The filmstrip effect
      // consumes it exactly once — no polling, no retry storm (§9); the
      // next Refresh preview re-mints. Lane 1231 (review note): the
      // assignment itself is now flag-gated too, so the OFF path performs
      // zero snapshot state work.
      if (workspaceShellV2Enabled) {
        setSnapshotGrant({
          status: snapshot.status,
          descriptor: snapshot.descriptor,
          compositionId: id,
          at: Date.now(),
        });
      }
      // Do not clear fit state here. The newly loaded frame must explicitly
      // report the current page before either a known failure or pending check
      // is released.
      setExportedAt(new Date().toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' }));
    } catch (e) { if (valid()) setError(e.message); }
    finally {
      if (!silent && valid()) setBusy(false);
      if (workspaceShellV2Enabled && !silent && workspaceAlive.current) {
        setVisibleBuild(value => value?.token === token ? null : value);
      }
    }
  };
  refreshWork.current = performRefresh;
  writerWork.current.refresh = async (request, valid) => {
    try { return await refreshWork.current(request); }
    finally { if (!request.silent && valid()) setBusy(false); }
  };
  // One save/export pipeline, one writer. A queued superseded request exits
  // before saving; a running request finishes before the latest order saves.
  const refreshPreview = (options = {}) => {
    if (!workspaceShellV2Enabled) return performRefresh();
    const request = options.silent || options.automatic ? options : { token: ++reorderEpoch.current };
    return queueWriter('refresh', request);
  };
  refreshPreviewRef.current = refreshPreview;
  useEffect(() => {
    if (reorderRequest > 0 && reorderPending) {
      refreshPreviewRef.current({ silent: true, token: reorderEpoch.current });
    }
  // Requests are emitted by the same reorder transaction, after state commits.
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [reorderRequest]);
  useEffect(() => {
    if (!workspaceShellV2Enabled && rebuildRequest > 0) scheduleRowRebuild();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [rebuildRequest]);

  // S7c: requests only — the S6d writer remains the sole save/export owner.
  // Last ATTEMPT (not last success) prevents a failed build from retrying itself.
  // editingId is deliberately absent: adopting the first POST ID is not a load.
  const automaticKey = `${workspaceEpoch.current}:${structureSignature}:${rebuildRequest}`;
  useEffect(() => {
    if (!workspaceShellV2Enabled || workspaceLoading) return undefined;
    if (instantReorder) { automaticAttempt.current = automaticKey; return undefined; }
    if (automaticAttempt.current === automaticKey) return undefined;
    const token = ++reorderEpoch.current; // Fence old publication BEFORE debounce.
    if (!canSave) { automaticAttempt.current = automaticKey; setVisibleBuild(null); setBusy(false); return undefined; }
    automaticTimer.current = setTimeout(() => {
      automaticAttempt.current = automaticKey;
      refreshPreviewRef.current({ automatic: true, token });
    }, 350);
    return () => clearTimeout(automaticTimer.current);
  }, [workspaceShellV2Enabled, workspaceLoading, automaticKey, instantReorder, canSave]);

  // Lane 1229 / S3: resolve one snapshot grant into thumbnail object URLs.
  // Thumb variant first; a pre-derivative (contract-v1) bundle 404s the
  // thumb route and the FULL image is the banked fallback. Any non-404
  // failure yields a labeled placeholder for that tile — never a retry
  // loop (§9). All URLs are revoked on replacement and on unmount.
  useEffect(() => {
    if (!workspaceShellV2Enabled) return undefined;
    const revoke = (urls) => (urls || [])
      .forEach((u) => { if (u) URL.revokeObjectURL(u); });
    if (!snapshotGrant || !snapshotGrant.descriptor
        || snapshotGrant.status !== 'ready') {
      releaseFilmstripUrls();
      setFilmstrip({
        state: snapshotGrant ? (snapshotGrant.status || 'unavailable') : 'none',
        urls: null,
      });
      return undefined;
    }
    let cancelled = false;
    // Lane 1231 / F1+F7: a composition transition bumps the epoch; every
    // await below re-checks it, so old-deck work never publishes and its
    // URLs are revoked rather than leaked.
    const epoch = filmstripEpochRef.current;
    const stale = () => cancelled || filmstripEpochRef.current !== epoch;
    const ownedUrls = new Set();
    let transferred = false;
    (async () => {
      // F7: the old URL set is dead the moment replacement starts — the
      // 'loading' state renders placeholders, so retaining it is pure leak.
      releaseFilmstripUrls();
      setFilmstrip({ state: 'loading', urls: null });
      try {
        const base = `${API_BASE}/api/decks/compositions/`
          + `${snapshotGrant.compositionId}/snapshots/${snapshotGrant.descriptor}`;
        const mr = await fetch(base, { headers: authHeaders() });
        if (stale()) return;
        if (mr.status === 410) {
          setFilmstrip({ state: 'expired', urls: null });
          return;
        }
        if (!mr.ok) {
          setFilmstrip({ state: 'unavailable', urls: null });
          return;
        }
        const manifest = await mr.json();
        if (stale()) return;
        if (manifest.status !== 'ready' || !Array.isArray(manifest.pages)) {
          setFilmstrip({ state: manifest.status || 'unavailable', urls: null });
          return;
        }
        // Own each URL as soon as it is minted. Promise.all may remain
        // pending forever when one object request hangs; the effect cleanup
        // must still be able to revoke URLs created by its completed peers.
        const urls = await Promise.all(manifest.pages.map(async (page) => {
          const variants = [`${base}/pages/${page.page_number}/thumb`,
            `${base}/pages/${page.page_number}`];
          for (let i = 0; i < variants.length; i += 1) {
            try {
              // eslint-disable-next-line no-await-in-loop
              const ir = await fetch(variants[i], { headers: authHeaders() });
              // eslint-disable-next-line no-await-in-loop
              if (ir.ok) {
                // eslint-disable-next-line no-await-in-loop
                const blob = await ir.blob();
                if (stale()) return null;
                const url = URL.createObjectURL(blob);
                if (stale()) {
                  URL.revokeObjectURL(url);
                  return null;
                }
                ownedUrls.add(url);
                return url;
              }
              if (ir.status !== 404) return null;
            } catch (e) { return null; }
          }
          return null;
        }));
        if (stale()) {
          revoke(urls);
          ownedUrls.clear();
          return;
        }
        filmstripUrlsRef.current = urls;
        transferred = true;
        ownedUrls.clear();
        setFilmstrip({ state: 'ready', urls });
      } catch (e) {
        if (!stale()) setFilmstrip({ state: 'unavailable', urls: null });
      }
    })();
    return () => {
      cancelled = true;
      if (!transferred) {
        revoke(Array.from(ownedUrls));
        ownedUrls.clear();
      }
    };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [workspaceShellV2Enabled, snapshotGrant]);

  // Unmount: fence FIRST (Lane 1231 / F7 — an in-flight full-image fetch
  // resolving after unmount must see a bumped epoch and revoke its own
  // result instead of writing to dead state), then release every held
  // thumbnail and Present object URL.
  useEffect(() => () => {
    presentEpochRef.current += 1;
    filmstripEpochRef.current += 1;
    (filmstripUrlsRef.current || [])
      .forEach((u) => { if (u) URL.revokeObjectURL(u); });
    filmstripUrlsRef.current = null;
    Object.values(presentUrlsLiveRef.current || {})
      .forEach((u) => { if (typeof u === 'string') URL.revokeObjectURL(u); });
    presentUrlsLiveRef.current = {};
  }, []);

  // Consult #5 fix: paging until a nav button disables (or any re-render
  // that unfocuses the overlay's controls) must not drop focus to the
  // body — re-anchor it on the overlay container.
  useEffect(() => {
    if (!presentOpen || !presentRef.current) return;
    if (!presentRef.current.contains(document.activeElement)) {
      presentRef.current.focus();
    }
  }, [presentOpen, presentIdx]);

  // Lane 1229 / S5: while Present is open, lazily fetch the FULL image for
  // the current page and prefetch the next — authenticated, blob -> object
  // URL, one attempt per page (a failure shows a labeled placeholder,
  // never a retry loop).
  useEffect(() => {
    if (!workspaceShellV2Enabled || !presentOpen || !snapshotGrant
        || snapshotGrant.status !== 'ready' || !snapshotGrant.descriptor) {
      return;
    }
    const base = `${API_BASE}/api/decks/compositions/`
      + `${snapshotGrant.compositionId}/snapshots/${snapshotGrant.descriptor}`;
    [presentIdx, presentIdx + 1].forEach((pageIdx) => {
      if (pageIdx < 0 || pageIdx >= (filmstripUrlsRef.current || []).length) return;
      if (presentFetchedRef.current.has(pageIdx)) return;
      presentFetchedRef.current.add(pageIdx);
      const epoch = presentEpochRef.current;
      setPresentUrls((m) => {
        if (presentEpochRef.current !== epoch) return m;
        const next = { ...m, [pageIdx]: null };
        presentUrlsLiveRef.current = next;
        return next;
      });
      (async () => {
        let resolved = false;
        try {
          const r = await fetch(`${base}/pages/${pageIdx + 1}`,
            { headers: authHeaders() });
          resolved = r.ok ? URL.createObjectURL(await r.blob()) : false;
        } catch (e) { resolved = false; }
        if (presentEpochRef.current !== epoch) {
          // Present closed while this page was in flight: never write the
          // stale entry, and never leak its object URL.
          if (typeof resolved === 'string') URL.revokeObjectURL(resolved);
          return;
        }
        setPresentUrls((m) => {
          // Closing can happen after the await-side check but before React
          // executes this updater. Re-check inside the state transaction so
          // a closed overlay cannot be repopulated by a late URL.
          if (presentEpochRef.current !== epoch) {
            if (typeof resolved === 'string') URL.revokeObjectURL(resolved);
            return m;
          }
          const next = { ...m, [pageIdx]: resolved };
          presentUrlsLiveRef.current = next;
          return next;
        });
      })();
    });
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [workspaceShellV2Enabled, presentOpen, presentIdx, snapshotGrant]);

  // Lane 1229 / S5 (§5): on open, focus the overlay (the trap's anchor) and
  // ATTEMPT fullscreen — a rejected/absent Fullscreen API leaves the same
  // in-app modal, which is the sanctioned fallback.
  useEffect(() => {
    if (!presentOpen || !presentRef.current) return;
    const el = presentRef.current;
    el.focus();
    if (typeof el.requestFullscreen === 'function') {
      try {
        const req = el.requestFullscreen();
        if (req && req.catch) req.catch(() => {});
      } catch (e) { /* fallback modal stands */ }
    }
  }, [presentOpen]);

  // Lane 1231 / F6: when the BROWSER drives the fullscreen exit (native
  // Escape, OS gesture), Present must close in the same gesture — one
  // idempotent close path (closePresent) handles overlay, URL cleanup,
  // and focus restoration. The fallback modal (fullscreen never granted)
  // sees no fullscreenchange and is untouched.
  useEffect(() => {
    if (!presentOpen) { presentEnteredFsRef.current = false; return undefined; }
    const onFsChange = () => {
      if (document.fullscreenElement) {
        presentEnteredFsRef.current = true;
        return;
      }
      if (presentEnteredFsRef.current) {
        presentEnteredFsRef.current = false;
        closePresentRef.current();
      }
    };
    document.addEventListener('fullscreenchange', onFsChange);
    return () => document.removeEventListener('fullscreenchange', onFsChange);
  }, [presentOpen]);

  // Deliver a completed export. Phase 2 (plan §7.6): the download delivers
  // BYTES ONLY. It no longer replaces `exportDoc` and no longer marks the
  // preview current -- the preview keeps the last EDITABLE (marked) document,
  // so downloading never disables inline editing. The omitted/audience
  // disclosures still surface (read on both intents, Lane 905).
  const finishDownload = (ex, id) => {
    setOmitted(ex.omitted || []);
    setAudienceFiltered(ex.audienceFiltered || { keys: [], notice: '' });
    triggerHtmlDownload(ex.html, `deck-${id || 'export'}.html`);
  };

  // THE SECOND ARTIFACT. The internal prep pack -- what to say on each page, what
  // they will come back with, and why an argument was ruled out. It is NEVER
  // folded into the supplier-facing deck export, and it is a different file with
  // a different name; the toolbar's menu is where that distinction is stated.
  //
  // Held while the server is still writing it (Lane 768): a page fills itself in
  // as the pass lands, a downloaded file does not.
  const performDownloadPack = async (_, valid = () => true) => {
    if (!editingId) return;
    setError(null); setOutOpen(false); setPackDownloading(true);
    try {
      // Phase 2 (plan §7.6): the pack is composed from the SAVED version, so
      // it saves first exactly like the deck download; a failed save aborts.
      const id = await performSave({ valid });
      if (!id || !valid()) return;
      await downloadPackHtml({ compositionId: id, apiBase: API_BASE, authHeaders, isCurrent: valid, native: Boolean(presentationTemplates) });
    } catch (e) { if (valid()) setError(e.message); } finally { if (valid()) setPackDownloading(false); }
  };

  // Download ALWAYS routes through the gated export call (intent='download') -- it
  // NEVER reuses the (ungated) preview cache, so an internal-only slide can't slip
  // into a downloaded file without confirmation (Lane 72 §18 guardrail). A 409
  // opens the confirm modal; the user's explicit "Download anyway" re-requests
  // WITH acknowledgement and Option C exports the full deck (never strips).
  const performDownload = async (_, valid = () => true) => {
    setError(null);
    try {
      // Phase 2 (plan §7.6): a download ALWAYS saves first -- even when the
      // deck was saved before -- so the file can never omit dirty edits; a
      // failed save exports nothing and the dirty state survives.
      const id = await performSave({ valid });
      if (!id || !valid()) return;
      const ex = await exportHtml(id, { intent: 'download' });
      if (!valid()) return;
      if (ex.needsConfirmation) {
        setPendingInternalConfirm({ id, slides: ex.internalSlides || [] });
        return;   // wait for the user's explicit confirmation
      }
      finishDownload(ex, id);
    } catch (e) { if (valid()) setError(e.message); }
  };

  // The confirm modal's "Download anyway": re-request WITH acknowledgement and
  // deliver the full deck (internal slides included -- Option C never strips).
  const performInternalDownload = async (_, valid = () => true) => {
    const pending = pendingInternalConfirm;
    setPendingInternalConfirm(null);
    if (!pending) return;
    setError(null);
    try {
      const ex = await exportHtml(pending.id, { intent: 'download', acknowledgeInternal: true });
      if (!valid()) return;
      if (ex.needsConfirmation) return;   // defensive: should not recur once acknowledged
      finishDownload(ex, pending.id);
    } catch (e) { if (valid()) setError(e.message); }
  };

  writerWork.current.download = performDownload;
  writerWork.current.pack = performDownloadPack;
  writerWork.current.confirm = performInternalDownload;
  const download = () => workspaceShellV2Enabled
    ? queueWriter('download') : performDownload();
  const downloadPack = () => workspaceShellV2Enabled
    ? queueWriter('pack') : performDownloadPack();
  const proceedInternalDownload = () => workspaceShellV2Enabled
    ? queueWriter('confirm') : performInternalDownload();

  // --- load a saved deck into the editor ---------------------------------
  const loadDeck = async (compositionId) => {
    if (workspaceShellV2Enabled) { cancelReorder(); setWorkspaceLoading(true); }
    setError(null); setStatus(null);
    try {
      const r = await fetch(`${API_BASE}/api/decks/compositions/${compositionId}`, { headers: authHeaders() });
      if (!r.ok) throw new Error(`Load failed (${r.status})`);
      const d = await r.json();
      setViewOnly(d.viewer_can_edit === false ? { author: d.author_name || 'the person who built it' } : null);
      setCanDelete(d.viewer_can_delete !== false);
      setPresentationTemplates(d.presentation_templates || null);
      nativeRevision.current = d.intake?.kind === 'template_library_presentation'
        ? {compositionId:d.composition_id,revision:d.intake.edit_revision,pending:null} : null;
      // Reset before EVERY identity branch, including the part-list pack
      // early return. Otherwise a deck preview can survive under the pack.
      resetSnapshotPresentation();
      // Lane 1231 / F1: the deck identity is changing — fence and clear ALL
      // snapshot presentation state BEFORE the new page list renders, so
      // the old deck's thumbnails can never appear under new page names
      // and its in-flight resolution can never publish late.
      setEditingId(d.composition_id);
      setSubject({ supplier_id: d.supplier_id, supplier_name: d.supplier_name });
      setTitle(d.title || '');
      setMeetingType(d.meeting_type || 'QBR');
      // slide_order comes back as the canonical per-instance shape (the server
      // normalizes legacy decks); defensively ensure each item has an overrides obj.
      const order = Array.isArray(d.slide_order)
        ? d.slide_order.map((it) => {
          const inst = withEditorInstanceIdentity({
            template_key: it.template_key,
            overrides: (it.overrides && typeof it.overrides === 'object') ? it.overrides : {},
          });
          // Phase 3: stable rows survive the load seam (legacy: key absent)
          if (it.row_blocks && typeof it.row_blocks === 'object'
            && Object.keys(it.row_blocks).length) {
            inst.row_blocks = it.row_blocks;
          }
          if (it.element_patches && typeof it.element_patches === 'object'
            && !Array.isArray(it.element_patches)) {
            inst.element_patches = it.element_patches;
          }
          if (Array.isArray(it.added_elements)) {
            inst.added_elements = it.added_elements;
          }
          return inst;
        })
        : [];
      setSelected(order);
      setActiveSlideIdx(0);
      setIntake(d.intake || null);  // Lane 168: the persisted meeting-plan story
      if (workspaceShellV2Enabled) cancelReorder();
      setExportDoc(''); setOmitted([]); setPreviewMode('slide');
      setExportedStructureSig(null); setPreviewRevision(null); setFitErrors({});
      // Consult #6 (Ryan-approved 2026-08-31): fit truth is SESSION-LOCAL, and
      // building the measuring preview legitimately persists a draft — so a
      // LOADED deck's edited pages start NOT-yet-measured. Save and both
      // downloads hold until one preview build measures each edited page (the
      // existing flag copy explains the state); pages without buyer edits
      // carry no clip risk and start clear.
      const loadedPending = {};
      order.forEach((inst, i) => {
        if (Object.keys(inst.overrides || {}).length
          || (inst.row_blocks && Object.keys(inst.row_blocks).length)
          || (elementEditorEnabled
            && ((inst.element_patches && Object.keys(inst.element_patches).length)
              || (inst.added_elements && inst.added_elements.length)))) {
          loadedPending[i] = true;
        }
      });
      setFitChecksPending(loadedPending);
      undoStack.current = [];
      elementHistory.current = {};
      editorProtocolRef.current = 0;
      setEditorProtocolVersion(0);
      setSelectedElement(null);
      setExportedAt(null);
      setAudienceFiltered({ keys: [], notice: '' });
      // Lane 789: a deck that was just loaded IS saved. Computed from the values
      // we just set (the render has not run yet), through the same one formula
      // the staleness memo uses. `briefRevision` is untouched by a load, so the
      // current value is the right one to fold in.
      setSavedSignature(signatureOf({
        supplierId: d.supplier_id,
        title: d.title || '',
        meetingType: d.meeting_type || 'QBR',
        order,
        briefRevision,
      }));
      setStatus(`Loaded "${d.title || 'Untitled'}".`);
      return true;
    } catch (e) { setError(e.message); return false; }
    finally { if (workspaceShellV2Enabled) setWorkspaceLoading(false); }
  };

  const initialDeckLoaded = useRef(null);
  useEffect(() => {
    if (initialCompositionId && /^\d+$/.test(String(initialCompositionId)) && initialDeckLoaded.current !== initialCompositionId) {
      initialDeckLoaded.current = initialCompositionId;
      loadDeck(Number(initialCompositionId)).finally(() => setOpeningDeck(false));
    }
    // The initial URL is a one-time handoff; ordinary deck changes stay in this editor.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [initialCompositionId]);


  // --- derived: active slide (always in range -- consult condition) ------
  const slideCount = selected.length;
  const safeActiveIdx = slideCount === 0 ? -1 : Math.min(activeSlideIdx, slideCount - 1);

  // Lane 1229 / S3 (§3): the active tile scrolls into view. jsdom has no
  // scrollIntoView, hence the guard; V1 has no railRef rows to match.
  useEffect(() => {
    if (!workspaceShellV2Enabled || safeActiveIdx < 0) return;
    const root = narrowWorkspace ? stripRef.current : railRef.current;
    if (!root) return;
    const attr = narrowWorkspace ? 'data-strip-idx' : 'data-row-idx';
    const el = root.querySelector(`[${attr}="${safeActiveIdx}"]`);
    if (el && typeof el.scrollIntoView === 'function') {
      el.scrollIntoView({ block: 'nearest' });
    }
  }, [narrowWorkspace, workspaceShellV2Enabled, safeActiveIdx]);
  const activeInstance = safeActiveIdx >= 0 ? selected[safeActiveIdx] : null;
  const activeKey = activeInstance ? activeInstance.template_key : null;
  const activeFieldsAll = activeKey ? editableFieldsFor(activeKey) : [];
  // Consult #5 fix: on a MATERIALIZED block the panel offers managed-token
  // fields only for rows that exist — a field for row 3 of a 2-row block has
  // nowhere to land (the writer drops it; offering it would be a dead input).
  const activeFields = useMemo(() => {
    const inst = activeInstance;
    if (!inst) return activeFieldsAll;
    const contract = rowContractFor(inst.template_key);
    const rb = contract && inst.row_blocks && inst.row_blocks[contract.block_key];
    if (!Array.isArray(rb)) return activeFieldsAll;
    return activeFieldsAll.filter((f) => {
      const parsed = parseManagedToken(contract, f.token);
      return !parsed || (parsed.rowNum >= 1 && parsed.rowNum <= rb.length);
    });
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeInstance, templates]);
  const activeKeyCopies = activeKey ? selected.filter((s) => s.template_key === activeKey).length : 0;

  // ⭐ PLAN B PHASE 2 — the parent's half of the §4.2 verification chain.
  // DeckPreviewPane already proved source-window + schema; here the AUTHORITY
  // checks run: current structure revision, exact existing instance, token on
  // that instance's editable contract, bounded single-line value. Rejections
  // are silent (malformed) or explained (stale revision -> refresh action).
  const handleInlineEdit = useCallback(({ instanceIndex, token, value, structureRevision }) => {
    if (!previewRevision || structureRevision !== previewRevision || previewStale) {
      setStatus('The preview is out of date. Refresh it to keep editing.');
      return;
    }
    const inst = selected[instanceIndex];
    if (!inst) return;
    const fields = editableFieldsFor(inst.template_key);
    if (!fields.some((f) => f.token === token)) return;
    if (typeof value !== 'string' || value.length > 2000 || /[\r\n]/.test(value)) return;
    setFitChecksPending((m) => ({ ...m, [instanceIndex]: true }));
    applyOverrideTxn(instanceIndex, token, value);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [previewRevision, previewStale, selected, templates, applyOverrideTxn]);

  const handleUndoRequest = useCallback(({ structureRevision }) => {
    if (!previewRevision || structureRevision !== previewRevision || previewStale) return;
    undo();
  }, [previewRevision, previewStale, undo]);

  const handleFitStatus = useCallback(({
    fits, instanceIndex, structureRevision, fitRevision,
  }) => {
    if (!previewRevision || structureRevision !== previewRevision || previewStale) return;
    const inst = selected[instanceIndex];
    const version = editorProtocolRef.current === 2 ? 2 : 1;
    if (!inst || fitRevision !== fitRevisionOf(inst, version)) return;
    setFitErrors((m) => {
      const next = { ...m };
      if (fits === false) next[instanceIndex] = true;
      else delete next[instanceIndex];
      return next;
    });
    setFitChecksPending((m) => {
      if (!m[instanceIndex]) return m;
      const next = { ...m };
      delete next[instanceIndex];
      return next;
    });
  }, [previewRevision, previewStale, selected]);
  const fitError = Object.keys(fitErrors).length > 0;
  const fitPending = Object.keys(fitChecksPending).length > 0
    || (workspaceShellV2Enabled && reorderPending);
  const fitBlocked = fitError || fitPending;
  const activeFitPending = fitChecksPending[safeActiveIdx] === true;

  // One row-operation applier for BOTH surfaces (frame overlay and page
  // panel): first use materializes the block from the effective values and
  // removes the managed positional overrides in the same transaction (plan
  // §8.1/§8.2); the op itself is one undo entry; the server re-validates the
  // whole shape at save (deny by default).
  const applyRowOp = useCallback((idx, op, rowIndex) => {
    if (busy || rowOpPending || viewOnly) return;   // Lane 1412: view-only deck
    const inst = selected[idx];
    const eff = effectiveRowsFor(inst);
    if (!eff) return;
    const { contract, rows } = eff;
    let nextRows;
    if (op === 'add') {
      if (rows.length >= contract.max_rows) return;
      const used = new Set(rows.map((r) => r.row_id));
      let k = 1;
      while (used.has(`r${k}`)) k += 1;
      nextRows = [...rows, { row_id: `r${k}`, values: { ...contract.defaults } }];
    } else {
      if (rows.length <= contract.min_rows) return;
      if (!Number.isInteger(rowIndex) || rowIndex < 1 || rowIndex > rows.length) return;
      nextRows = rows.filter((_, i) => i !== rowIndex - 1);
    }
    setFitChecksPending((m) => ({ ...m, [idx]: true }));
    setSelected((s) => {
      const cur = s[idx];
      if (!cur) return s;
      // strip every managed positional token from overrides — row_blocks is
      // the single source for this block from here on (dual-source is a 422)
      const ov = { ...(cur.overrides || {}) };
      Object.values(contract.field_tokens).forEach((pattern) => {
        for (let n = 1; n <= contract.max_rows; n += 1) {
          delete ov[tokenFromPattern(pattern, n)];
        }
      });
      pushUndo({ kind: 'rows', idx,
        prevOverrides: cur.overrides || {},
        prevRowBlocks: cur.row_blocks || null });
      const out = [...s];
      out[idx] = { ...cur, overrides: ov,
        row_blocks: { ...(cur.row_blocks || {}),
          [contract.block_key]: nextRows } };
      return out;
    });
    setRebuildRequest((n) => n + 1);
    setSelectedElement(null);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [busy, rowOpPending, viewOnly, selected, effectiveRowsFor]);

  const handleRowMessage = useCallback((op) => ({ blockKey, rowIndex,
    instanceIndex, structureRevision }) => {
    if (!previewRevision || structureRevision !== previewRevision || previewStale) {
      setStatus('The preview is out of date. Refresh it to keep editing.');
      return;
    }
    const inst = selected[instanceIndex];
    if (!inst) return;
    const contract = rowContractFor(inst.template_key);
    if (!contract || contract.block_key !== blockKey) return;
    applyRowOp(instanceIndex, op, rowIndex);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [previewRevision, previewStale, selected, rowContractFor, applyRowOp]);

  const handleElementRowMessage = useCallback((op) => ({
    blockKey, blockOrdinal, rowId, instanceIndex, fitRevision, rowBase,
    structureRevision,
  }) => {
    if (editorProtocolRef.current !== 2 || !previewRevision
      || structureRevision !== previewRevision || previewStale) return;
    const current = selected[instanceIndex];
    if (!current || fitRevision !== fitRevisionOf(current, 2)) return;
    const contract = rowContractFor(current.template_key);
    if (!contract || contract.block_key !== blockKey) return;
    const existing = current.row_blocks && current.row_blocks[contract.block_key];
    const materialized = Array.isArray(existing)
      ? { contract, rows: existing }
      : validServerRowBase(current, rowBase, blockOrdinal);
    if (!materialized) return;
    let nextRows = materialized.rows;
    if (op === 'add') {
      if (nextRows.length >= contract.max_rows) return;
      const used = new Set(nextRows.map((row) => row.row_id));
      let k = 1;
      while (used.has(`r${k}`)) k += 1;
      nextRows = [...nextRows, { row_id: `r${k}`, values: { ...contract.defaults } }];
    } else {
      if (nextRows.length <= contract.min_rows
        || !nextRows.some((row) => row.row_id === rowId)) return;
      nextRows = nextRows.filter((row) => row.row_id !== rowId);
    }
    setFitChecksPending((flags) => ({ ...flags, [instanceIndex]: true }));
    setSelected((state) => {
      const inst = state.find((item) => item._editor_instance_id === current._editor_instance_id);
      if (!inst) return state;
      const before = elementStateOf(inst);
      const ov = { ...(inst.overrides || {}) };
      Object.values(contract.field_tokens).forEach((pattern) => {
        for (let n = 1; n <= contract.max_rows; n += 1) {
          delete ov[tokenFromPattern(pattern, n)];
        }
      });
      const patches = { ...(inst.element_patches || {}) };
      if (op === 'delete') {
        const rowPrefix = new RegExp(`(^|\\.)r${blockOrdinal}\\.${rowId.replace(/[-/\\^$*+?.()|[\]{}]/g, '\\$&')}\\.`);
        Object.keys(patches).forEach((elementId) => {
          if (rowPrefix.test(elementId)) delete patches[elementId];
        });
      }
      const next = { ...inst, overrides: ov, element_patches: patches,
        row_blocks: { ...(inst.row_blocks || {}), [contract.block_key]: nextRows } };
      const after = elementStateOf(next);
      pushElementHistory(inst._editor_instance_id, { before, after, rebuild: true });
      return state.map((item) => (item._editor_instance_id === inst._editor_instance_id
        ? next : item));
    });
    setSelectedElement(null);
    setRebuildRequest((n) => n + 1);
  }, [previewRevision, previewStale, selected, rowContractFor,
    validServerRowBase]);

  const handleProtocolVersion = useCallback((version) => {
    editorProtocolRef.current = version;
    setEditorProtocolVersion(version);
    if (version !== 2) setSelectedElement(null);
    // W5: frame ready is a flush point — a fresh frame must not wait out a
    // typing timer for its first true state.
    flushHydrateRef.current();
  }, []);

  // Inline editing is live only when: capable device, This-page mode, and the
  // shown preview still matches the deck's structure (plan §4.1/§7.5).
  const canvasMode = workspaceShellV2Enabled ? 'deck' : previewMode;
  const inlineEditing = inlineCapable
    && (workspaceShellV2Enabled || previewMode === 'slide') && !previewStale && !viewOnly;

  // The frame speaks positional tokens; a materialized block's values are
  // projected into that vocabulary for hydration (values live in row_blocks).
  const activeInstanceForHydrate = safeActiveIdx >= 0 ? selected[safeActiveIdx] : null;
  // Lane 1233 / W5: the view the PANE hydrates from. Frozen (identity-
  // stable) during a v2 panel typing burst; always live in V1 and for the
  // panel's own inputs (which read activeInstanceForHydrate/selected
  // directly — the buyer's keystrokes echo instantly).
  const hydrateInstance = (workspaceShellV2Enabled && hydrateFreezeRef.current)
    ? hydrateFreezeRef.current : activeInstanceForHydrate;
  // W5 flush points: page, dock, and preview-mode transitions must carry
  // the latest text into the switch (plan v1.1 §8.2).
  useEffect(() => {
    if (workspaceShellV2Enabled) flushHydrate();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [safeActiveIdx, dockMode, previewMode]);

  // Lane 1233 / W6: the ONE v2 page-switch entry — flush the W5 typing
  // timer in the same tick, then move selection; the frame receives one
  // hydrate carrying the latest text AND the new active page.
  const selectPage = (idx) => {
    if (workspaceShellV2Enabled) flushHydrate();
    setActiveSlideIdx(idx);
  };

  // Called at the START of a v2 panel text keystroke: freeze the CURRENT
  // (pre-keystroke) instance once per burst and restart the one trailing
  // timer. V1 never defers.
  const deferTextHydrate = () => {
    if (!workspaceShellV2Enabled) return;
    if (hydrateFreezeRef.current === null && activeInstanceForHydrate) {
      hydrateFreezeRef.current = activeInstanceForHydrate;
    }
    if (hydrateTimerRef.current) clearTimeout(hydrateTimerRef.current);
    hydrateTimerRef.current = setTimeout(() => {
      hydrateTimerRef.current = null;
      hydrateFreezeRef.current = null;
      setHydrateEpoch((n) => n + 1);
    }, 150);
  };
  const activeEditorIdentity = activeInstanceForHydrate
    ? activeInstanceForHydrate._editor_instance_id : null;
  const activeSelectedElement = elementEditorEnabled && selectedElement
    && selectedElement.identity === activeEditorIdentity
    && selectedElement.instanceIndex === safeActiveIdx ? selectedElement : null;
  const activeSelectedSource = activeSelectedElement && activeSelectedElement.kind === 'source'
    ? activeSelectedElement : null;
  const activeSelectedAdded = activeSelectedElement && activeSelectedElement.kind === 'added'
    && activeInstanceForHydrate
    ? (activeInstanceForHydrate.added_elements || []).find(
      (box) => box.id === activeSelectedElement.addedId) || null : null;
  const activeSelectedPatch = activeSelectedSource && activeInstanceForHydrate
    && activeInstanceForHydrate.element_patches
    && activeInstanceForHydrate.element_patches[activeSelectedSource.elementId]
    && activeInstanceForHydrate.element_patches[activeSelectedSource.elementId].d
      === activeSelectedSource.digest
    ? activeInstanceForHydrate.element_patches[activeSelectedSource.elementId] : null;
  const activeSelectedContent = activeSelectedAdded ? activeSelectedAdded.t : activeSelectedSource
    ? (activeSelectedPatch && typeof activeSelectedPatch.t === 'string'
      ? activeSelectedPatch.t : activeSelectedSource.base)
    : '';
  const activeSelectedStyle = activeSelectedAdded
    ? (activeSelectedAdded.s || EMPTY_OVERRIDES)
    : (activeSelectedPatch && activeSelectedPatch.s
      ? activeSelectedPatch.s : EMPTY_OVERRIDES);
  const applyElementStyleChange = useCallback((key, value) => {
    if (!activeSelectedElement || !STYLE_KEYS.has(key)) return;
    const style = { ...activeSelectedStyle };
    if (value === null) delete style[key];
    else style[key] = value;
    if (activeSelectedAdded) {
      applyAddedElementTxn(safeActiveIdx, activeSelectedAdded.id, (elements) =>
        elements.map((box) => (box.id === activeSelectedAdded.id
          ? { ...box, s: style } : box)));
    } else if (activeSelectedSource) {
      applyElementPatchTxn({ ...activeSelectedSource, change: { s: style } });
    }
  }, [activeSelectedElement, activeSelectedAdded, activeSelectedSource,
    activeSelectedStyle, safeActiveIdx, applyAddedElementTxn, applyElementPatchTxn]);
  const handleElementStyleReset = useCallback(() => {
    if (!activeSelectedElement) return;
    if (activeSelectedAdded) {
      applyAddedElementTxn(safeActiveIdx, activeSelectedAdded.id, (elements) =>
        elements.map((box) => (box.id === activeSelectedAdded.id
          ? { ...box, s: {} } : box)));
    } else if (activeSelectedSource) {
      applyElementPatchTxn({ ...activeSelectedSource, change: { s: {} } });
    }
  }, [activeSelectedElement, activeSelectedAdded, activeSelectedSource,
    safeActiveIdx, applyAddedElementTxn, applyElementPatchTxn]);
  const applyActiveAddedChange = useCallback((change, options) => {
    if (!activeSelectedAdded) return;
    applyAddedElementTxn(safeActiveIdx, activeSelectedAdded.id, (elements) =>
      elements.map((box) => (box.id === activeSelectedAdded.id
        ? { ...box, ...change } : box)), options);
  }, [activeSelectedAdded, safeActiveIdx, applyAddedElementTxn]);
  const deleteActiveAdded = useCallback(() => {
    if (!activeSelectedAdded) return;
    applyAddedElementTxn(safeActiveIdx, activeSelectedAdded.id, (elements) =>
      elements.filter((box) => box.id !== activeSelectedAdded.id));
  }, [activeSelectedAdded, safeActiveIdx, applyAddedElementTxn]);
  const selectionBlocked = busy || rowOpPending || previewStale || reorderPending
    || setupOpen || argumentOpen || pagesDrawerOpen || presentOpen || outOpen || libraryOpen;
  const rowMatch = activeSelectedSource && /(?:^|\.)r([1-9][0-9]{0,2})\.([A-Za-z0-9_-]{1,32})(?:\.|$)/.exec(activeSelectedSource.elementId);
  const rowBase = rowMatch && validServerRowBase(activeInstanceForHydrate,
    activeSelectedSource.rowBase, Number(rowMatch[1]));
  const selectedRow = rowBase && { blockKey: rowBase.contract.block_key,
    blockOrdinal: Number(rowMatch[1]), rowId: rowMatch[2], rowBase: activeSelectedSource.rowBase };
  const selectedRows = rowBase && (activeInstanceForHydrate.row_blocks?.[rowBase.contract.block_key] || rowBase.rows);
  const canDeleteRow = Boolean(selectedRow && selectedRows.length > rowBase.contract.min_rows
    && selectedRows.some(row => row.row_id === selectedRow.rowId));
  const canDeleteTemplate = Boolean(activeSelectedSource?.removable && !rowMatch && !activeSelectedPatch?.r);
  const deletionBlocked = selectionBlocked || activeSelectedSource?.editing === true;
  removalBlockedRef.current = deletionBlocked;
  const restoreDeletedRows = () => {
    if (!workspaceShellV2Enabled || selectionBlocked || !activeInstanceForHydrate) return;
    flushHydrateRef.current();
    setFitChecksPending(flags => ({ ...flags, [safeActiveIdx]: true }));
    setSelected(state => state.map(inst => {
      if (inst._editor_instance_id !== activeEditorIdentity) return inst;
      const patches = Object.fromEntries(Object.entries(inst.element_patches || {}).flatMap(([id, patch]) => {
        const restored = { ...patch }; delete restored.r;
        return Object.keys(restored).some(k => k !== 'd') ? [[id, restored]] : [];
      }));
      const next = { ...inst, element_patches: patches };
      pushElementHistory(inst._editor_instance_id, {before: elementStateOf(inst), after: elementStateOf(next), rebuild: false});
      return next;
    }));
  };
  const deleteSelection = () => {
    if (!workspaceShellV2Enabled || deletionBlocked || !activeSelectedElement) return;
    positionPadRef.current?.stop(); flushHydrateRef.current();
    if (activeSelectedAdded) deleteActiveAdded();
    else if (canDeleteTemplate) applyElementPatchTxn({ ...activeSelectedSource, change: { r: true } });
    else if (canDeleteRow) handleElementRowMessage('delete')({ ...selectedRow,
      instanceIndex: safeActiveIdx, structureRevision: previewRevision,
      fitRevision: fitRevisionOf(activeInstanceForHydrate, 2) });
  };
  const deleteAddedFromFrame = ({ instanceIndex, addedId, fitRevision, structureRevision }) => {
    if (!workspaceShellV2Enabled || selectionBlocked
      || !validElementFrameTxn(instanceIndex, fitRevision, structureRevision)
      || !activeSelectedAdded || activeSelectedElement.instanceIndex !== instanceIndex
      || activeSelectedAdded.id !== addedId) return;
    deleteSelection();
  };
  const positionKey = activeSelectedElement
    ? `${activeSelectedElement.identity}:${activeSelectedElement.elementId || activeSelectedElement.addedId}` : '';
  const stepPosition = (dx, dy) => {
    const group = positionHistoryRef.current;
    const current = selectedRef.current[safeActiveIdx];
    if (!workspaceShellV2Enabled || selectionBlocked || !group || group.target !== positionKey
      || !current || current._editor_instance_id !== group.identity) return;
    if (activeSelectedAdded) {
      const box = current.added_elements?.find(item => item.id === activeSelectedAdded.id);
      if (box) applyActiveAddedChange({ x: Math.max(0, Math.min(1920 - box.w, box.x + dx)),
        y: Math.max(0, box.y + dy) }, { historyGroup: group });
    } else if (activeSelectedSource?.nudgeable) {
      const geometry = current.element_patches?.[activeSelectedSource.elementId]?.g || { dx: 0, dy: 0 };
      applyElementPatchTxn({ ...activeSelectedSource,
        change: { g: { dx: geometry.dx + dx, dy: geometry.dy + dy } } }, { historyGroup: group });
    }
  };
  const resetPosition = () => {
    if (selectionBlocked) return;
    if (activeSelectedAdded) applyActiveAddedChange({ x: 0, y: 0 });
    else if (activeSelectedSource?.nudgeable) applyElementPatchTxn({ ...activeSelectedSource, change: { g: { dx: 0, dy: 0 } } });
  };
  const applyActiveGeometry = useCallback((axis, raw) => {
    if (!activeSelectedSource || !activeSelectedSource.nudgeable) return;
    const value = Number(raw);
    if (!Number.isFinite(value)) return;
    const current = activeSelectedPatch && activeSelectedPatch.g
      ? activeSelectedPatch.g : { dx: 0, dy: 0 };
    applyElementPatchTxn({ ...activeSelectedSource,
      change: { g: { ...current, [axis]: value } } });
  }, [activeSelectedSource, activeSelectedPatch, applyElementPatchTxn]);
  useEffect(() => {
    setSelectedElement((current) => (current && !previewStale
      && current.identity === activeEditorIdentity ? current : null));
  }, [activeEditorIdentity, previewRevision, previewStale]);
  // Lane 1233 / W5: every hydrate-bound derivation reads hydrateInstance
  // (frozen during a typing burst), so the pane's inputs keep identity and
  // no per-keystroke hydrate fires. The panel and fit-truth read live state.
  const activeFitRevision = fitRevisionOf(
    hydrateInstance, editorProtocolVersion === 2 ? 2 : 1
  );
  const activeElementFitRevision = fitRevisionOf(hydrateInstance, 2);
  const hydrateElementPatches = hydrateInstance
    ? (hydrateInstance.element_patches || EMPTY_OVERRIDES)
    : EMPTY_OVERRIDES;
  // W5 (Consult #2 fix): the identity-stable empty is V2-ONLY — in V1 the
  // historical fresh-[] per render re-fired a hydrate on parent re-renders,
  // and applyHydrate cancels an in-frame edit, so stabilizing it would be a
  // flag-OFF behavior change (arguably an improvement, but not this lane's
  // to make silently). V1 keeps its exact cadence.
  const emptyAdded = workspaceShellV2Enabled ? EMPTY_ADDED : [];
  const hydrateAddedElements = hydrateInstance
    ? (hydrateInstance.added_elements || emptyAdded) : emptyAdded;
  // Bug fix (a LOADED deck's downloads sit greyed forever): the frame measures
  // only the ACTIVE page, so a loaded deck's OTHER edited pages stay fitPending
  // -- greying both downloads and Save -- until the buyer opens each one. This
  // asks the frame to ALSO measure every still-pending, NON-active page on the
  // next hydrate, each tagged with its own content revision so the parent's
  // per-page fit guard (handleFitStatus) accepts the echoed report.
  //   Safety: a NON-active page is pending ONLY from a load -- editing a page
  // makes it active first, and a clipping edit moves it into fitErrors, not
  // fitChecksPending -- so its preview DOM equals its saved/current state and the
  // measurement is honest. A page that genuinely overflows still reports
  // fits:false and keeps the gate shut; this only un-sticks never-measured
  // pages, it never relaxes the clip gate (feature #19).
  // Both protocols now sweep loaded non-active pages. v2 includes geometry and
  // added boxes in its revision, so a result can never clear newer E2b state.
  const pendingMeasureKeyRef = useRef(null);
  const pendingMeasureStableRef = useRef([]);
  const pendingMeasure = useMemo(() => {
    const out = [];
    // Every visible S7 page needs its own canonical transaction revision before
    // the first click. Use the existing measure surface, never a new wire field.
    const indexes = workspaceShellV2Enabled && elementEditorEnabled
      ? selected.map((_, i) => i) : Object.keys(fitChecksPending);
    indexes.forEach((raw) => {
      const i = Number(raw);
      if (!Number.isInteger(i) || i < 0 || i === safeActiveIdx) return;
      const inst = selected[i];
      if (!inst || frameIndex(i) < 0) return;
      out.push({ instanceIndex: frameIndex(i),
        fitRevision: fitRevisionOf(inst, (workspaceShellV2Enabled && elementEditorEnabled)
          || editorProtocolVersion === 2 ? 2 : 1) });
    });
    if (!workspaceShellV2Enabled) return out;
    // Lane 1233 / W5 (v2 only): identity-stable output — a keystroke bumps
    // fitChecksPending for the ACTIVE page (excluded above), so the list's
    // CONTENT is unchanged and its identity must be too, or the pane
    // re-hydrates per letter anyway. V1 cadence deliberately untouched.
    const key = JSON.stringify(out);
    if (pendingMeasureKeyRef.current !== key) {
      pendingMeasureKeyRef.current = key;
      pendingMeasureStableRef.current = out;
    }
    return pendingMeasureStableRef.current;
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [fitChecksPending, safeActiveIdx, selected, previewOrder, editorProtocolVersion,
    workspaceShellV2Enabled, elementEditorEnabled]);
  const hydrateOverrides = useMemo(() => {
    const inst = hydrateInstance;
    if (!inst) return EMPTY_OVERRIDES;
    const contract = rowContractFor(inst.template_key);
    const rb = contract && inst.row_blocks && inst.row_blocks[contract.block_key];
    if (!Array.isArray(rb)) return inst.overrides || EMPTY_OVERRIDES;
    const out = { ...(inst.overrides || {}) };
    rb.forEach((row, i) => {
      contract.fields.forEach((field) => {
        out[tokenFromPattern(contract.field_tokens[field], i + 1)] = (row.values && row.values[field]) || '';
      });
    });
    return out;
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [hydrateInstance, templates]);

  // Row affordances for the frame overlay (null when no contract / no rows).
  const activeRowMeta = useMemo(() => {
    const eff = effectiveRowsFor(hydrateInstance);
    if (!eff) return null;
    const rowEditing = elementEditorEnabled
      ? (workspaceShellV2Enabled || previewMode === 'slide') && !previewStale : inlineEditing;
    return {
      blockKey: eff.contract.block_key,
      canAdd: rowEditing && !busy && !rowOpPending
        && eff.rows.length < eff.contract.max_rows,
      canDelete: rowEditing && !busy && !rowOpPending
        && eff.rows.length > eff.contract.min_rows,
    };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [hydrateInstance, elementEditorEnabled, previewMode, previewStale,
    inlineEditing, busy, rowOpPending, templates, workspaceShellV2Enabled]);
  // The words section auto-collapses ONLY when the active page actually
  // offers inline-editable fields (plan §7.5) -- the locked-page note is not a
  // text field and stays visible; a buyer's explicit toggle always wins.
  const wordsAutoCollapsed = inlineEditing
    && activeKey != null && activeFields.length > 0;
  const wordsExpanded = wordsOpen == null ? !wordsAutoCollapsed : wordsOpen;

  // Plain key list for the deckStyles helpers that operate on template_keys.
  const selectedKeys = useMemo(() => selected.map((s) => s.template_key), [selected]);

  const inDeckCounts = useMemo(() => {
    const m = {};
    selected.forEach((s) => { m[s.template_key] = (m[s.template_key] || 0) + 1; });
    return m;
  }, [selected]);

  // ⭐ LANE 941 -- THE BUILD'S OWN VERDICT ON EVERY PAGE IN THE LIBRARY.
  //
  // Read STRAIGHT off the record the backend wrote (`page_verdicts` in
  // helpers/deck_objectives.py, persisted at intake.intent.page_verdicts). This
  // component derives NOTHING: it does not decide what counts as withheld, does
  // not re-check a gate, and does not author a reason. Every reason below is the
  // gate's own sentence, carried through unedited.
  //
  // Why not compute it here from `intent.excluded` + `intent.admissible`, both of
  // which are already on hand? Because then the build screen, the prep pack and
  // the chat card each hold their own version of "withheld", and they disagree the
  // first time one of them is edited -- which has already happened once: the pack
  // (deck_briefing.py:1005-1014) names only the pages where `in_skeleton or hard`
  // and rolls the rest into "Other library pages were not relevant to this
  // meeting", which is a DIFFERENT and less true answer than this one (Rule 120).
  //
  // ⚠️ ABSENT ON EVERY DECK BUILT BEFORE THIS LANE -> `{}` -> the withheld block
  // does not render and the library renders exactly as it did. Nothing degrades.
  const pageVerdicts = useMemo(() => {
    const v = intake && intake.intent && intake.intent.page_verdicts;
    return (v && typeof v === 'object' && !Array.isArray(v)) ? v : {};
  }, [intake]);

  // The withheld pages, as a flat list sorted by the name the buyer reads.
  //
  // Deliberately NOT grouped by reason, and deliberately not routed through
  // `groupHardExclusions`: that helper answers the ARGUMENT LINE's question
  // ("which arguments are ruled out and not mine to put back") and filters on
  // `hard`, so a SOFT exclusion is invisible to it. Here a soft exclusion is the
  // single most useful row on screen -- it is the page the buyer legitimately may
  // add back, which is exactly the act that produced Brad's bad deck.
  // ⛔ `inDeckCounts` IS PART OF THE FILTER, NOT DECORATION -- CAUGHT AT CONSULT.
  //
  // `page_verdicts` is computed ONCE at build time and persisted; `selected` moves
  // every time the buyer edits. So the instant he takes the lane's own advice and
  // adds a withheld page back, its verdict still reads `withheld` forever. Without
  // this check the rail went on saying "1 page left out" and listing a page now
  // sitting in his deck -- while `SlideCard` (LibraryDrawer.js:152) correctly
  // suppressed its own chip on the same page. Two surfaces THIS ONE LANE BUILT,
  // disagreeing about one page, which is the precise failure Rule 120 is about.
  // The two filters are now the same filter.
  const withheldPages = useMemo(() => {
    const out = [];
    Object.keys(pageVerdicts).forEach((key) => {
      const rec = pageVerdicts[key] || {};
      if (rec.state !== 'withheld') return;
      if (inDeckCounts[key]) return;   // he already put it back
      // ⭐ LANE 948 -- BEAT 1 OF RYAN'S REGISTER. `figure.sentence` is composed
      // ONCE, in `deck_objectives._register_figure`, from the engine values the
      // gate itself decided on -- so this row cannot state a figure the page
      // would not show, and the library card beside it cannot word it
      // differently. Absent on every page whose exclusion has no figure attached
      // (and on every deck built before this lane) -> the row renders exactly as
      // it did, which is beats 2 and 3 and is the correct output, not a
      // degraded one.
      const figure = (rec.figure && typeof rec.figure.sentence === 'string')
        ? rec.figure.sentence : '';
      out.push({
        key, name: templateName(key), reason: rec.reason || '', figure, hard: !!rec.hard,
      });
    });
    out.sort((a, b) => a.name.localeCompare(b.name));
    return out;
  }, [pageVerdicts, inDeckCounts, templates]);   // eslint-disable-line react-hooks/exhaustive-deps

  // --- derived: preview source (single active slide vs whole deck) -------
  // Fall back to the WHOLE deck whenever the active slide is absent
  // (deck mode, or the active slide was omitted from the export) so the preview
  // is NEVER blank once an export exists -- restores rev-1's "always renders
  // after refresh" reliability while keeping the single-slide default.
  // Lane 905: an audience-filtered page is not in the export either, so it must
  // count as "not rendered" here. It is NOT folded into `omitted` itself --
  // omitted means "we could not build it" and drives a different, and in this
  // case untrue, sentence. Without this V1's isolation index is off by one for
  // every page after a filtered one, and V2 cannot detect its honest fallback.
  const notRendered = useMemo(
    () => [...omitted, ...(audienceFiltered.keys || [])],
    [omitted, audienceFiltered]);
  const displayedKeys = workspaceShellV2Enabled && previewOrder.length
    ? previewOrder.map((inst) => inst.template_key) : selectedKeys;
  const renderedIdx = renderedIndexForActive(displayedKeys, notRendered, frameIndex(safeActiveIdx));
  // Lane 1233 / W1+W6: the PREVIEW-ONLY shell marker rides the v2 doc
  // (frame gesture + page-switch gate); downloads consume the untouched
  // export html, never these memos. Memoized SEPARATELY so its string
  // identity is stable across page switches — that stability IS W6: the
  // pane's srcDoc never changes on page selection, so the iframe loads
  // once and the strict hydrate's activeInstance/mode drive which page is
  // visible in-frame. V1 keeps the per-page isolation lifecycle exactly.
  const markedExportDoc = useMemo(() => (
    workspaceShellV2Enabled && elementEditorEnabled && exportDoc
      ? exportDoc.replace('</head><body>', '</head><body data-dk-shell-v2="1">')
      : exportDoc
  ), [workspaceShellV2Enabled, elementEditorEnabled, exportDoc]);
  // Persistent switching is fail-closed across the independently shipped
  // shell/editor flags and the exact marker seam. Without BOTH protocol-2
  // editor support and a proven marker injection, retain positional isolation.
  const persistentFrameEnabled = workspaceShellV2Enabled
    && elementEditorEnabled && markedExportDoc !== exportDoc;
  const previewSrcDoc = useMemo(() => {
    if (!exportDoc) return '';
    if (workspaceShellV2Enabled) return hideCanvasScrollbar(persistentFrameEnabled ? markedExportDoc : exportDoc);
    if (previewMode === 'deck' || renderedIdx < 0) return exportDoc;
    // V1, or a shell whose independent editor capability/marker seam is
    // unavailable: the injected script hides all but the positional page.
    return isolateSlideInDoc(exportDoc, renderedIdx);
  }, [exportDoc, previewMode, renderedIdx, persistentFrameEnabled,
    markedExportDoc, workspaceShellV2Enabled]);

  const canvasOrderKey = JSON.stringify(workspaceShellV2Enabled
    ? selected.map((inst) => inst._editor_instance_id) : []);
  const canvasOrder = useMemo(() => {
    if (!workspaceShellV2Enabled) return undefined;
    const order = selected.map((inst, i) => previewOrder.length
      ? previewOrder.findIndex((original) => original._editor_instance_id === inst._editor_instance_id)
      : i).filter((index) => index >= 0 && !notRendered.includes(displayedKeys[index]));
    // Non-reorder structure edits must still deliver their disabled/stale
    // hydrate. Omit an incomplete permutation instead of invalidating it.
    return order.length === displayedKeys.filter((key) => !notRendered.includes(key)).length
      ? order : undefined;
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [workspaceShellV2Enabled, canvasOrderKey, previewOrder, notRendered]);

  // Refresh is enabled as soon as the deck has a slide; a missing subject is
  // surfaced by save()'s clear "Pick a subject supplier first" error on click
  // (a subject is genuinely required to save/export), not by a greyed button.
  const canRefresh = slideCount > 0;

  // Lane 789: dismiss the download menu on any click outside it. Registered only
  // while the menu is open, so the page carries no listener at rest.
  useEffect(() => {
    if (!outOpen) return undefined;
    const close = () => setOutOpen(false);
    document.addEventListener('click', close);
    return () => document.removeEventListener('click', close);
  }, [outOpen]);

  // Stable reference -- MeetingPrepPack reports through this on every status
  // change, and an inline arrow would re-fire its effect on every render.
  const handlePackStatus = useCallback((s) => {
    setPackStatus((prev) => (
      prev.loading === s.loading && prev.enriching === s.enriching && prev.ready === s.ready
        ? prev : { loading: s.loading, enriching: s.enriching, ready: s.ready }
    ));
  }, []);

  // The build's own record of what a HARD gate left out, and why. Feeds the
  // argument line's "ruled out by the data" half -- distinct from a lever the
  // buyer ruled out, because this one is not theirs to put back.
  const hardExclusions = useMemo(() => {
    const ex = intake && intake.intent && intake.intent.excluded;
    return Array.isArray(ex) ? ex : [];
  }, [intake]);

  // ⭐ WHAT THE SECOND DOWNLOAD IS, IN WORDS, AND WHY IT MIGHT NOT BE READY.
  // Ryan: "make sure that they can see that there's the prep and the document."
  // The last arm is the pack's own internal-use banner language, because
  // that sentence is already the right one and a paraphrase would drift from it.
  let packMenuNote;
  if (!briefingEnabled) {
    packMenuNote = 'The meeting prep pack is not available for this deck.';
  } else if (!editingId) {
    packMenuNote = 'Save the deck first. The pack is composed from the saved version.';
  } else if (packDownloading) {
    packMenuNote = 'Preparing…';
  } else if (packStatus.enriching) {
    packMenuNote = 'Your talking points are being prepared. The download will be available when they are ready.';
  } else {
    packMenuNote = `Meeting brief, talking points, responses to pushback, and transitions. Never included in the deck you share with `
      + `${subject ? subject.supplier_name : 'the supplier'}.`;
  }

  const deckHeadline = title || (subject ? `${meetingType}: ${subject.supplier_name}` : 'New deck');
  // Lane 804 (pre-launch item 2, second half): the meeting type is dropped from
  // the toolbar subline too. With the input gone it can no longer be changed, so
  // leaving it here would pin a permanent, uneditable "· QBR" beside every deck
  // name -- the definition of a label that serves no purpose. The HEADLINE keeps
  // its `${meetingType} — ${supplier}` fallback deliberately: that is the deck's
  // NAME when the user has not typed a title, and removing it would leave a deck
  // unnamed.
  const deckSubline = [
    subject ? subject.supplier_name : 'No supplier picked yet',
    `${slideCount} page${slideCount === 1 ? '' : 's'}`,
  ].filter(Boolean).join(' · ');

  // Lane 1229 / S5 (§5, v1.2 raster amendment): Present is a read-only view
  // over the SAME §11 snapshot bundle the filmstrip resolved — one FULL page
  // image at a time, lazily fetched (current + next), never a second
  // renderer, no frame, no postMessage surface. Disabled with a visible
  // reason until a verified, current, fit-clean preview with ready
  // snapshots exists.
  // Lane 1231 / F4: the §4 narrow/coarse condition, from the SAME
  // matchMedia compound the inline-capability effect tracks — layout and
  // capability cannot diverge.
  const closePagesDrawer = () => {
    setPagesDrawerOpen(false);
    if (pagesDrawerBtnRef.current) pagesDrawerBtnRef.current.focus();
  };

  const presentPageCount = (filmstrip.state === 'ready' && filmstrip.urls)
    ? filmstrip.urls.length : 0;
  let presentDisabledReason = null;
  if (busy) presentDisabledReason = 'Working…';
  else if (!exportDoc) presentDisabledReason = workspaceShellV2Enabled ? 'Waiting for the deck' : 'Build the preview first';
  else if (previewStale) presentDisabledReason = 'Refresh the preview first';
  else if (fitBlocked) presentDisabledReason = 'Fix page fit first';
  else if (presentPageCount === 0) {
    presentDisabledReason = 'Page snapshots are not ready';
  } else if (renderedIdx < 0) {
    // Lane 1231 / F5: the ACTIVE page was omitted or audience-filtered
    // out of the built artifact. Present must never silently substitute
    // another page — an honest disable instead.
    presentDisabledReason = 'The selected page is not in the built preview';
  }

  const closePresent = () => {
    setPresentOpen(false);
    presentEpochRef.current += 1;  // fence in-flight page fetches
    presentEnteredFsRef.current = false;
    Object.values(presentUrlsLiveRef.current || {}).forEach((u) => {
      if (typeof u === 'string') URL.revokeObjectURL(u);
    });
    presentUrlsLiveRef.current = {};
    setPresentUrls({});
    presentFetchedRef.current = new Set();
    if (document.fullscreenElement && document.exitFullscreen) {
      try {
        const exit = document.exitFullscreen();
        if (exit && exit.catch) exit.catch(() => {});
      } catch (e) { /* fullscreen exit is best-effort */ }
    }
    if (presentBtnRef.current) presentBtnRef.current.focus();
  };

  const openPresent = () => {
    // Start on the active page's RENDERED index (the manifest's order).
    // Lane 1231 / F5: renderedIdx < 0 can no longer reach here — the
    // button is disabled with an honest reason instead of substituting
    // page 1; the guard stays as defence in depth.
    if (renderedIdx < 0) return;
    setPresentIdx(renderedIdx);
    setPresentOpen(true);
  };
  closePresentRef.current = closePresent;

  // Lane 1229 / S3 → Lane 1231: the thumbnail stage, ONE renderer shared by
  // the rail tiles and the narrow strip. Snapshot pages are in RENDERED
  // order (an omitted/audience-filtered slide consumes no page number), so
  // every consumer resolves through the SAME rendered-index mapping the
  // canvas isolation uses. Decorative (aria-hidden): the page identity
  // lives on the selection button's accessible name, the status truth on
  // the visible chips.
  const renderThumbStage = (idx, prefix = 'thumb') => {
    const tileRenderedIdx = renderedIndexForActive(displayedKeys, notRendered, frameIndex(idx));
    const tileUrl = (filmstrip.state === 'ready'
      && filmstrip.urls && tileRenderedIdx >= 0)
      ? filmstrip.urls[tileRenderedIdx] : null;
    let placeholder = FILMSTRIP_PLACEHOLDER[filmstrip.state]
      || FILMSTRIP_PLACEHOLDER.none;
    if (filmstrip.state === 'ready') {
      placeholder = tileRenderedIdx < 0
        ? 'Not in the built preview'
        : 'Preview unavailable';
    }
    return (
      <span className="dk-thumb" aria-hidden="true">
        {tileUrl ? (
          <img className="dk-thumb-img" src={tileUrl}
            alt="" data-testid={`${prefix}-img-${idx}`} />
        ) : (
          <span className="dk-thumb-ph"
            data-testid={`${prefix}-placeholder-${idx}`}>
            {placeholder}
          </span>
        )}
      </span>
    );
  };

  // Lane 1229 / S2 (§2): the v2 dock-mode switcher, rendered at the top of
  // whichever dock zone is visible (exactly one renders at a time). Defined
  // once so the two mount points cannot drift. Null in V1.
  const dockTabs = workspaceShellV2Enabled ? (
    <div className="dk-dock-tabs" data-testid="dock-tabs">
      <button type="button"
        className={`dk-dock-tab${dockMode === 'pages' ? ' on' : ''}`}
        aria-pressed={dockMode === 'pages'}
        data-testid="dock-tab-pages"
        onClick={() => {
          // Only arm the focus hand-off when the mode actually flips —
          // a stale pending target would misfire on the NEXT switch.
          if (dockMode !== 'pages') {
            dockFocusPending.current = 'tab-pages';
            setDockMode('pages');
          }
        }}>
        Pages
      </button>
      <button type="button"
        className={`dk-dock-tab${dockMode === 'edit' ? ' on' : ''}`}
        aria-pressed={dockMode === 'edit'}
        data-testid="dock-tab-edit"
        onClick={() => {
          // §4: opening Edit moves focus to the panel heading. Armed only
          // on a real flip (see the Pages tab note).
          if (dockMode !== 'edit') {
            dockFocusPending.current = 'heading';
            setDockMode('edit');
          }
        }}>
        Edit
      </button>
    </div>
  ) : null;

  const fitBlockedReason = fitError
              ? 'Shorten the highlighted text to fit the page'
              : (!exportDoc
                ? (workspaceShellV2Enabled ? 'Waiting to confirm edited text fits the page' : 'Build the preview to confirm edited text fits the page')
                : (previewStale
                  ? 'Refresh the preview to confirm edited text fits the page'
                  : (activeFitPending
                    ? 'Checking that edited text fits the page'
                    : 'Open each edited page to confirm its text fits')));

  // Shared elements retain their components, props and handlers in both layouts.
  const ActionGroup = workspaceShellV2Enabled ? 'div' : React.Fragment;
  const toolbar = (<>
      {!workspaceShellV2Enabled && error && <div className="dk-banner err">{error}</div>}
      {!workspaceShellV2Enabled && status && <div className="dk-banner ok">{status}</div>}
      {workspaceShellV2Enabled && <WorkspaceNotice status={status} error={error} dismissError={dismissError} />}

      {/* ===== 1. THE ARGUMENT LINE =========================================
          DECK-scoped, so it is not in a slide-scoped panel any more. Always
          visible, expands full-width, and carries Lane 766's audit controls
          verbatim. Renders NOTHING when the brief surface is dark (every tenant
          on PROD today), so this page is then exactly the page it was. */}
      {!workspaceShellV2Enabled && <ArgumentLine
        supplierId={subject ? subject.supplier_id : null}
        supplierName={subject ? subject.supplier_name : null}
        hardExclusions={hardExclusions}
        nameFor={templateName}
        apiBase={API_BASE}
        token={token}
        onChanged={() => setBriefRevision((n) => n + 1)}
      />}

      {/* ===== 2. ONE TOOLBAR ===============================================
          Every deck-level action lives here and nowhere else. The four
          configuration inputs that used to sit on screen permanently (and wrap
          onto a second row, which Ryan called out as misalignment) are behind
          "Deck setup" -- a row that does not exist cannot wrap. */}
      <div className="dk-bar">
        <div className="dk-ident">
          <div className="t" data-testid="deck-headline">{deckHeadline}</div>
          <div className="s">{deckSubline}</div>
        </div>
        <ActionGroup {...(workspaceShellV2Enabled ? { className: 'dk-bar-actions', 'data-testid': 'deck-actions' } : {})}>
        <button type="button" className={`dk-btn ghost sm${setupOpen ? ' on' : ''}`}
          aria-expanded={setupOpen} data-testid="deck-setup-toggle"
          ref={setupToggleRef}
          onClick={() => setSetupOpen((v) => !v)}>
          <GearIcon />Deck setup
        </button>
        <span className="sp" />
        {workspaceShellV2Enabled && <div className="dk-built">
          {exportedAt && <span>Built {exportedAt}</span>}
          <button type="button" className="dk-btn ghost sm dk-refresh-data"
            disabled={busy || !canRefresh} onClick={refreshPreview}
            title="Refresh data" aria-label="Refresh data">
            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.7" aria-hidden="true">
              <path d="M20 7v5h-5M20 12a8 8 0 1 0-2 5M20 7l-3-3" />
            </svg>
          </button>
        </div>}

        {/* Save is a control when there is something to save and a STATEMENT
            when there is not. Refresh preview and both downloads still save
            first, exactly as before, so nothing depends on this being clicked. */}
        {/* Plan §7.4: while an edit overflows its page, saving and both
            downloads are held; the flag names the reason in plain words. */}
        {fitBlocked && (
          <span className={`dk-fitflag${workspaceShellV2Enabled ? ' dk-sr-status' : ''}`} data-testid="fit-flag">
            {fitBlockedReason}
          </span>
        )}
        {viewOnly && (
          <span className="dk-savestate" data-testid="view-only">
            Built by {viewOnly.author}. You can view, present and download it; only they can change or delete it. Nothing you change here is saved, and downloads use the deck as they saved it.
          </span>
        )}
        {/* eslint-disable-next-line no-nested-ternary */}
        {viewOnly ? null : dirty ? (
          <button type="button" className="dk-btn sm" disabled={busy || fitBlocked} onClick={save}
            data-testid="save-deck" title={workspaceShellV2Enabled && fitBlocked ? fitBlockedReason : undefined}>
            {busy ? 'Saving…' : 'Save deck'}
          </button>
        ) : (!workspaceShellV2Enabled && canSave ? (
          <span className="dk-savestate" data-testid="save-state">All changes saved</span>
        ) : null)}

        {/* ⭐ THE TWO ARTIFACTS. Ryan: "Just call it Download ... make sure that
            they can see that there's the prep and the document." Two files leave
            this page and they are NOT the same file -- one is put on screen in
            front of the supplier, the other never leaves your side of the table.
            The old page had two buttons both saying "Download" in two different
            panels and said nothing about which was which. */}
        {/* Lane 1229 / S2 (§2): the v2 toolbar's Edit toggle enters/exits the
            left dock's Edit mode. It swaps DOCK CONTENT only — the editor
            object, callbacks, and capability expression are untouched, so no
            second editor lifecycle exists (§7.1). V1 renders no such button. */}
        {workspaceShellV2Enabled && (
          <button type="button"
            className={`dk-btn ghost sm${dockMode === 'edit' ? ' on' : ''}`}
            aria-pressed={dockMode === 'edit'}
            data-testid="dock-edit-toggle"
            onClick={() => setDockMode((m) => {
              // §4: entering Edit focuses the panel heading; exiting keeps
              // focus on this persistent toolbar button.
              if (m !== 'edit') dockFocusPending.current = 'heading';
              return m === 'edit' ? 'pages' : 'edit';
            })}>
            Edit
          </button>
        )}
        {/* Lane 1229 / S5 (§5): Present — read-only, snapshot-image-backed,
            disabled with the visible reason until a verified, current,
            fit-clean preview with ready snapshots exists. */}
        {workspaceShellV2Enabled && (
          <>
            <button type="button" className="dk-btn ghost sm"
              ref={presentBtnRef}
              data-testid="present-button"
              disabled={Boolean(presentDisabledReason)}
              title={(fitBlocked ? fitBlockedReason : presentDisabledReason) || 'Present full screen'}
              onClick={openPresent}>
              Present
            </button>
          </>
        )}
        <div className="dk-menuwrap" onClick={(e) => e.stopPropagation()}>
          <button type="button" className="dk-btn pri" disabled={busy}
            aria-expanded={outOpen} aria-haspopup="true"
            data-testid="download-menu-button"
            onClick={() => setOutOpen((v) => !v)}>
            <DownloadIcon />Download<ChevronDownIcon />
          </button>
          {outOpen && (
            <div className="dk-menu" data-testid="download-menu">
              {!workspaceShellV2Enabled && <div className="dk-menu-h">Two files. They are not the same file.</div>}
              <button type="button" className="dk-mi" data-testid="download-deck"
                disabled={slideCount === 0 || fitBlocked}
                onClick={() => { setOutOpen(false); download(); }}>
                <span className="t">
                  <span className="dk-audpill share">Share with the supplier</span>
                  The deck
                </span>
                {!workspaceShellV2Enabled && <span className="d">
                  The {slideCount} page{slideCount === 1 ? '' : 's'} you put on screen in
                  front of {subject ? subject.supplier_name : 'the supplier'}. No strategy,
                  no leverage, no ruled-out arguments.
                </span>}
              </button>
              <button type="button" className="dk-mi" data-testid="download-pack"
                disabled={!briefingEnabled || !editingId || (presentationTemplates ? !packStatus.ready : packStatus.enriching)
                  || packDownloading || fitBlocked}
                onClick={downloadPack}>
                <span className="t">
                  <span className="dk-audpill keep">Keep: internal only</span>
                  {presentationTemplates && !packStatus.ready
                    ? (packStatus.enriching ? 'Meeting prep pack · preparing…' : 'Meeting prep pack · not ready')
                    : workspaceShellV2Enabled ? 'Meeting prep pack' : 'Your meeting prep pack'}
                </span>
                {!workspaceShellV2Enabled && <span className="d">{packMenuNote}</span>}
              </button>
            </div>
          )}
        </div>
        </ActionGroup>
      </div>

  </>);

  // Lane 1355: the deck chat (native decks only; the server decides the rest). It
  // reads the SAVED deck, so with unsaved changes it names no slide rather than the
  // wrong one, and the chip says so. It never reloads or edits the deck (slice 1).
  const nativeDeck = Boolean(editingId) && intake?.kind === 'template_library_presentation';
  // A save or preview build is queued, running or uncertain.
  const deckSaving = () => busyRef.current || writerPending.current > 0
    || Boolean(nativeRevision.current && nativeRevision.current.pending);
  const deckChatContext = () => ({
    edit_revision: nativeRevision.current && nativeRevision.current.compositionId === editingId
      ? nativeRevision.current.revision : (intake?.edit_revision ?? 0),
    source_revision: intake?.source_revision ?? 0,
    selected_position: !dirty && safeActiveIdx >= 0 ? safeActiveIdx + 1 : null,
    // The deck is clean: no unsaved edit, and no save queued, running or uncertain.
    may_request: !dirtyRef.current && !deckSaving(),
    // No unsaved edit, but a save or build is still running (for example the build
    // right after the deck opens): the chat waits for it rather than refuse
    // (staging 2026-10-01).
    settling: !dirtyRef.current && deckSaving(),
  });
  // Resolves when no save or build is running (or the buyer starts editing, or after
  // CHAT_SETTLE_MS, whichever is first); the message is then sent with the deck as
  // it is at that moment.
  const deckChatSettled = () => new Promise((resolve) => {
    const started = Date.now();
    const check = () => {
      if (!deckSaving() || dirtyRef.current || Date.now() - started > CHAT_SETTLE_MS
          || !workspaceAlive.current) {
        resolve();
      } else {
        setTimeout(check, 250);
      }
    };
    check();
  });
  // A chat change in the save queue (plan 5.1 item 4): after earlier writes finish,
  // `fn(clean)` runs with editing paused (clean: nothing unsaved at that moment);
  // { reload, position } reloads the deck and selects the changed slide, unless the
  // buyer has moved to another deck meanwhile.
  const applyChatChange = (fn) => {
    const epoch = workspaceEpoch.current;
    const compositionId = editingId;
    const valid = () => workspaceAlive.current && workspaceEpoch.current === epoch;
    const run = async () => {
      if (!valid()) return { refused: 'This deck is no longer open.' };
      const clean = !dirtyRef.current
        && !(nativeRevision.current && nativeRevision.current.pending);
      setChatWriting(true);
      if (document.activeElement && document.activeElement.blur) document.activeElement.blur();
      try {
        const out = await fn(clean);
        if (out && out.reload && valid()) {
          // A change that committed while the buyer has unsaved edits (a recovered
          // receipt) never reloads over them (Codex s2 r5 #1).
          if (!clean || dirtyRef.current) {
            // A save now would be refused (the deck moved on), so say what works.
            setChatReload('The deck changed from the chat while you had unsaved edits. Copy '
              + 'any text you want to keep, then reload the deck.');
            return { ...out, reloaded: false };
          }
          const loaded = await loadDeck(compositionId);
          // loadDeck starts a new workspace epoch, so check the deck itself: loaded,
          // still this deck, still on screen, and at the revision the change produced.
          const here = loaded && workspaceAlive.current && nativeRevision.current
            && nativeRevision.current.compositionId === compositionId;
          if (!here || (out.editRevision != null
            && nativeRevision.current.revision !== out.editRevision)) {
            if (workspaceAlive.current) {
              setChatReload('The deck changed from the chat, but it could not reload. '
                + 'Reload the deck before editing.');
            }
          } else {
            setChatReload(null);
            if (out.position) setActiveSlideIdx(out.position - 1);
          }
        }
        return out;
      } finally {
        setChatWriting(false);
      }
    };
    return queueChatWriter(run);
  };
  // One chat writer at a time, after earlier saves: `run` goes to the end of the
  // deck's save queue.
  const queueChatWriter = (run) => {
    const next = writerSerial.current.then(run, run);
    writerSerial.current = next.catch(() => {});
    writerPending.current += 1;
    next.then(() => { writerPending.current -= 1; }, () => { writerPending.current -= 1; });
    return next;
  };
  // "Reload the deck" after a chat change: in the save queue with editing paused,
  // and the banner (and its save block) stays until a reload has succeeded
  // (Codex s2 r7 #1, #2). The buyer was told to copy unsaved text first.
  const reloadAfterChat = () => {
    const compositionId = editingId;
    return queueChatWriter(async () => {
      if (!workspaceAlive.current) return;
      setChatWriting(true);
      if (document.activeElement && document.activeElement.blur) document.activeElement.blur();
      try {
        const loaded = await loadDeck(compositionId);
        if (loaded && workspaceAlive.current && nativeRevision.current
          && nativeRevision.current.compositionId === compositionId) {
          setChatReload(null);
        }
      } finally {
        setChatWriting(false);
      }
    });
  };
  const deckChat = nativeDeck && !viewOnly ? (
    <DeckChatDock key={editingId} apiBase={API_BASE} compositionId={editingId}
      authHeaders={authHeaders} getDeckContext={deckChatContext}
      hidden={presentOpen} layout={desktopWorkspace ? 'dock' : 'drawer'}
      draft={deckChatDraft.id === editingId ? deckChatDraft.text : ''}
      onDraft={(text) => setDeckChatDraft({ id: editingId, text })}
      applyChange={applyChatChange} whenSettled={deckChatSettled} />
  ) : null;

  const pages = (<>
        {(!workspaceShellV2Enabled || dockMode === 'pages') && (
        <div className={workspaceShellV2Enabled ? 'dk-zone dk-z-pages' : 'dk-zone'}>
          {dockTabs}
          <div className="dk-zone-h">
            <span className="zt">Pages</span>
            <span className="zc">{slideCount} page{slideCount === 1 ? '' : 's'}</span>
          </div>
          {/* Lane 1229 / S3: strip-level filmstrip truth. Thumbnails always
              depict the LAST BUILT preview; staleness is the EXISTING deck
              truth (previewStale), never thumbnail inference (§3). */}
          {workspaceShellV2Enabled && previewStale
            && filmstrip.state === 'ready' && (
            <div className="dk-strip-note" data-testid="filmstrip-stale-note">
              Page previews show the last built preview. Refresh to update.
            </div>
          )}
          {/* Lane 1231 / F4 (§4): on narrow/coarse the rail does NOT render
              inline — the strip below is selection-only, and the FULL rail
              (reorder, remove, add, withheld, resume) moves into the
              focus-managed Pages drawer. One rail, one set of handlers;
              only its host changes. */}
          {(() => {
            const rail = (
          <div className="dk-rail" ref={railRef}>
            {selected.map((inst, idx) => {
              const key = inst.template_key;
              const name = templateName(key);
              const isActive = idx === safeActiveIdx;
              const edited = Object.keys(inst.overrides || {}).length > 0;
              const dragHandlers = {
                draggable: true,
                onDragStart: (e) => { setDragIdx(idx); if (e.dataTransfer) e.dataTransfer.effectAllowed = 'move'; },
                onDragOver: (e) => {
                  e.preventDefault();
                  const rect = e.currentTarget.getBoundingClientRect();
                  const after = (e.clientY - rect.top) > rect.height / 2;
                  setDragOverIdx(after ? idx + 1 : idx);
                },
                onDrop: (e) => { e.preventDefault(); handleSlideDrop(dragOverIdx); },
                onDragEnd: () => { setDragIdx(null); setDragOverIdx(null); },
              };
              // Lane 1231 / F2: the v2 tile is keyed by the instance's
              // STABLE editor identity, so a keyboard reorder moves the DOM
              // node instead of unmounting it (focus travels with it). V1
              // keeps its positional key — React keys are invisible in the
              // rendered DOM, but byte-equivalence is cheap to keep total.
              const identity = inst._editor_instance_id || `${key}-${idx}`;
              if (workspaceShellV2Enabled) {
                const chips = v2TileStatus({
                  instance: inst,
                  internalOnly: false,
                  fitError: Boolean(fitErrors[idx]),
                  fitPending: Boolean(fitChecksPending[idx]),
                });
                const statusId = chips.length
                  ? `dk-tilestate-${idx}` : undefined;
                return (
                  <React.Fragment key={identity}>
                    {dragIdx !== null && dragOverIdx === idx && dragIdx !== idx && (
                      <div className="dk-insbar" aria-hidden="true" />
                    )}
                    {/* F2: a NON-interactive wrapper (drag lives here) with a
                        genuine selection <button> and SIBLING move/remove
                        buttons — no nested interactives. The accessible name
                        stays exactly `Page N: <title>`; the status chips are
                        associated via aria-describedby and rendered visibly. */}
                    <div
                      className={`dk-row dk-tile${isActive ? ' on' : ''}${dragIdx === idx ? ' drag' : ''}`}
                      data-row-idx={idx}
                      data-row-identity={identity}
                      title={`${name}: drag to reorder, or use Alt+↑ / Alt+↓`}
                      {...dragHandlers}
                    >
                      <button type="button" className="dk-rowbtn"
                        data-testid={`tile-select-${idx}`}
                        aria-label={`Page ${idx + 1}: ${name}`}
                        aria-current={isActive ? 'page' : undefined}
                        aria-keyshortcuts="Alt+ArrowUp Alt+ArrowDown"
                        aria-describedby={statusId}
                        onClick={() => selectPage(idx)}
                        onKeyDown={(e) => {
                          // §3: Up/Down (vertical desktop) AND Left/Right
                          // (horizontal narrow strip) move selection and
                          // focus — both axes always handled, so keyboard
                          // behavior cannot depend on a media query.
                          if (e.key === 'ArrowDown' || e.key === 'ArrowUp'
                            || e.key === 'ArrowRight' || e.key === 'ArrowLeft') {
                            e.preventDefault();
                            const forward = e.key === 'ArrowDown' || e.key === 'ArrowRight';
                            if (e.altKey) {
                              railFocusPending.current = { identity, control: 'select' };
                              moveSlide(idx, forward ? 1 : -1);
                              return;
                            }
                            const next = forward
                              ? Math.min(idx + 1, slideCount - 1)
                              : Math.max(idx - 1, 0);
                            selectPage(next);
                            const el = railRef.current && railRef.current
                              .querySelector(`[data-row-idx="${next}"] .dk-rowbtn`);
                            if (el) el.focus();
                          }
                        }}>
                        {/* W3 (binding 4.6): tile = thumbnail + corner
                            number + decorative status marks. The page TITLE
                            lives in the accessible name (and the wrapper
                            tooltip) — the thumbnail speaks for itself. The
                            status TRUTH is the offscreen string below,
                            reachable via aria-describedby; the marks are
                            aria-hidden decoration. */}
                        <span className="dk-thumbwrap">
                          {renderThumbStage(idx)}
                          <span className="dk-tileno" aria-hidden="true">{idx + 1}</span>
                          {chips.length > 0 && (
                            <span className="dk-tilemarks" aria-hidden="true">
                              {chips.map((c) => (
                                <span key={c.kind} className={`dk-tilemark ${c.kind}`}>
                                  {c.kind === 'int' ? 'INT' : ''}
                                </span>
                              ))}
                            </span>
                          )}
                        </span>
                      </button>
                      {chips.length > 0 && (
                        <span className="dk-sr-status" id={statusId}
                          data-testid={`tile-status-${idx}`}>
                          {chips.map((c) => c.label).join('. ')}
                        </span>
                      )}
                      <button type="button" className="dk-ib rm dk-tile-delete"
                        data-act="rm" aria-label={`Remove ${name}`}
                        onClick={() => removeSlide(idx)}>&times;</button>
                    </div>
                  </React.Fragment>
                );
              }
              return (
                <React.Fragment key={`${key}-${idx}`}>
                  {dragIdx !== null && dragOverIdx === idx && dragIdx !== idx && (
                    <div className="dk-insbar" aria-hidden="true" />
                  )}
                  <div
                    className={`dk-row${isActive ? ' on' : ''}${dragIdx === idx ? ' drag' : ''}`}
                    role="button"
                    tabIndex={0}
                    aria-pressed={isActive}
                    // Explicit, because the row's own contents include the
                    // hover controls -- without this the accessible name of
                    // "page 2" would read "2 Agenda up down remove".
                    aria-label={`Page ${idx + 1}: ${name}`}
                    title={name}
                    {...dragHandlers}
                    onClick={() => setActiveSlideIdx(idx)}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); setActiveSlideIdx(idx); }
                    }}
                  >
                    <span className="n">{idx + 1}</span>
                    <span className="nm">{name}</span>
                    {edited && (
                      <span className="dk-dot" title="You have edited words on this page" />
                    )}
                    <span className="dk-rowacts">
                      <button type="button" className="dk-ib" disabled={idx === 0}
                        aria-label={`Move ${name} up`}
                        onClick={(e) => { e.stopPropagation(); moveSlide(idx, -1); }}>&uarr;</button>
                      <button type="button" className="dk-ib" disabled={idx === slideCount - 1}
                        aria-label={`Move ${name} down`}
                        onClick={(e) => { e.stopPropagation(); moveSlide(idx, 1); }}>&darr;</button>
                      <button type="button" className="dk-ib rm"
                        aria-label={`Remove ${name}`}
                        onClick={(e) => { e.stopPropagation(); removeSlide(idx); }}>&times;</button>
                    </span>
                  </div>
                </React.Fragment>
              );
            })}
            {dragIdx !== null && dragOverIdx === slideCount && slideCount > 0 && (
              <div className="dk-insbar" aria-hidden="true" />
            )}
            {slideCount === 0 && (
              <div className="dk-empty">
                No pages yet. Add one below, or plan the whole meeting from
                &ldquo;Deck setup&rdquo;.
              </div>
            )}
            {/* Adding a page lives at the end of the list it adds to. The
                library is also where "Generate a page" now lives -- it is the
                catalogue of pages, and generating one is what you do when the
                catalogue has not got what you want. */}
            {!workspaceShellV2Enabled && <button type="button" className="dk-addrow" onClick={() => setLibraryOpen(true)}>
              <PlusIcon />Add a page
            </button>}

            {/* ⭐ LANE 941 -- WHAT WAS LEFT OUT, AND WHY, UNDER THE LIST IT IS
                MISSING FROM.

                Brad re-added an excluded page BLIND, because nothing told him it
                had been left out or on what grounds -- and that is how his bad
                deck exists. The reasons were never missing; eleven of them were
                computed for his supplier and every one was thrown at a status
                toast that scrolled away. This is the same data, in the one place
                the question gets asked: directly beneath "Pages".

                The COUNT is always on screen; the reasons expand. Every string
                below is the gate's own sentence, unedited (see `withheldPages`). */}
            {withheldPages.length > 0 && (
              <div className="dk-wh">
                <button type="button" className="dk-wh-t"
                  aria-expanded={withheldOpen}
                  data-testid="withheld-toggle"
                  onClick={() => setWithheldOpen((v) => !v)}>
                  <Chevron open={withheldOpen} />
                  {`${withheldPages.length} page${withheldPages.length === 1 ? '' : 's'} left out`}
                </button>
                {withheldOpen && (
                  <div className="dk-wh-body" data-testid="withheld-list">
                    {withheldPages.map((p) => (
                      <div className="dk-wh-row" key={p.key}>
                        <span className="nm">{p.name}</span>
                        {p.reason && <span className="rs">{p.reason}</span>}
                        {p.figure && (
                          // The engine's own figure for the decision above it,
                          // verbatim from the one producer. Never composed here.
                          <span className="fg" data-testid={`withheld-figure-${p.key}`}>
                            {p.figure}
                          </span>
                        )}
                      </div>
                    ))}
                    <div className="dk-hint" style={{ marginTop: '8px' }}>
                      Every one of these is still in the slide library if you want it.
                    </div>
                  </div>
                )}
              </div>
            )}
            {workspaceShellV2Enabled && <button type="button" className="dk-addrow"
              onClick={() => setLibraryOpen(true)}><PlusIcon />Add a page</button>}
          </div>
            );
            if (!narrowWorkspace) return rail;
            return (
              <>
                <div className="dk-striprow">
                <div className="dk-strip" data-testid="pages-strip" ref={stripRef}>
                  {selected.map((inst, idx) => {
                    const name = templateName(inst.template_key);
                    const isActive = idx === safeActiveIdx;
                    const identity = inst._editor_instance_id
                      || `${inst.template_key}-${idx}`;
                    const chips = v2TileStatus({
                      instance: inst,
                      internalOnly: false,
                      fitError: Boolean(fitErrors[idx]),
                      fitPending: Boolean(fitChecksPending[idx]),
                    });
                    const statusId = chips.length
                      ? `dk-stripstate-${idx}` : undefined;
                    return (
                      <button type="button" key={identity}
                        className={`dk-striptile${isActive ? ' on' : ''}`}
                        data-strip-idx={idx}
                        aria-label={`Page ${idx + 1}: ${name}`}
                        aria-current={isActive ? 'page' : undefined}
                        aria-describedby={statusId}
                        onClick={() => selectPage(idx)}
                        onKeyDown={(e) => {
                          if (e.key === 'ArrowRight' || e.key === 'ArrowLeft'
                            || e.key === 'ArrowDown' || e.key === 'ArrowUp') {
                            e.preventDefault();
                            const forward = e.key === 'ArrowRight' || e.key === 'ArrowDown';
                            const next = forward
                              ? Math.min(idx + 1, slideCount - 1)
                              : Math.max(idx - 1, 0);
                            selectPage(next);
                            const strip = e.currentTarget.closest('.dk-strip');
                            const el = strip && strip
                              .querySelector(`[data-strip-idx="${next}"]`);
                            if (el) el.focus();
                          }
                        }}>
                        <span className="dk-thumbwrap">
                          {renderThumbStage(idx, 'strip-thumb')}
                          <span className="dk-tileno" aria-hidden="true">{idx + 1}</span>
                          {chips.length > 0 && (
                            <span className="dk-tilemarks" aria-hidden="true">
                              {chips.map((c) => (
                                <span key={c.kind} className={`dk-tilemark ${c.kind}`}>
                                  {c.kind === 'int' ? 'INT' : ''}
                                </span>
                              ))}
                            </span>
                          )}
                        </span>
                        {chips.length > 0 && (
                          <span className="dk-sr-status" id={statusId}
                            data-testid={`strip-status-${idx}`}>
                            {chips.map((c) => c.label).join('. ')}
                          </span>
                        )}
                      </button>
                    );
                  })}
                </div>
                  <button type="button" className="dk-btn sm dk-drawerbtn"
                    ref={pagesDrawerBtnRef}
                    data-testid="pages-drawer-open"
                    aria-expanded={pagesDrawerOpen}
                    onClick={() => setPagesDrawerOpen(true)}>
                    All pages
                    {withheldPages.length > 0
                      ? ` · ${withheldPages.length} left out` : ''}
                  </button>
                </div>
                {pagesDrawerOpen && (
                  <div className="dk-pagesdrawer-backdrop">
                  <div className="dk-pagesdrawer" role="dialog" aria-modal="true"
                    aria-label="All pages" data-testid="pages-drawer"
                    onKeyDown={(e) => {
                      if (e.key === 'Escape') {
                        e.stopPropagation();
                        closePagesDrawer();
                      } else if (e.key === 'Tab') {
                        const focusables = Array.from(e.currentTarget
                          .querySelectorAll('button:not(:disabled), [href], input:not(:disabled), '
                            + 'select:not(:disabled), textarea:not(:disabled), [tabindex]:not([tabindex="-1"])'));
                        if (focusables.length) {
                          const first = focusables[0];
                          const last = focusables[focusables.length - 1];
                          if (!focusables.includes(document.activeElement)) {
                            e.preventDefault();
                            (e.shiftKey ? last : first).focus();
                          } else if (e.shiftKey && document.activeElement === first) {
                            e.preventDefault();
                            last.focus();
                          } else if (!e.shiftKey && document.activeElement === last) {
                            e.preventDefault();
                            first.focus();
                          }
                        }
                      }
                    }}>
                    <div className="dk-pagesdrawer-h">
                      <span className="t" tabIndex={-1}
                        ref={pagesDrawerHeadingRef}>All pages</span>
                      <span className="sp" />
                      <button type="button" className="dk-btn sm"
                        data-testid="pages-drawer-close"
                        onClick={closePagesDrawer}>Done</button>
                    </div>
                    {rail}
                  </div>
                  </div>
                )}
              </>
            );
          })()}
        </div>
        )}

  </>);

  const preview = (<>
        <div className={workspaceShellV2Enabled ? 'dk-zone dk-z-canvas' : 'dk-zone'}>
          {workspaceShellV2Enabled && <BuildProgress build={visibleBuild} />}
          <DeckPreviewPane
            key={workspaceShellV2Enabled ? previewGeneration : undefined}
            frameRef={previewFrameRef}
            srcDoc={previewSrcDoc}
            omitted={omitted}
            audienceNotice={audienceFiltered.notice}
            evidenceNotices={intake?.kind === 'template_library_presentation' ? intake.evidence_notices : []}
            stale={previewStale}
            busy={busy}
            canRefresh={canRefresh}
            onRefresh={refreshPreview}
            mode={canvasMode}
            onModeChange={setPreviewMode}
            inspectorOpen={workspaceShellV2Enabled ? true : inspectorOpen}
            onToggleInspector={workspaceShellV2Enabled
              ? null : () => setInspectorOpen((v) => !v)}
            updatedLabel={exportedAt}
            needsSlides={slideCount === 0}
            needsSubject={!subject}
            frameHeight={workspaceShellV2Enabled ? 620 : 420}
            fillHeight={workspaceShellV2Enabled}
            fixedSlide={desktopWorkspace}
            toolbarElsewhere={workspaceShellV2Enabled}
            editor={{
              shell2: persistentFrameEnabled,
              order: canvasOrder,
              revision: previewRevision,
              expectedVersion: elementEditorEnabled ? 2 : 1,
              fitRevision: settlement ? settlement.nonce : activeFitRevision,
              fitRevisionV2: settlement ? settlement.nonce : activeElementFitRevision,
              protocolVersion: editorProtocolVersion,
              activeInstance: settlement ? settlement.instanceIndex : frameIndex(safeActiveIdx),
              overrides: hydrateOverrides,
              patches: hydrateElementPatches,
              addedElements: hydrateAddedElements,
              mode: canvasMode,
              enabled: !settlement && (elementEditorEnabled
                ? (workspaceShellV2Enabled || previewMode === 'slide') && !previewStale
                : inlineEditing),
              inlineEnabled: !settlement && elementEditorEnabled && inlineEditing,
              rows: activeRowMeta,
              measure: settlement ? EMPTY_ADDED : pendingMeasure,
              onEdit: fromFrame(handleInlineEdit),
              onProtocolVersion: handleProtocolVersion,
              onSelect: fromFrame(handleElementSelect),
              onViewport: fromFrame(({ instanceIndex, structureRevision }) => {
                if (workspaceShellV2Enabled && structureRevision === previewRevision
                  && !previewStaleRef.current && selected[instanceIndex]) selectPage(instanceIndex);
              }),
              onElementEdit: fromFrame(handleElementEdit),
              onElementNudge: fromFrame(handleElementNudge),
              onAddedAdd: fromFrame(handleAddedAdd),
              onAddedDelete: fromFrame(deleteAddedFromFrame),
              onAddedSelect: fromFrame(handleAddedSelect),
              onAddedMove: fromFrame(handleAddedMove),
              onAddedResize: fromFrame(handleAddedResize),
              onElementUndo: fromFrame(handleElementHistory('undo')),
              onElementRedo: fromFrame(handleElementHistory('redo')),
              onUndoRequest: handleUndoRequest,
              onFitStatus: (message) => {
                if (!acknowledgeSettlement(message)) fromFrame(handleFitStatus)(message);
              },
              onRowAdd: fromFrame(handleRowMessage('add')),
              onRowDelete: fromFrame(handleRowMessage('delete')),
              onElementRowAdd: fromFrame(handleElementRowMessage('add')),
              onElementRowDelete: fromFrame(handleElementRowMessage('delete')),
            }}
          />
        </div>

  </>);

  const notes = (<>
        {workspaceShellV2Enabled && (
          <div className="dk-zone dk-z-notes" data-testid="speaker-notes-bar">
            <div className="dk-notes-header">
            <button type="button" className="dk-notes-h"
              aria-expanded={notesOpenEffective}
              data-testid="speaker-notes-toggle"
              ref={notesToggleRef}
              onClick={() => setNotesOpen(!notesOpenEffective)}>
              <Chevron open={notesOpenEffective} />
              <span className="t">Speaker notes</span>
            </button>
            <span className="dk-internal-chip">Internal prep, not included in supplier download</span>
            <div className="dk-notes-pack-action" ref={setPrepHeaderTarget} />
            </div>
            {(notesOpenEffective || presentationTemplates) && (
              <div className="dk-notes-b" data-testid="speaker-notes-body" hidden={!notesOpenEffective}>
                {presentationTemplates ? (
                  <MeetingPrepPack key={editingId} compositionId={editingId} apiBase={API_BASE}
                    authHeaders={authHeaders} onlySlideIndex={safeActiveIdx}
                    savedSignature={savedSignature} dirty={dirty} onSave={save} onStatus={handlePackStatus} readOnly={Boolean(viewOnly)}
                    introKey={user?.user_id && user?.company_id && editingId
                      ? `dk.prepIntro.v1.${user.company_id}.${user.user_id}.${editingId}` : null}
                    workspaceReady={Boolean(exportDoc) && !busy && !presentOpen && !setupOpen
                      && !argumentOpen && !pagesDrawerOpen && !libraryOpen
                      && !withheldOpen && !pendingInternalConfirm && !outOpen}
                    onOpenNotes={() => setNotesOpen(true)} notesToggleRef={notesToggleRef}
                    headerTarget={prepHeaderTarget} />
                ) : (
                  <div data-testid="meeting-plan-unavailable"
                    className="dk-inspect-empty">
                    The meeting prep pack is not available for this deck.
                  </div>
                )}
              </div>
            )}
          </div>
        )}

  </>);

  const inspector = (<>
        {(workspaceShellV2Enabled ? dockMode === 'edit' : inspectorOpen) && (
        <div className={workspaceShellV2Enabled ? 'dk-zone dk-z-insp' : 'dk-zone'}
          // Lane 1229 / S4 (§4): Escape closes the Edit dock/sheet and
          // restores focus to the toolbar Edit toggle. V2 only.
          onKeyDown={workspaceShellV2Enabled ? (e) => {
            if (e.key === 'Escape') {
              e.stopPropagation();
              // Consult #4 fix: an in-flight panel edit commits its ONE
              // coalesced history entry on blur — but unmounting a focused
              // input never fires blur, so the entry would silently skip
              // and per-field undo lose the edit. Blur explicitly first.
              if (document.activeElement
                  && typeof document.activeElement.blur === 'function') {
                document.activeElement.blur();
              }
              dockFocusPending.current = 'toggle';
              setDockMode('pages');
            }
          } : undefined}>
          {dockTabs}
          <div className="dk-insp">
            {activeKey ? (
              <>
                <div className="dk-insp-h"
                  ref={inspHeadingRef}
                  tabIndex={workspaceShellV2Enabled ? -1 : undefined}>
                  <div className="dk-kick">{`Page ${safeActiveIdx + 1} of ${slideCount}`}</div>
                  <div className="dk-insp-nm">{templateName(activeKey)}</div>
                  {activeKeyCopies > 1 && (
                    // Per-instance edits (Part 5): each copy is independent.
                    <div className="dk-ins-copies">
                      Edits apply to this copy only. There are {activeKeyCopies} copies of this slide in the deck.
                    </div>
                  )}
                </div>

                <div className="dk-sec">
                  {elementEditorEnabled ? (
                    <>
                      <div className="dk-sec-h"><span className="t">
                        {activeSelectedAdded ? 'Selected text box' : 'Selected text'}
                      </span>{workspaceShellV2Enabled && (activeSelectedAdded || canDeleteRow || canDeleteTemplate) &&
                        <button type="button" className="dk-btn sm danger" disabled={deletionBlocked}
                          onClick={deleteSelection}>{activeSelectedAdded ? 'Delete text box' : 'Delete row'}</button>}</div>
                      {workspaceShellV2Enabled && Object.values(activeInstanceForHydrate?.element_patches || {}).some(p => p.r === true) &&
                        <button type="button" className="dk-btn sm" disabled={selectionBlocked} onClick={restoreDeletedRows}>Restore deleted text</button>}
                      {activeSelectedElement && !(workspaceShellV2Enabled && activeSelectedPatch?.r) ? (
                        <div className="dk-stack">
                          <div className="dk-ef" style={{ marginTop: 0 }}>
                            <label className="dk-efl" htmlFor="deck-selected-content">Content</label>
                            <input
                              id="deck-selected-content"
                              className="dk-inp"
                              value={activeSelectedContent}
                              maxLength={2000}
                              onFocus={() => {
                                panelEditStart.current = {
                                  kind: 'element',
                                  identity: activeSelectedElement.identity,
                                  before: elementStateOf(activeInstanceForHydrate),
                                };
                              }}
                              onChange={(e) => {
                                // W5: truth first (state + fit-pending land
                                // in the txn), hydrate deferred to the one
                                // trailing timer.
                                deferTextHydrate();
                                if (activeSelectedAdded) {
                                  applyActiveAddedChange({ t: e.target.value }, { history: false });
                                } else if (activeSelectedSource) {
                                  applyElementTextTxn({
                                    ...activeSelectedSource,
                                    value: e.target.value,
                                  }, { history: false });
                                }
                              }}
                              onBlur={() => {
                                flushHydrate();  // W5: blur is a flush point
                                const start = panelEditStart.current;
                                panelEditStart.current = null;
                                if (!start || start.kind !== 'element'
                                  || start.identity !== activeSelectedElement.identity) return;
                                const current = selectedRef.current.find((item) =>
                                  item._editor_instance_id === start.identity);
                                if (!current) return;
                                const after = elementStateOf(current);
                                if (stableStringify(after) !== stableStringify(start.before)) {
                                  pushElementHistory(start.identity, {
                                    before: start.before,
                                    after,
                                    rebuild: stableStringify(after.row_blocks)
                                      !== stableStringify(start.before.row_blocks),
                                  });
                                }
                              }}
                              onKeyDown={(e) => { if (e.key === 'Enter') e.target.blur(); }}
                              aria-label="Content"
                            />
                          </div>
                          {activeSelectedSource && (
                            <button type="button" className="dk-btn sm"
                              disabled={!activeSelectedPatch || typeof activeSelectedPatch.t !== 'string'}
                              onClick={handleElementReset}>
                              Reset content
                            </button>
                          )}
                          {workspaceShellV2Enabled && (activeSelectedAdded || activeSelectedSource?.nudgeable) && (
                            <PositionPad key={`position:${positionKey}`} ref={positionPadRef} contextKey={exportDoc}
                              disabled={selectionBlocked || dockMode !== 'edit'}
                              x={activeSelectedAdded ? activeSelectedAdded.x : activeSelectedPatch?.g?.dx || 0}
                              y={activeSelectedAdded ? activeSelectedAdded.y : activeSelectedPatch?.g?.dy || 0}
                              onStart={() => { flushHydrateRef.current(); positionHistoryRef.current = {
                                identity: activeSelectedElement.identity, target: positionKey }; }}
                              onStop={() => { positionHistoryRef.current = null; }}
                              onStep={stepPosition} onReset={resetPosition} />
                          )}
                          {activeSelectedSource && !workspaceShellV2Enabled && (
                            <div className="dk-geometry-grid">
                              <label className="dk-efl" htmlFor="deck-element-dx">X offset</label>
                              <input id="deck-element-dx" className="dk-inp" type="number"
                                disabled={!activeSelectedSource.nudgeable}
                                value={(activeSelectedPatch && activeSelectedPatch.g
                                  && activeSelectedPatch.g.dx) || 0}
                                onChange={(e) => applyActiveGeometry('dx', e.target.value)} />
                              <label className="dk-efl" htmlFor="deck-element-dy">Y offset</label>
                              <input id="deck-element-dy" className="dk-inp" type="number"
                                disabled={!activeSelectedSource.nudgeable}
                                value={(activeSelectedPatch && activeSelectedPatch.g
                                  && activeSelectedPatch.g.dy) || 0}
                                onChange={(e) => applyActiveGeometry('dy', e.target.value)} />
                              {!activeSelectedSource.nudgeable && (
                                <div className="dk-dualnote" role="note">
                                  This inline target cannot be nudged without changing page flow.
                                </div>
                              )}
                            </div>
                          )}
                          {activeSelectedAdded && (
                            <div className="dk-geometry-grid">
                              {!workspaceShellV2Enabled && <>
                              <label className="dk-efl" htmlFor="deck-added-x">X position</label>
                              <input id="deck-added-x" aria-label="X position" className="dk-inp"
                                type="number" min="0" max={1920 - activeSelectedAdded.w}
                                value={activeSelectedAdded.x}
                                onChange={(e) => applyActiveAddedChange({ x: Number(e.target.value) })} />
                              <label className="dk-efl" htmlFor="deck-added-y">Y position</label>
                              <input id="deck-added-y" aria-label="Y position" className="dk-inp"
                                type="number" min="0" value={activeSelectedAdded.y}
                                onChange={(e) => applyActiveAddedChange({ y: Number(e.target.value) })} />
                              </>}
                              <label className="dk-efl" htmlFor="deck-added-w">Width</label>
                              <input id="deck-added-w" aria-label="Width" className="dk-inp"
                                type="number" min="40" max={1920 - activeSelectedAdded.x}
                                value={activeSelectedAdded.w}
                                onChange={(e) => applyActiveAddedChange({ w: Number(e.target.value) })} />
                              {!workspaceShellV2Enabled && <button type="button" className="dk-btn sm danger"
                                onClick={deleteActiveAdded}>Delete text box</button>}
                            </div>
                          )}
                          <div className="dk-style-head">Formatting</div>
                          <StyleInspector
                            key={`${activeSelectedElement.identity}:${activeSelectedElement.elementId || activeSelectedElement.addedId}`}
                            style={activeSelectedStyle}
                            onChange={applyElementStyleChange}
                            onReset={handleElementStyleReset} />
                          {activeSelectedSource && activeSelectedSource.dualContext && (
                            <div className="dk-dualnote" role="note">
                              This label also drives a chart or graphic. Text and formatting edits
                              change only the visible label; the source-driven graphic stays unchanged.
                            </div>
                          )}
                          <div className="dk-savehint">
                            Select another text item on the page to edit that target.
                          </div>
                        </div>
                      ) : (
                        <div className="dk-locknote" style={{ marginTop: 0 }}>
                          Select text on the page to edit it here.
                        </div>
                      )}
                    </>
                  ) : (
                    <>
                  {/* Plan §7.5: only THIS section may default collapsed when
                      inline editing is available (words are editable on the
                      page itself); prep, caveats and the full plan stay put.
                      A click stores the buyer's explicit choice. */}
                  <div className="dk-sec-h">
                    <button type="button" className="dk-wh-t" style={{ padding: 0 }}
                      aria-expanded={wordsExpanded}
                      data-testid="words-toggle"
                      onClick={() => setWordsOpen(!wordsExpanded)}>
                      <Chevron open={wordsExpanded} />
                      <span className="t">Your words on this page</span>
                    </button>
                  </div>
                  {/* eslint-disable-next-line no-nested-ternary */}
                  {!wordsExpanded ? null : activeFields.length > 0 ? (
                    <div className="dk-stack">
                      {activeFields.map((f) => (
                        <div className="dk-ef" key={f.token} style={{ marginTop: 0 }}>
                          <label className="dk-efl">{f.label}</label>
                          <input
                            className="dk-inp"
                            value={currentOverride(safeActiveIdx, f.token) || ''}
                            placeholder={f.default}
                            maxLength={2000}
                            onFocus={() => {
                              panelEditStart.current = {
                                idx: safeActiveIdx,
                                token: f.token,
                                value: currentOverride(safeActiveIdx, f.token),
                              };
                            }}
                            onChange={(e) => {
                              deferTextHydrate();  // W5: work waits, truth does not
                              setFitChecksPending((m) => ({ ...m, [safeActiveIdx]: true }));
                              applyOverrideTxn(
                                safeActiveIdx, f.token, e.target.value,
                                { history: false }
                              );
                            }}
                            onBlur={() => {
                              flushHydrate();  // W5: blur is a flush point
                              // Plan §7.2: the whole typing session is ONE
                              // history entry (keystrokes updated state and
                              // hydration live, with no history).
                              const start = panelEditStart.current;
                              panelEditStart.current = null;
                              if (!start || start.idx !== safeActiveIdx
                                || start.token !== f.token) return;
                              const now = currentOverride(safeActiveIdx, f.token);
                              if (now !== start.value) {
                                pushUndo({ idx: safeActiveIdx, token: f.token,
                                  prev: start.value, next: now });
                              }
                            }}
                            onKeyDown={(e) => { if (e.key === 'Enter') e.target.blur(); }}
                            aria-label={`${templateName(activeKey)}: ${f.label}`}
                          />
                        </div>
                      ))}
                      <div className="dk-savehint">
                        Once built, the preview updates as you type. Refresh it after page changes.
                      </div>
                    </div>
                  ) : (
                    // Honest empty-note: analytical slides carry no editable text; their
                    // content is generated from the subject supplier's data (Rule 116).
                    <div className="dk-locknote" style={{ marginTop: 0 }}>
                      This page has no editable text. Its content is generated from
                      {subject ? ` ${subject.supplier_name}'s` : ' the subject supplier’s'} data.
                    </div>
                  )}
                    </>
                  )}
                  {/* Lane 736 (Ryan's D54.1 "caveat the number"). An analytical
                      slide's numbers are not editable -- and must not become
                      editable, since they are engine-computed (Rule 116). What the
                      buyer CAN add is a note ABOUT one of them, which renders
                      beside that number rather than on another page. Self-hides
                      when the flag is off or the slide carries no caveatable
                      metric, so every other page's panel is unchanged. */}
                  {subject && activeKey ? (
                    <MetricCaveatPanel
                      supplierId={subject.supplier_id}
                      slideKey={activeKey}
                      authHeaders={authHeaders}
                    />
                  ) : null}
                </div>

                {/* PLAN B PHASE 3 — the keyboard/narrow path for row add and
                    delete (plan §8.4: hover is an enhancement, never the only
                    path). Registry-driven: only the four contract templates
                    render this; bounds come from the same contract the frame
                    overlay and the server enforce. */}
                {!elementEditorEnabled && (() => {
                  const eff = effectiveRowsFor(activeInstance);
                  if (!eff) return null;
                  const { contract, rows } = eff;
                  const canAdd = rows.length < contract.max_rows;
                  const canDelete = rows.length > contract.min_rows;
                  const opsBlocked = busy || rowOpPending;
                  return (
                    <div className="dk-sec" data-testid="rows-section">
                      <div className="dk-sec-h"><span className="t">Rows on this page</span></div>
                      <div className="dk-stack">
                        {rows.map((row, i) => (
                          <div key={row.row_id} className="dk-rowline">
                            <span className="n">{i + 1}</span>
                            <span className="nm">
                              {(row.values && row.values[contract.key_field]) || '(empty row)'}
                            </span>
                            <button type="button" className="dk-ib rm"
                              disabled={!canDelete || opsBlocked}
                              aria-label={`Delete row ${i + 1}`}
                              onClick={() => applyRowOp(safeActiveIdx, 'delete', i + 1)}>
                              &times;
                            </button>
                          </div>
                        ))}
                        <button type="button" className="dk-btn sm"
                          disabled={!canAdd || opsBlocked}
                          data-testid="row-add"
                          onClick={() => applyRowOp(safeActiveIdx, 'add', null)}>
                          {canAdd
                            ? `Add a row (up to ${contract.max_rows})`
                            : `This page holds up to ${contract.max_rows} rows`}
                        </button>
                        {rowOpPending && (
                          <div className="dk-savehint">Updating the page…</div>
                        )}
                      </div>
                    </div>
                  );
                })()}

                {/* THE PREP FOR THIS PAGE. Same server-composed pack as before,
                    asked for one page instead of eleven. "Open the full plan"
                    still opens the whole pack in the wide sheet.
                    Lane 1229 / S4: in v2 this section MOVES to the speaker-
                    notes bar under the canvas (§2) — move, not copy (§7.1):
                    exactly one of the two conditional sites renders per
                    layout, hosting the same MeetingPrepPack element shape. */}
                {!workspaceShellV2Enabled && (
                <div className="dk-sec">
                  <div className="dk-sec-h">
                    <span className="t">Prep for this page</span>
                    <span className="sp" />
                    <span className="dk-internal-chip">Internal</span>
                  </div>
                  {presentationTemplates ? (
                    <MeetingPrepPack key={editingId} compositionId={editingId} apiBase={API_BASE}
                      authHeaders={authHeaders} onlySlideIndex={safeActiveIdx}
                      savedSignature={savedSignature} dirty={dirty} onSave={save} onStatus={handlePackStatus} readOnly={Boolean(viewOnly)} />
                  ) : (
                    // Lane 752's honest note, kept: PROD carries no DECK_*
                    // variable at all, so a blank section here would be every
                    // environment except staging.
                    <div data-testid="meeting-plan-unavailable" className="dk-inspect-empty">
                      The meeting prep pack is not available for this deck.
                    </div>
                  )}
                </div>
                )}

                {/* NO "Remove this page" here, deliberately. Removing a page is
                    a PAGE-LIST action and it lives on the page's own row, where
                    reordering lives -- putting a second copy in this panel would
                    re-open the thing the redesign closed: one action, two places
                    to look for it. The generated-page arm used to carry its own
                    Remove for the same reason the old panel carried everything;
                    the row's control covers both. */}
              </>
            ) : (
              <div className="dk-inspect-empty" style={{ margin: '14px' }}>
                Pick a page on the left to see its words and its prep.
              </div>
            )}
          </div>
        </div>
        )}
  </>);

  // --- render ------------------------------------------------------------
  // While a deck named in the URL opens, the page stays mounted (its refs and
  // effects run as always) but hidden behind one line (Codex flash r1).
  return (
    <div className={(workspaceShellV2Enabled ? `dk dk-v2${desktopWorkspace ? ' dk-normal-host' : ' dk-stacked-host'}` : 'dk')
      + (openingDeck ? ' dk-opening' : '')} ref={stackedRootRef} aria-busy={openingDeck || undefined}
      onKeyDownCapture={workspaceShellV2Enabled ? event => {
        if (event.target.closest('[data-deck-full-plan]')) return;
        if ((event.metaKey || event.ctrlKey) && /^(z|y)$/i.test(event.key)) positionPadRef.current?.stop();
        if (!['Delete', 'Backspace'].includes(event.key) || event.repeat || event.metaKey || event.ctrlKey || event.altKey || event.isComposing
          || event.nativeEvent.isComposing || event.target.closest('input,textarea,select,[contenteditable]:not([contenteditable="false"])')) return;
        if (!deletionBlocked && (activeSelectedAdded || canDeleteRow || canDeleteTemplate)) { event.preventDefault(); deleteSelection(); }
      } : undefined}>
      {openingDeck && <p className="dk-loading dk-opening-note" role="status">Opening your deck…</p>}
      {!desktopWorkspace && toolbar}

      {/* ===== 3. DECK SETUP (configuration, one click away) ================= */}
      {(() => {
        if (!setupOpen) return null;
        // Lane 1233 / W4 (binding 4.4): ONE setup body — the same fields,
        // handlers, and testid in both hosts; only the host differs. V1
        // renders the inline band byte-identically; v2 hosts it in a
        // compact focus-managed dialog (heading focused on open; Escape
        // and backdrop close and restore the toggle).
        const setupBody = (
          <div className={workspaceShellV2Enabled
            ? 'dk-setup in-dialog' : 'dk-setup'} data-testid="deck-setup">
          <div className="dk-setup-g">
            <div className="dk-setup-fld">
              <span className="dk-fl">Subject supplier<span className="dk-req">*</span></span>
              <AsyncSelect
                loadOptions={debouncedLoad}
                defaultOptions
                cacheOptions
                isClearable
                value={subject ? { value: subject.supplier_id, label: subject.supplier_name } : null}
                placeholder="Search suppliers by name..."
                onChange={(opt) => setSubject(opt && opt.supplier ? opt.supplier : null)}
                styles={selectStyles}
                menuPortalTarget={typeof document !== 'undefined' ? document.body : null}
                aria-label="Subject supplier"
              />
            </div>
            <div className="dk-setup-fld">
              <span className="dk-fl">Deck title</span>
              <input className="dk-inp" value={title} placeholder="e.g. Q2 QBR with Parker"
                aria-label="Deck title"
                onChange={(e) => setTitle(e.target.value)} />
            </div>
            {/* Lane 804 (pre-launch item 2) -- the "Meeting type" field is GONE.
                It was the only element in the module carrying that label, and it
                served no purpose: {{meeting_type}} has rendered on ZERO slides
                since QA-6 (2026-07-22) dropped it from both the cover chrome and
                the content-slide eyebrow, and the "Plan a meeting" intake -- the
                way decks are actually made -- overwrites meeting_type SERVER-side
                from the chosen objective's label (routes/deck_builder.py:1775),
                so whatever was typed here never survived that path anyway.
                CAPTURE IS UNTOUCHED: meetingType is still state, still restored
                from a saved deck by loadDeck's setMeetingType, and still sent on
                both save calls -- it is now simply not user-editable, sitting at
                the 'QBR' default every new deck already started from. */}
            <div className="dk-setup-fld">
              <span className="dk-fl">Load a saved deck</span>
              <select
                className="dk-inp sel"
                value={editingId || ''}
                onChange={(e) => { if (e.target.value) (onOpenDeck || loadDeck)(Number(e.target.value)); }}
                aria-label="Load saved deck"
              >
                <option value="">{savedDecks.length ? 'Select a saved deck…' : 'None yet'}</option>
                {/* Lane 828: the author, when the deck records one. Decks written
                    before that lane stored a literal NULL, so they say so rather
                    than being attributed to a guess -- and the picker is where it
                    matters, since two decks for the same supplier were otherwise
                    indistinguishable. Lane 1372: the built date too (savedDeckLabel). */}
                {savedDecks.map((d) => (
                  <option key={d.composition_id} value={d.composition_id}>
                    {savedDeckLabel(d, { author: true })}
                  </option>
                ))}
              </select>
            </div>
          </div>
          <div className="dk-setup-f">
            <button type="button" className="dk-btn sm" onClick={() => setSetupOpen(false)}>Done</button>
            <button type="button" className="dk-btn sm" onClick={onStartChat}>Start a deck in chat</button>
            {/* Only offered once a deck is actually loaded -- there is nothing to
                delete otherwise, and requiring the load is the misclick defence. */}
            {editingId && canDelete && (
              <button type="button" className="dk-btn sm" onClick={deleteDeck} disabled={busy}>
                Delete deck
              </button>
            )}
            {workspaceShellV2Enabled && <button type="button" className="dk-btn sm"
              onClick={() => setArgumentOpen(true)}>Deck argument</button>}

          </div>
          </div>
        );
        if (!workspaceShellV2Enabled) return setupBody;
        return (
          <div className="dk-backdrop" role="presentation"
            data-testid="deck-setup-dialog"
            onClick={closeSetupDialog}
            onKeyDown={(e) => {
              if (e.key === 'Escape') {
                e.stopPropagation();
                closeSetupDialog();
              }
            }}>
            <div className="dk-modal dk-setup-modal" role="dialog"
              aria-modal="true" aria-label={argumentOpen ? 'Deck argument' : 'Deck setup'}
              onClick={(e) => e.stopPropagation()}>
              <div className="dk-modal-h">
                <div className="dk-modal-t" tabIndex={-1}
                  ref={setupHeadingRef}>{argumentOpen ? 'Deck argument' : 'Deck setup'}</div>
              </div>
              {argumentOpen ? <div className="dk-argument-popup">
                <ArgumentLine popup supplierId={subject ? subject.supplier_id : null}
                  supplierName={subject ? subject.supplier_name : null}
                  apiBase={API_BASE} token={token}
                  onChanged={() => setBriefRevision((n) => n + 1)} />
                <button type="button" className="dk-btn sm" onClick={() => setArgumentOpen(false)}>
                  Back to Deck setup
                </button>
              </div> : setupBody}
            </div>
          </div>
        );
      })()}

      {chatWriting && (
        <div className="dk-chat-writing" role="status" aria-live="polite">
          <span>Wingman is updating the deck…</span>
        </div>
      )}
      {chatReload && !chatWriting && (
        <div className="dk-chat-reload" role="alert">
          <span>{chatReload}</span>
          <button type="button" className="dk-btn" onClick={reloadAfterChat}>
            Reload the deck</button>
        </div>
      )}
      {desktopWorkspace ? (
        <DeckWorkspaceShell toolbar={toolbar} rail={<>{pages}{inspector}</>}
          notes={notes} notesOpen={notesOpenEffective} onOpenNotes={() => setNotesOpen(true)}
          chat={deckChat}>
          {preview}
        </DeckWorkspaceShell>
      ) : (
        <div className={workspaceShellV2Enabled
          ? 'dk-ws dk-ws-v2'
          : (inspectorOpen ? 'dk-ws' : 'dk-ws no-inspector')}>
          {pages}{preview}{notes}{inspector}
        </div>
      )}
      {!desktopWorkspace && deckChat}

      {/* Phase 3: the slide-library gallery (near-full-screen categorized grid
          overlay; renders above the workspace). Holds the search + a per-category
          responsive grid of slide cards (Editable/Generated + in-deck pills,
          "When to use" advisory expand) + Add. Lane 315 / deck v3 amendment 3. */}
      <LibraryDrawer
        open={libraryOpen}
        onClose={() => setLibraryOpen(false)}
        templates={templates}
        onAdd={addSlide}
        inDeckCounts={inDeckCounts}
        slideCount={slideCount}
        subjectName={subject ? subject.supplier_name : null}
        /* ⭐ LANE 941 -- THE VERDICT BESIDE THE ADD BUTTON. THIS IS THE ONE THAT
           STOPS THE NEXT BRAD. He opened this library, found an excluded page and
           added it with nothing on screen telling him the build had ruled it out
           or why. The same `page_verdicts` record the withheld list reads, handed
           to the surface where the mistake is actually made.

           `{}` on every deck built before this lane -> the library renders exactly
           as it shipped. Absent prop, absent chip, no behaviour change. */
        pageVerdicts={pageVerdicts}
      />


      {/* Lane 1229 / S5 (§5): the Present overlay. Full-screen when the
          browser permits (the open effect ATTEMPTS it); this same in-app
          modal is the sanctioned fallback. One full snapshot image at a
          time, plain <img> — no frame, no scripts, no postMessage surface,
          zero write paths. Left/Right + PageUp/PageDown page within bounds;
          Escape closes and restores focus to the Present button; Tab is
          trapped across the overlay's controls. */}
      {workspaceShellV2Enabled && presentOpen && (
        <div className="dk-present" role="dialog" aria-modal="true"
          aria-label="Present deck" ref={presentRef} tabIndex={-1}
          data-testid="present-overlay"
          onKeyDown={(e) => {
            if (e.key === 'Escape') {
              e.stopPropagation();
              closePresent();
            } else if (e.key === 'ArrowRight' || e.key === 'PageDown') {
              e.preventDefault();
              setPresentIdx((i) => Math.min(i + 1, presentPageCount - 1));
            } else if (e.key === 'ArrowLeft' || e.key === 'PageUp') {
              e.preventDefault();
              setPresentIdx((i) => Math.max(i - 1, 0));
            } else if (e.key === 'Tab') {
              const focusables = Array.from(
                e.currentTarget.querySelectorAll('button:not(:disabled)'));
              if (focusables.length) {
                const first = focusables[0];
                const last = focusables[focusables.length - 1];
                // Consult #5 fix: when focus sits on the overlay container
                // itself (initial state) or anywhere outside the control
                // list, redirect INTO the list — otherwise Shift+Tab from
                // the container escapes into background content.
                if (!focusables.includes(document.activeElement)) {
                  e.preventDefault();
                  (e.shiftKey ? last : first).focus();
                } else if (e.shiftKey && document.activeElement === first) {
                  e.preventDefault(); last.focus();
                } else if (!e.shiftKey && document.activeElement === last) {
                  e.preventDefault(); first.focus();
                }
              }
            }
          }}>
          {(() => {
            const pageUrl = presentUrls[presentIdx];
            return typeof pageUrl === 'string' ? (
              <img className="dk-present-img" src={pageUrl}
                alt={`Page ${presentIdx + 1} of ${presentPageCount}`}
                data-testid="present-img" />
            ) : (
              <div className="dk-present-ph" data-testid="present-placeholder">
                {pageUrl === false
                  ? 'This page image is unavailable.'
                  : `Loading page ${presentIdx + 1}…`}
              </div>
            );
          })()}
          <div className="dk-present-bar">
            <button type="button" className="dk-btn sm" data-testid="present-prev"
              disabled={presentIdx === 0}
              onClick={() => setPresentIdx((i) => Math.max(i - 1, 0))}>
              &lsaquo; Previous
            </button>
            <span className="dk-present-n">
              {`Page ${presentIdx + 1} of ${presentPageCount}`}
            </span>
            <button type="button" className="dk-btn sm" data-testid="present-next"
              disabled={presentIdx >= presentPageCount - 1}
              onClick={() => setPresentIdx((i) => Math.min(i + 1, presentPageCount - 1))}>
              Next &rsaquo;
            </button>
            <span className="sp" />
            <button type="button" className="dk-btn sm" data-testid="present-close"
              onClick={closePresent}>
              Exit
            </button>
          </div>
        </div>
      )}

      {/* Lane 72 (§18 export-disclosure guardrail) -- warn-and-confirm modal.
          Opens only when a DOWNLOAD was refused because the deck contains
          internal-only slides. Names the slides; "Download anyway" re-requests
          WITH acknowledgement and delivers the full deck (Option C never strips).
          Cancel / backdrop click dismisses without downloading. */}
      {pendingInternalConfirm && (
        <div
          className="dk-backdrop"
          role="dialog"
          aria-modal="true"
          aria-label="Internal-only slides confirmation"
          onClick={() => setPendingInternalConfirm(null)}
        >
          <div className="dk-modal sm" onClick={(e) => e.stopPropagation()}>
            <div className="dk-modal-h">
              <div className="dk-modal-t">Internal-only slides in this deck</div>
            </div>
            <div className="dk-modal-b sm">
              <div style={{ fontSize: '13px', lineHeight: 1.5, marginBottom: '12px' }}>
                This deck contains slide(s) marked <strong>internal use only</strong>. They
                reveal your own leverage and are <strong>not for supplier distribution</strong>:
              </div>
              <ul className="dk-modal-list">
                {pendingInternalConfirm.slides.map((s) => (
                  <li key={s.template_key}>{s.name || s.template_key}</li>
                ))}
              </ul>
              <div style={{ fontSize: '13px', color: 'var(--text-3)' }}>
                Download the full deck anyway? The internal slide(s) will be included.
              </div>
            </div>
            <div className="dk-modal-f">
              <button type="button" className="dk-btn"
                onClick={() => setPendingInternalConfirm(null)}>Cancel</button>
              <button type="button" className="dk-btn pri"
                onClick={proceedInternalDownload}>Download anyway</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default DeckAssembler;
